import { useState, useMemo, useCallback, useRef, useEffect } from "react";
import { patientAPI, normalizePatient, reportAPI, normalizeReport, medicationAPI, allergyAPI, imagingAPI, vitalsAPI, historyAPI, patientSummaryAPI } from "../utils/api";
import { useApp } from "../context/AppContext";
import { useTheme } from "../context/ThemeContext";
import UploadReport from "../components/UploadReport";
import {
  ComposedChart, Line, XAxis, YAxis,
  CartesianGrid, Tooltip, ResponsiveContainer,
  ReferenceArea, ReferenceLine, Area
} from "recharts";

// ── Constants ────────────────────────────────────────────────────────────────
const TYPE_META = {
  Hematology:      { color: "#2563EB", clsBadge: "badge-blue",    clsType: "type-hematology"   },
  Hormone:         { color: "#7C3AED", clsBadge: "badge-purple",  clsType: "type-hormone"      },
  Immunology:      { color: "#0D9488", clsBadge: "badge-teal",    clsType: "type-immunology"   },
  Biochemistry:    { color: "#16A34A", clsBadge: "badge-green",   clsType: "type-biochemical"  },
  Diabetes:        { color: "#F59E0B", clsBadge: "badge-amber",   clsType: "type-diabetes"     },
  "Lipid Profile": { color: "#EA580C", clsBadge: "badge-orange",  clsType: "type-lipid"        },
  "Liver Function":{ color: "#DC2626", clsBadge: "badge-red",     clsType: "type-liver"        },
  "Kidney Function":{ color:"#0891B2", clsBadge: "badge-cyan",    clsType: "type-kidney"       },
  Serology:        { color: "#DB2777", clsBadge: "badge-pink",    clsType: "type-serology"     },
  Coagulation:     { color: "#9333EA", clsBadge: "badge-violet",  clsType: "type-coagulation"  },
  Microbiology:    { color: "#65A30D", clsBadge: "badge-lime",    clsType: "type-micro"        },
  Urinalysis:      { color: "#0284C7", clsBadge: "badge-sky",     clsType: "type-urine"        },
  "Stool Analysis":{ color: "#92400E", clsBadge: "badge-brown",   clsType: "type-stool"        },
  "Cardiac Markers":{ color:"#E11D48", clsBadge: "badge-rose",    clsType: "type-cardiac"      },
  "Tumor Markers": { color: "#7E22CE", clsBadge: "badge-fuchsia", clsType: "type-tumor"        },
  Pathology:       { color: "#475569", clsBadge: "badge-slate",   clsType: "type-pathology"    },
  Other:           { color: "#64748B", clsBadge: "badge-gray",    clsType: "type-other"        },
};

// Category → sub-types (mirrors UploadReport.js)
const REPORT_SUBTYPES = {
  "Hematology": ["CBC / Complete Blood Count","PBF / Blood Film Study","Semen Analysis","ESR","Haemoglobin (Hb)","Blood Grouping & Rh Factor","BT / CT","Circulating Eosinophil Count"],
  "Biochemistry": ["Blood Sugar / RBS / FBS","Electrolytes","Ferritin","Vitamin D","AMH","Urine for Protein"],
  "Diabetes": ["FBS / Fasting Blood Sugar","HbA1c","OGTT","FBS + 2HABF Combined"],
  "Lipid Profile": ["Lipid Profile"],
  "Liver Function": ["Full LFT Panel","Serum Bilirubin (Direct & Indirect)","A/G Ratio / Serum Proteins"],
  "Kidney Function": ["Full KFT Panel","eGFR","ACR / Urine Albumin"],
  "Hormone": ["Thyroid Panel (T3/T4/TSH/FT3/FT4)","Reproductive Hormones","Beta-hCG"],
  "Immunology": ["Total IgE","Anti-CCP"],
  "Serology": ["CRP + RA Test (Qualitative)","ASO Titre","ASO + CRP + RA Combined","CRP Quantitative / Hs-CRP","Dengue NS1+IgM+IgG","HBsAg + Anti-HCV + HIV + VDRL Screening","HBsAg ELISA","Cross Matching","Kala-azar + Malaria","Malarial Parasite (P.v + P.f)","Widal Test","Triple / Febrile Antigens","H.pylori Antibody"],
  "Coagulation": ["PT / INR / Prothrombin Time","APTT","D-Dimer","BT / CT"],
  "Microbiology": ["Gram Stain / V/S Study","Z-N Stain / AFB","Tuberculin / MT Test","Skin Scraping"],
  "Urinalysis": ["Urine RME / Routine & Microscopic","Urine Pregnancy Test","Ketone Bodies"],
  "Stool Analysis": ["Stool Examination","OBT / Occult Blood Test"],
  "Cardiac Markers": ["Troponin-I","NT-proBNP"],
  "Tumor Markers": ["PSA","AFP","CEA","CA-125"],
  "Pathology": ["FNAC / Cytopathology"],
};

// Display format per sub-type
const DISPLAY_FORMAT = {
  "CBC / Complete Blood Count":    "numeric",
  "ESR":                           "numeric",
  "Haemoglobin (Hb)":              "numeric",
  "BT / CT":                       "numeric",
  "Circulating Eosinophil Count":  "numeric",
  "Electrolytes":                  "numeric",
  "Ferritin":                      "numeric",
  "Vitamin D":                     "numeric",
  "AMH":                           "numeric",
  "Full LFT Panel":                "numeric",
  "Serum Bilirubin (Direct & Indirect)": "numeric",
  "A/G Ratio / Serum Proteins":    "numeric",
  "Full KFT Panel":                "numeric",
  "eGFR":                          "numeric",
  "ACR / Urine Albumin":           "numeric",
  "Urine for Protein":             "numeric",
  "PT / INR / Prothrombin Time":   "numeric",
  "APTT":                          "numeric",
  "D-Dimer":                       "numeric",
  "Ketone Bodies":                  "numeric",
  "FBS / Fasting Blood Sugar":     "numeric",
  "FBS + 2HABF Combined":          "numeric",
  "PBF / Blood Film Study":        "narrative",
  "FNAC / Cytopathology":          "conclusion",
  "Blood Grouping & Rh Factor":    "qualitative",
  "Urine Pregnancy Test":          "qualitative",
  "HbA1c":                         "staged",
  "Lipid Profile":                 "staged",
  "OGTT":                          "sequential",
  "Semen Analysis":                "sectioned-semen",
  "Urine RME / Routine & Microscopic": "sectioned-urine",
  "Thyroid Panel (T3/T4/TSH/FT3/FT4)": "hormone",
  "Reproductive Hormones": "hormone",
  "Beta-hCG":                      "hormone",
  // Microbiology
  "Gram Stain / V/S Study":        "sectioned-micro",
  "Z-N Stain / AFB":               "narrative",
  "Tuberculin / MT Test":          "numeric",
  "Skin Scraping":                 "narrative",
  // Cardiac Markers
  "Troponin-I":                    "numeric",
  "NT-proBNP":                     "numeric",
  // Tumor Markers
  "PSA":                           "numeric",
  "AFP":                           "numeric",
  "CEA":                           "numeric",
  "CA-125":                        "numeric",
  // Stool
  "Stool Examination":             "sectioned-stool",
  "OBT / Occult Blood Test":       "qualitative",
  // Serology qualitative
  "CRP + RA Test (Qualitative)":   "qualitative",
  "ASO + CRP + RA Combined":       "qualitative",
  "Dengue NS1+IgM+IgG":            "qualitative",
  "HBsAg + Anti-HCV + HIV + VDRL Screening": "qualitative",
  "Kala-azar + Malaria":           "qualitative",
  "Malarial Parasite (P.v + P.f)": "qualitative",
  "H.pylori Antibody":             "qualitative",
  "Widal Test":                    "numeric",
  "Triple / Febrile Antigens":     "numeric",
  "ASO Titre":                     "numeric",
  "CRP Quantitative / Hs-CRP":     "numeric",
  "HBsAg ELISA":                   "elisa",
  "Cross Matching":                "qualitative",
  // Immunology
  "Total IgE":                     "numeric",
  "Anti-CCP":                      "numeric",
};

function getDisplayFormat(reportType) {
  return DISPLAY_FORMAT[reportType] || "numeric";
}

const SUBTYPE_TO_CATEGORY = {};
Object.entries(REPORT_SUBTYPES).forEach(([cat, subs]) => {
  subs.forEach(sub => { SUBTYPE_TO_CATEGORY[sub] = cat; });
});

// Get parent category for a report type
function getCategory(reportType) {
  return SUBTYPE_TO_CATEGORY[reportType] || reportType;
}

// Inject ref-tip-box CSS override — use position:fixed viewport-relative tooltip
// so it escapes parent overflow:hidden constraints in report cards and tables.
// JS mouseover/mouseout handlers calculate position from getBoundingClientRect.
if (typeof document !== "undefined" && !document.getElementById("ref-tip-override")) {
  const s = document.createElement("style");
  s.id = "ref-tip-override";
  s.textContent = `
    .ref-tip-wrap .ref-tip-box {
      display: none !important;
    }
    #reftip-global {
      position: fixed;
      background: #1E293B;
      color: #F1F5F9;
      border-radius: 8px;
      padding: 10px 14px;
      min-width: 180px;
      max-width: 280px;
      max-height: 220px;
      overflow-y: auto;
      font-size: 11px;
      line-height: 1.8;
      white-space: pre-line;
      z-index: 99999;
      pointer-events: none;
      display: none;
    }
  `;
  document.head.appendChild(s);

  // Create single global tooltip element
  const tip = document.createElement("div");
  tip.id = "reftip-global";
  document.body.appendChild(tip);

  // Show tooltip on mouseover any ref-tip-wrap
  document.addEventListener("mouseover", (e) => {
    const wrap = e.target.closest(".ref-tip-wrap");
    if (!wrap) return;
    const box = wrap.querySelector(".ref-tip-box");
    if (!box) return;
    tip.textContent = box.textContent;
    tip.style.display = "block";

    const rect = wrap.getBoundingClientRect();
    const tipH = tip.offsetHeight || 220;
    const tipW = tip.offsetWidth || 280;
    const spaceBelow = window.innerHeight - rect.bottom;
    const spaceAbove = rect.top;

    // Position: prefer below, flip to above if not enough space
    let top = spaceBelow >= tipH + 8 || spaceBelow >= spaceAbove
      ? rect.bottom + 6
      : rect.top - tipH - 6;

    // Clamp within viewport vertically
    top = Math.max(8, Math.min(top, window.innerHeight - tipH - 8));

    // Horizontal: center on trigger, clamp to viewport
    let left = rect.left + rect.width / 2 - tipW / 2;
    left = Math.max(8, Math.min(left, window.innerWidth - tipW - 8));

    tip.style.top = top + "px";
    tip.style.left = left + "px";
  });

  document.addEventListener("mouseout", (e) => {
    const wrap = e.target.closest(".ref-tip-wrap");
    if (wrap && !wrap.contains(e.relatedTarget)) {
      tip.style.display = "none";
    }
  });
}

// Format ref_text for tooltip display — converts " | " pipe separators to newlines
// so the pre-line CSS wraps each range onto its own line in the tooltip.
// Used for multi-range hover text (Beta-hCG weeks, KDIGO stages, gestational ranges etc.)
function formatRefText(refText) {
  if (!refText) return refText;
  return refText.replace(/ \| /g, "\n");
}

// Format seconds to "X min Y sec" for BT/CT display
function formatSeconds(secs) {
  if (secs == null || isNaN(secs)) return secs;
  const m = Math.floor(secs / 60);
  const s = Math.round(secs % 60);
  if (m === 0) return `${s} sec`;
  if (s === 0) return `${m} min`;
  return `${m} min ${s} sec`;
}

// Build complete tooltip content for any reference range display.
// Shows the applied range (refMin–refMax) that was actually used for status,
// then the full PDF ref_text as context if available and different.
// This is the single source of truth for ALL ref range tooltips in the app.
function buildRefTooltip(refMin, refMax, refText, unit) {
  const parts = [];
  const u = unit ? " " + unit : "";

  // Applied range (what was used for status calculation)
  if (refMin != null && refMax != null) {
    parts.push("Applied range: " + refMin + " – " + refMax + u);
  } else if (refMin != null) {
    parts.push("Applied range: ≥ " + refMin + u);
  } else if (refMax != null) {
    parts.push("Applied range: < " + refMax + u);
  }

  // Full PDF ref_text as context (if it adds information beyond the applied range)
  if (refText) {
    const rangeStr = refMin != null && refMax != null ? (refMin + " – " + refMax) : "";
    const refNorm = refText.trim().replace(/ \| /g, "\n");
    // Only show if different from the simple applied range
    if (!rangeStr || !refText.includes(String(refMin)) || !refText.includes(String(refMax)) || isMultiRange(refText)) {
      if (parts.length > 0) parts.push("─────────────────");
      parts.push(refNorm);
    }
  }

  // Fallback: if nothing else, just show the range
  if (parts.length === 0) return null;
  return parts.join("\n");
}
function isTimeParam(paramName) {
  if (!paramName) return false;
  const n = paramName.toLowerCase();
  return n.includes("bleeding time") || n.includes("clotting time");
}
// "Adults: Up to 1.1 mg/dl\nNew Born: 13.3 mg/dl") vs plain single-range
// text that just happens to be long ("Normal: Up to 90 mg/dl").
// The old threshold (length > 20) wrongly triggered on single-range texts.
function isMultiRange(refText) {
  if (!refText) return false;
  // Genuine multi-range signals: newline, pipe separator, or age/sex bracket keywords
  return /\n|\|/.test(refText) ||
    /\b(adult|new.?born|infant|child|male|female|neonate|pediatric|trimester|week|years?|yrs?|\d+\s*-\s*\d+\s*yrs?|\d+\s*yrs?|\d+\s*months?)/i.test(refText);
}

const STATUS_STYLE = {
  "Normal":        { bg: "#DCFCE7", color: "#16A34A", label: "Normal" },
  "High":          { bg: "#FEE2E2", color: "#DC2626", label: "High ↑" },
  "Low":           { bg: "#DBEAFE", color: "#2563EB", label: "Low ↓" },
  "Critical High": { bg: "#7F1D1D", color: "#FFF",   label: "Critical H" },
  "Critical Low":  { bg: "#1E3A5F", color: "#FFF",   label: "Critical L" },
  "Abnormal":      { bg: "#EEF2FF", color: "#4F46E5", label: "Abnormal" },
  "Needs Review":  { bg: "#F1F5F9", color: "#64748B", label: "Needs Review" },
  "TNP":           { bg: "#F1F5F9", color: "#94A3B8", label: "TNP" },
  "Informational": { bg: "#E0F2FE", color: "#0369A1", label: "Info" },
};

const RANGES = ["All", "3M", "6M", "1Y", "Custom"];

// ── Helpers ──────────────────────────────────────────────────────────────────
function filterRange(reports, range, from, to, fromYear, toYear, useYearOnly) {
  const now = new Date();
  return reports.filter(r => {
    const d = new Date(r.date);
    if (range === "3M")    return (now - d) / (864e5 * 30) <= 3;
    if (range === "6M")    return (now - d) / (864e5 * 30) <= 6;
    if (range === "1Y")    { const y = new Date(now); y.setFullYear(y.getFullYear() - 1); return d >= y; }
    if (range === "Custom") {
      if (useYearOnly && fromYear && toYear) {
        return d.getFullYear() >= parseInt(fromYear) && d.getFullYear() <= parseInt(toYear);
      }
      if (!useYearOnly && from && to) {
        const toDate = new Date(to); toDate.setHours(23,59,59,999);
        return d >= new Date(from) && d <= toDate;
      }
    }
    return true;
  });
}

// ── Sub-components ────────────────────────────────────────────────────────────
// Format date as "25 Jul 2026" — used everywhere instead of raw ISO string
function formatDate(dateStr) {
  if (!dateStr) return "—";
  const d = new Date(dateStr);
  if (isNaN(d)) return dateStr;
  return d.getDate() + " " + ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"][d.getMonth()] + " " + d.getFullYear();
}

function StatusBadge({ status }) {
  const base = { display:"inline-block", fontSize:10, fontWeight:600, letterSpacing:".04em", padding:"2px 7px", borderRadius:4, marginTop:3 };
  if (!status || status === "Normal")       return <span style={{ ...base, color:"#059669", background:"#F0FDF4" }}>Normal</span>;
  if (status === "High")                    return <span style={{ ...base, color:"#D97706", background:"#FEF3C7" }}>H</span>;
  if (status === "Low")                     return <span style={{ ...base, color:"#2563EB", background:"#DBEAFE" }}>L</span>;
  if (status === "Critical High")           return <span style={{ ...base, color:"#fff",    background:"#DC2626" }}>Critical H</span>;
  if (status === "Critical Low")            return <span style={{ ...base, color:"#fff",    background:"#7C3AED" }}>Critical L</span>;
  if (status === "Abnormal")                return <span style={{ ...base, color:"#D97706", background:"#FEF3C7" }}>Abnormal</span>;
  if (status === "Needs Review")            return <span style={{ ...base, color:"#6B21A8", background:"#F3E8FF" }}>Needs review</span>;
  if (status === "TNP")                     return <span style={{ ...base, color:"#94A3B8", background:"#F1F5F9", fontStyle:"italic" }}>TNP</span>;
  if (status === "Informational")           return <span style={{ ...base, color:"#0369A1", background:"#E0F2FE" }}>Info</span>;
  return <span style={{ ...base, color:"#64748B", background:"#F1F5F9" }}>{status}</span>;
}

function GaugeBar({ value, refMin, refMax, status }) {
  const fVal = parseFloat(value);
  if (isNaN(fVal) || refMin == null || refMax == null) return null;
  const range = parseFloat(refMax) - parseFloat(refMin);
  if (range <= 0) return null;
  const pct = Math.min(Math.max((fVal - parseFloat(refMin)) / range, 0), 1) * 100;
  const color = STATUS_STYLE[status]?.color || "#64748B";


  return (
    <div style={{ height: 4, background: "#E4E8F0", borderRadius: 99, overflow: "visible", position: "relative", flex: 1, minWidth: 60 }}>
      <div style={{ position: "absolute", left: 0, right: 0, top: 0, height: "100%", background: "#DCFCE7", opacity: 0.6, borderRadius: 99 }} />
      <div style={{
        position: "absolute", top: -4, left: `${pct}%`,
        width: 12, height: 12, borderRadius: "50%",
        background: color, border: "2px solid white",
        boxShadow: "0 1px 4px rgba(0,0,0,.25)",
        transform: "translateX(-50%)"
      }} />
    </div>
  );
}

// ── Qualitative Timeline (Positive/Negative over time) ───────────────────────
function QualitativeTimeline({ data, paramName }) {
  const sorted = [...data].sort((a, b) => new Date(a.date) - new Date(b.date));
  const getStyle = (status) => {
    if (!status || status === "Needs Review") return { bg: "#F1F5F9", color: "#64748B", border: "#CBD5E1", label: "?" };
    if (status === "Normal") return { bg: "#DCFCE7", color: "#15803D", border: "#86EFAC", label: "NEG" };
    if (status === "Abnormal") return { bg: "#FEE2E2", color: "#B91C1C", border: "#FCA5A5", label: "POS" };
    if (status?.includes("Critical")) return { bg: "#7F1D1D", color: "#fff", border: "#DC2626", label: "CRIT" };
    if (status === "Informational") return { bg: "#E0F2FE", color: "#0369A1", border: "#BAE6FD", label: "INFO" };
    return { bg: "#FEF9C3", color: "#A16207", border: "#FDE047", label: "?" };
  };

  return (
    <div style={{ padding: "8px 4px" }}>
      {/* No chart message */}
      <div style={{
        background: "#F8FAFC", border: "1px dashed #CBD5E1", borderRadius: 8,
        padding: "10px 14px", marginBottom: 16, display: "flex", alignItems: "center", gap: 10
      }}>
        <span style={{ fontSize: 18 }}>📋</span>
        <div>
          <div style={{ fontSize: 12, fontWeight: 600, color: "#374151" }}>Qualitative result — no numeric chart available</div>
          <div style={{ fontSize: 11, color: "#94A3B8", marginTop: 1 }}>Showing Positive/Negative history below</div>
        </div>
      </div>

      {/* Latest result highlight */}
      {sorted.length > 0 && (() => {
        const latest = sorted[sorted.length - 1];
        const s = getStyle(latest.status);
        return (
          <div style={{
            background: s.bg, border: `1px solid ${s.border}`, borderRadius: 10,
            padding: "12px 16px", marginBottom: 16, display: "flex", align: "center", justifyContent: "space-between"
          }}>
            <div>
              <div style={{ fontSize: 11, color: s.color, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 2 }}>Latest Result</div>
              <div style={{ fontSize: 20, fontWeight: 800, color: s.color }}>{latest.valueText || latest.status}</div>
              <div style={{ fontSize: 11, color: s.color, opacity: 0.7, marginTop: 2 }}>{formatDate(latest.date)}</div>
            </div>
            <div style={{
              width: 48, height: 48, borderRadius: "50%", background: s.color,
              display: "flex", alignItems: "center", justifyContent: "center",
              color: "#fff", fontWeight: 800, fontSize: 13, flexShrink: 0
            }}>{s.label}</div>
          </div>
        );
      })()}

      {/* Timeline dots */}
      {sorted.length > 1 && (
        <div>
          <div style={{ fontSize: 11, color: "#94A3B8", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 10 }}>History</div>
          <div style={{ position: "relative", paddingBottom: 32 }}>
            {/* Timeline line */}
            <div style={{
              position: "absolute", top: 16, left: 16, right: 16, height: 2,
              background: "#E2E8F0", zIndex: 0
            }} />
            <div style={{ display: "flex", justifyContent: "space-between", position: "relative", zIndex: 1 }}>
              {sorted.map((d, i) => {
                const s = getStyle(d.status);
                const isLatest = i === sorted.length - 1;
                return (
                  <div key={i} style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 6 }}>
                    {isLatest && (
                      <div style={{ fontSize: 8, fontWeight: 800, color: "#5B4FCF", textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 2, textAlign: "center" }}>Latest</div>
                    )}
                    <div style={{
                      width: isLatest ? 38 : 28, height: isLatest ? 38 : 28,
                      borderRadius: "50%", background: isLatest ? s.color : s.bg,
                      border: `2px solid ${s.border}`,
                      display: "flex", alignItems: "center", justifyContent: "center",
                      fontWeight: 800, fontSize: isLatest ? 11 : 9, color: isLatest ? "#fff" : s.color,
                      boxShadow: isLatest ? `0 0 0 4px ${s.border}66` : "none",
                      transition: "all 0.15s"
                    }}>{s.label}</div>
                    <div style={{ fontSize: 9, color: "#64748B", textAlign: "center", whiteSpace: "nowrap" }}>
                      {d.date ? (() => { const dt = new Date(d.date); return dt.getDate() + " " + ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"][dt.getMonth()]; })() : ""}
                    </div>
                    <div style={{ fontSize: 9, fontWeight: 600, color: s.color, textAlign: "center" }}>
                      {(d.valueText || "").slice(0, 8)}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Redesigned Clinical Trend Chart ──────────────────────────────────────────
function ClinicalTrendChart({ data, refMin, refMax, refText, unit, color, paramName }) {
  const { t } = useTheme();
  if (!data || data.length === 0) return (
    <div style={{ padding: "32px 0", textAlign: "center", color: "#94A3B8", fontSize: 13 }}>
      No data available for this parameter.
    </div>
  );

  // Helper: extract numeric from value or value_text including <N, >N, and range "N-M" formats
  // <5.0 → 5.0, >10 → 10, "3-5" → 4.0 (midpoint — more accurate for trend plotting)
  // Status determination uses upper bound (in startEdit/updateEdit) — this is for chart only
  // Tooltip shows original valueText so no clinical info is lost
  const extractNumeric = (d) => {
    if (d.value !== null && d.value !== undefined && d.value !== "" && !isNaN(parseFloat(d.value))) {
      return parseFloat(d.value);
    }
    const vt = String(d.valueText || "").trim();
    // Threshold formats: <5.0, >10
    const threshM = vt.match(/^[<>≤≥]\s*(\d+(?:\.\d+)?)/);
    if (threshM) return parseFloat(threshM[1]);
    // Range format: "3-5", "4-6" — use MIDPOINT for chart (not upper bound)
    const rangeM = vt.match(/^(\d+(?:\.\d+)?)\s*[-–]\s*(\d+(?:\.\d+)?)\s*$/);
    if (rangeM) return (parseFloat(rangeM[1]) + parseFloat(rangeM[2])) / 2;
    return null;
  };

  // Detect qualitative results — all data points have no numeric value AND no threshold valueText
  const isQualitative = data.every(d => extractNumeric(d) === null);
  if (isQualitative) return <QualitativeTimeline data={data} paramName={paramName} />;

  // Sort newest → oldest (left → right) — latest on left
  // Use labId as tiebreaker when dates are identical (higher labId = later upload)
  const sorted = [...data].sort((a, b) => {
    const dateDiff = new Date(b.date) - new Date(a.date);
    if (dateDiff !== 0) return dateDiff;
    return (b.labId || 0) - (a.labId || 0);
  });
  const latest = sorted[0];
  const prev   = sorted.length >= 2 ? sorted[1] : null;

  const latestVal = extractNumeric(latest);
  const prevVal   = prev ? extractNumeric(prev) : null;

  // Detect semi-quantitative range results like "3-5", "4-6" — hide % change per enterprise EMR standard
  // Also detect titre/ratio results like "1:80", "1:160" — % change is clinically invalid for serial dilutions
  const isRangeResult = /^\d+(?:\.\d+)?[-–]\d+(?:\.\d+)?$/.test((latest.valueText || "").trim())
    || /^\s*\d+\s*[:/]\s*\d+\s*$/.test((latest.valueText || "").trim());

  const pctChange = (prev && !isRangeResult && latestVal !== null && prevVal !== null && prevVal !== 0)
    ? ((latestVal - prevVal) / Math.abs(prevVal) * 100).toFixed(1)
    : null;

  // Add numericValue to each data point for recharts (handles <N threshold formats)
  // Reversed to oldest→newest for X-axis — universal EMR standard (Epic, Cerner, CHR)
  // sorted stays newest-first for: Current Value, Previous Value, % change calculations
  const chartData = [...sorted].reverse().map(d => ({ ...d, numericValue: extractNumeric(d) }));
  const vals = chartData.map(d => d.numericValue).filter(v => v !== null && !isNaN(v));

  // Safety: if no plottable values exist, fall back to qualitative view
  if (vals.length === 0) return <QualitativeTimeline data={data} paramName={paramName} />;
  const rMin  = refMin != null ? parseFloat(refMin) : null;
  const rMax  = refMax != null ? parseFloat(refMax) : null;
  const dataMin = Math.min(...vals, rMin ?? Infinity);
  const dataMax = Math.max(...vals, rMax ?? -Infinity);
  const padding = (dataMax - dataMin) * 0.2 || 1;
  const yMin = dataMin - padding;
  const yMax = dataMax + padding;

  const CustomDot = (props) => {
    const { cx, cy, payload } = props;
    const isCrit   = payload.status?.includes("Critical");
    const isAbn    = payload.status && payload.status !== "Normal";
    const isLatest = payload.date === latest.date;
    const dotColor = isCrit ? "#7F1D1D" : isAbn ? "#DC2626" : color;
    const r = isLatest ? 7 : 5;
    return (
      <g>
        {isCrit && <circle cx={cx} cy={cy} r={r + 5} fill="none" stroke="#DC2626" strokeWidth={1.5} opacity={0.35} strokeDasharray="3 2"/>}
        <circle cx={cx} cy={cy} r={r} fill={dotColor} stroke="white" strokeWidth={2}/>
        {isLatest && <circle cx={cx} cy={cy} r={r + 3} fill="none" stroke={color} strokeWidth={1.5} opacity={0.4}/>}
      </g>
    );
  };

  const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;
    const d = payload[0]?.payload;
    const s = STATUS_STYLE[d?.status] || STATUS_STYLE["Needs Review"];
    return (
      <div style={{
        background: "#1E293B", border: "none", borderRadius: 10,
        padding: "10px 14px", fontSize: 12,
        boxShadow: "0 8px 24px rgba(0,0,0,.25)"
      }}>
        <div style={{ color: "#94A3B8", marginBottom: 5, fontSize: 11 }}>{formatDate(label)}</div>
        <div style={{ fontWeight: 800, fontSize: 18, color: s.color === "#FFF" ? "#fff" : s.color }}>
          {isTimeParam(paramName)
            ? (d?.valueText || formatSeconds(d?.value))
            : (d?.valueText && d?.valueText !== String(d?.value) ? d?.valueText : d?.value)}
          <span style={{ fontWeight: 400, fontSize: 11, color: "#64748B", marginLeft: 4 }}>{unit}</span>
        </div>
        <div style={{ marginTop: 5 }}>
          <span style={{
            background: s.bg, color: s.color === "#FFF" ? "#fff" : s.color,
            padding: "2px 7px", borderRadius: 4, fontSize: 10, fontWeight: 700
          }}>{d?.status}</span>
        </div>
        {(d?.refMin != null || rMin != null) && (
          <div style={{ color: "#64748B", fontSize: 10, marginTop: 5 }}>
            Ref: {d?.refMin ?? rMin}–{d?.refMax ?? rMax} {unit}
          </div>
        )}
      </div>
    );
  };

  // Status severity order — higher index = worse
  const STATUS_SEVERITY = { "Normal": 0, "Needs Review": 1, "Low": 2, "High": 2, "Abnormal": 3, "Critical Low": 4, "Critical High": 4 };

  const isEGFR = paramName && paramName.toLowerCase().includes("egfr");
  const rawPositive = pctChange !== null && parseFloat(pctChange) > 0;
  const prevSevC   = STATUS_SEVERITY[prev?.status]   ?? 1;
  const latestSevC = STATUS_SEVERITY[latest.status]  ?? 1;

  // changePositive = true → green, false → red, null → neutral grey
  // Logic:
  // 1. Status changed → use status comparison (unambiguous clinical signal)
  // 2. Both Normal + both values within ref range → neutral grey (no direction meaning)
  // 3. Both same non-Normal status → use Low/High type for direction
  // 4. eGFR → always status comparison
  let changePositive; // null = neutral grey
  if (latestSevC < prevSevC) {
    changePositive = true;  // status improved → green
  } else if (latestSevC > prevSevC) {
    changePositive = false; // status worsened → red
  } else if (isEGFR) {
    // eGFR same stage — up is good
    changePositive = rawPositive;
  } else if (latest.status === "Normal" && prev?.status === "Normal") {
    // Both Normal — check if both values are within ref range
    const latestInRange = (latest.refMin == null || latest.value >= latest.refMin) &&
                          (latest.refMax == null || latest.value <= latest.refMax);
    const prevInRange   = (prev.refMin   == null || prev.value   >= prev.refMin)   &&
                          (prev.refMax   == null || prev.value   <= prev.refMax);
    if (latestInRange && prevInRange) {
      changePositive = null; // both within range → neutral grey, no direction
    } else {
      // One was borderline — use Low/High type
      const isLowTypeParam = latest.status === "Low" || latest.status === "Critical Low";
      changePositive = isLowTypeParam ? rawPositive : !rawPositive;
    }
  } else {
    // Same non-Normal status (both Low, both High etc.)
    const isLowTypeParam = latest.status === "Low" || latest.status === "Critical Low";
    changePositive = isLowTypeParam ? rawPositive : !rawPositive;
  }


  if (sorted.length === 1) {
    const isHigh1 = latest.status === "High" || latest.status === "Critical High";
    const isLow1  = latest.status === "Low"  || latest.status === "Critical Low";
    const dotColor1  = isHigh1 ? "#DC2626" : isLow1 ? "#2563EB" : "#16A34A";
    const badgeBg1   = isHigh1 ? "#FEE2E2" : isLow1 ? "#DBEAFE" : "#DCFCE7";
    const badgeCol1  = isHigh1 ? "#B91C1C" : isLow1 ? "#1D4ED8" : "#15803D";
    const badgeTxt1  = isHigh1 ? (latest.status === "Critical High" ? "Critical High" : "High")
                     : isLow1  ? (latest.status === "Critical Low"  ? "Critical Low"  : "Low")
                     : "Normal";
    const valNum1 = parseFloat(latest.value);
    let dotPct1 = 50;
    if (!isNaN(valNum1) && rMin != null && rMax != null) {
      const rng = parseFloat(rMax) - parseFloat(rMin);
      const bMin = parseFloat(rMin) - rng * 0.4;
      const bMax = parseFloat(rMax) + rng * 0.4;
      dotPct1 = Math.min(Math.max(((valNum1 - bMin) / (bMax - bMin)) * 100, 2), 98);
    }
    return (
      <div style={{ padding: "4px 0" }}>
        <div style={{ display: "flex", alignItems: "flex-end", justifyContent: "space-between", marginBottom: 16 }}>
          <div style={{ display: "flex", alignItems: "baseline", gap: 8 }}>
            <span style={{ fontSize: 32, fontWeight: 600, color: dotColor1 }}>
              {isTimeParam(paramName)
                ? (latest.valueText || formatSeconds(latest.value))
                : (latest.valueText && latest.valueText !== String(latest.value) ? latest.valueText : latest.value)}
            </span>
            <span style={{ fontSize: 14, color: "#64748B" }}>{unit}</span>
          </div>
          <span style={{ background: badgeBg1, color: badgeCol1, fontSize: 12, fontWeight: 500, padding: "4px 14px", borderRadius: 99 }}>{badgeTxt1}</span>
        </div>
        {rMin != null && rMax != null && (
          <div style={{ marginBottom: 8 }}>
            <div style={{ position: "relative", height: 6, borderRadius: 99, overflow: "hidden", marginBottom: 8 }}>
              <div style={{ position: "absolute", left: 0, width: "30%", height: "100%", background: "#DBEAFE" }} />
              <div style={{ position: "absolute", left: "30%", width: "40%", height: "100%", background: "#DCFCE7" }} />
              <div style={{ position: "absolute", left: "70%", width: "30%", height: "100%", background: "#FEE2E2" }} />
              <div style={{ position: "absolute", top: -3, width: 12, height: 12, borderRadius: "50%", background: dotColor1, border: "2px solid white", left: `calc(${dotPct1}% - 6px)` }} />
            </div>
            <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11, color: "#94A3B8" }}>
              <span>Low</span>
              <span>Normal {rMin} – {rMax} {unit}</span>
              <span>High</span>
            </div>
          </div>
        )}
        <div style={{ display: "flex", gap: 20, marginTop: 14, paddingTop: 12, borderTop: "1px solid #F1F5F9", fontSize: 12, color: "#64748B" }}>
          <span>Date: <strong style={{ color: "#0F172A" }}>{formatDate(latest.date)}</strong></span>
        </div>
        <div style={{ marginTop: 8, fontSize: 12, color: "#94A3B8" }}>
          Only 1 report in this range — expand date range to see the trend chart
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Summary strip */}
      <div className="trend-summary-bar">
        <div className="trend-summary-item">
          <span className="trend-summary-label">Current Value</span>
          <span className="trend-summary-value" style={{ color: STATUS_STYLE[latest.status]?.color || color }}>
            {isTimeParam(paramName)
              ? (latest.valueText || formatSeconds(latest.value))
              : (latest.valueText && latest.valueText !== String(latest.value) ? latest.valueText : (isNaN(latestVal) ? "—" : latestVal))}
          </span>
          <span className="trend-summary-sub">{unit} · {formatDate(latest.date)}</span>
        </div>
        <div className="trend-divider" />
        {prev && (
          <>
            <div className="trend-summary-item">
              <span className="trend-summary-label">Previous</span>
              <span className="trend-summary-value" style={{ color: "#475569", fontSize: 16 }}>
                {isTimeParam(paramName)
                  ? (prev.valueText || formatSeconds(prev.value))
                  : (prev.valueText && prev.valueText !== String(prev.value) ? prev.valueText : (isNaN(prevVal) ? "—" : prevVal))}
              </span>
              <span className="trend-summary-sub">{unit} · {formatDate(prev.date)}</span>
            </div>
            <div className="trend-divider" />
          </>
        )}
        {(pctChange !== null || (prev && isRangeResult)) && (
          <>
            <div className="trend-summary-item">
              <span className="trend-summary-label">Change</span>
              {isRangeResult ? (
                <>
                  <span className="trend-summary-value" style={{ color: "#94A3B8", fontSize: 14 }}>—</span>
                  <span className="trend-summary-sub">N/A for qualitative range results</span>
                </>
              ) : (
                <>
                  <span className="trend-summary-value" style={{ color: changePositive === null ? "#94A3B8" : changePositive ? "#16A34A" : "#DC2626" }}>
                    {rawPositive ? "+" : ""}{pctChange}%
                  </span>
                  <span className="trend-summary-sub">vs previous result</span>
                </>
              )}
            </div>
            <div className="trend-divider" />
          </>
        )}
        <div className="trend-summary-item">
          <span className="trend-summary-label">Reference Range</span>
          <span className="trend-summary-value" style={{ color: "#16A34A", fontSize: 14, fontWeight: 700 }}>
            {rMin != null ? (() => {
              const tipContent = buildRefTooltip(rMin, rMax, refText, unit);
              return tipContent ? (
                <span className="ref-tip-wrap" style={{ cursor: "help" }}>
                  <span style={{ fontSize: 13, borderBottom: "1px dashed #16A34A" }}>
                    {isMultiRange(refText) ? "Multiple ranges" : `${rMin}–${rMax}`}
                  </span>
                  <div className="ref-tip-box" style={{ maxHeight: "220px", overflowY: "auto" }}>{tipContent}</div>
                </span>
              ) : `${rMin}–${rMax}`;
            })() : "N/A"}
          </span>
          <span className="trend-summary-sub">{unit}</span>
        </div>
        <div className="trend-divider" />
        <div className="trend-summary-item">
          <span className="trend-summary-label">Status</span>
          <span style={{ marginTop: 4 }}><StatusBadge status={latest.status} /></span>
        </div>
      </div>

      {/* Chart — oldest left, newest right */}
      <ResponsiveContainer width="100%" height={250}>
        <ComposedChart data={chartData} margin={{ top: 16, right: 20, left: 4, bottom: 8 }}>
          <defs>
            <linearGradient id={`areaGrad-${paramName}`} x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%"  stopColor={color} stopOpacity={0.15}/>
              <stop offset="95%" stopColor={color} stopOpacity={0}/>
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" vertical={false} />
          <XAxis
            dataKey="date"
            tick={{ fill: "#94A3B8", fontSize: 11 }}
            axisLine={false} tickLine={false}
            tickFormatter={v => { const d = new Date(v); return isNaN(d) ? v : d.getDate() + " " + ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"][d.getMonth()]; }}
          />
          <YAxis
            domain={[yMin, yMax]}
            tick={{ fill: "#94A3B8", fontSize: 11 }}
            axisLine={false} tickLine={false}
            width={44}
            tickFormatter={v => isTimeParam(paramName) ? formatSeconds(v) : Number(v.toFixed(2))}
          />
          <Tooltip content={<CustomTooltip />} />

          {/* Shaded reference range band */}
          {rMin != null && rMax != null && (
            <ReferenceArea
              y1={rMin} y2={rMax}
              fill="#DCFCE7" fillOpacity={0.45}
              ifOverflow="extendDomain"
            />
          )}
          {rMin != null && (
            <ReferenceLine y={rMin} stroke="#16A34A" strokeDasharray="4 4" strokeWidth={1.5}
              label={{ value: `Min ${rMin}`, fill: "#16A34A", fontSize: 9, position: "insideBottomLeft" }} />
          )}
          {rMax != null && (
            <ReferenceLine y={rMax} stroke="#DC2626" strokeDasharray="4 4" strokeWidth={1.5}
              label={{ value: `Max ${rMax}`, fill: "#DC2626", fontSize: 9, position: "insideTopLeft" }} />
          )}

          {/* Area fill */}
          <Area
            type="monotone" dataKey="numericValue"
            stroke="none"
            fill={`url(#areaGrad-${paramName})`}
            connectNulls
          />

          {/* Line */}
          <Line
            type="monotone" dataKey="numericValue"
            stroke={color} strokeWidth={2.5}
            dot={<CustomDot />}
            activeDot={{ r: 8, fill: color, stroke: "white", strokeWidth: 2 }}
            connectNulls
          />
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
}

// ── Result History Sidebar ───────────────────────────────────────────────────
function ResultHistory({ data, unit, color }) {
  const { t } = useTheme();
  // newest first
  const sorted = [...data].sort((a, b) => {
    const diff = new Date(b.date) - new Date(a.date);
    if (diff !== 0) return diff;
    return (b.labId || 0) - (a.labId || 0);
  });
  return (
    <div>
      {sorted.map((d, idx) => {
        const s = STATUS_STYLE[d.status] || STATUS_STYLE["Needs Review"];
        const isLatest = idx === 0;
        const isTitreRefD = d.refText && /^\s*1\s*[:/]\s*\d+\s*$/.test(d.refText.trim());
        const refRange = isTitreRefD ? ('< ' + d.refText.trim()) : (d.refMin != null ? `${d.refMin}–${d.refMax}` : null);
        return (
          <div key={d.date + idx} className={`history-item ${isLatest ? "is-latest" : ""}`}>
            {isLatest && (
              <div style={{
                position: "absolute", top: -1, right: 8,
                background: color, color: "#fff",
                fontSize: 9, fontWeight: 700,
                padding: "1px 7px", borderRadius: "0 0 5px 5px"
              }}>{t.latest}</div>
            )}
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 5 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: "#0F172A" }}>{formatDate(d.date)}</div>
              <div style={{ fontSize: 16, fontWeight: 800, color: s.color === "#FFF" ? "#0F172A" : s.color }}>
                {d.valueText && d.valueText !== String(d.value) ? d.valueText : d.value}
                <span style={{ fontSize: 10, color: "#94A3B8", fontWeight: 400, marginLeft: 3 }}>{unit}</span>
              </div>
            </div>
            <div style={{ height: 3, background: "#E4E8F0", borderRadius: 99, overflow: "hidden", marginBottom: 5 }}>
              {d.value != null && d.refMin != null && d.refMax != null && (
                <div style={{
                  width: `${Math.min(Math.max((d.value - d.refMin) / (d.refMax - d.refMin || 1), 0), 1) * 100}%`,
                  height: "100%", background: s.color === "#FFF" ? "#64748B" : s.color, borderRadius: 99
                }} />
              )}
            </div>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <StatusBadge status={d.status} />
              {refRange && (
                <span style={{ fontSize: 10, color: "#94A3B8" }}>Ref: {refRange} {unit}</span>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ── Main PatientDetail Component ─────────────────────────────────────────────
// ── Stable history sub-components (defined outside PatientDetail to prevent remounting) ──
const HistCard = ({ title, icon, iconBg, canAdd, onAdd, children }) => (
  <div style={{ background:"#fff", borderRadius:14, border:"1.5px solid #E2E8F0", overflow:"hidden", height:"100%", display:"flex", flexDirection:"column" }}>
    <div style={{ padding:"13px 16px", borderBottom:"1px solid #F1F5F9", display:"flex", alignItems:"center", justifyContent:"space-between", flexShrink:0 }}>
      <div style={{ display:"flex", alignItems:"center", gap:8, fontWeight:700, fontSize:14 }}>
        <div style={{ width:30, height:30, borderRadius:8, background:iconBg, display:"flex", alignItems:"center", justifyContent:"center" }}>{icon}</div>
        {title}
      </div>
      {canAdd && <button onClick={onAdd} style={{ display:"flex", alignItems:"center", gap:4, background: title==="Past Medical History"?"#EEF2FF":title==="Past Surgeries"?"#F0FDF4":"#F5F3FF", color: title==="Past Medical History"?"#4F46E5":title==="Past Surgeries"?"#059669":"#7C3AED", border: `1.5px solid ${title==="Past Medical History"?"#C7D2FE":title==="Past Surgeries"?"#BBF7D0":"#DDD6FE"}`, borderRadius:7, padding:"5px 12px", fontSize:12, fontWeight:700, cursor:"pointer" }}>+ Add</button>}
    </div>
    <div style={{ padding:"14px 16px", flex:1 }}>{children}</div>
  </div>
);

const InlineAddForm = ({ children, onSave, onCancel, saving }) => (
  <div style={{ background:"#F8FAFC", border:"1.5px dashed #CBD5E1", borderRadius:10, padding:14, marginTop:12 }}>
    {children}
    <div style={{ display:"flex", gap:8, justifyContent:"flex-end", marginTop:8 }}>
      <button onClick={onCancel} style={{ padding:"6px 14px", background:"#fff", border:"1.5px solid #E2E8F0", borderRadius:7, fontSize:12, color:"#64748B", cursor:"pointer" }}>Cancel</button>
      <button onClick={onSave} disabled={saving} style={{ padding:"6px 16px", background:"#4F46E5", color:"#fff", border:"none", borderRadius:7, fontSize:12, fontWeight:700, cursor:"pointer" }}>{saving?"Saving…":"Add"}</button>
    </div>
  </div>
);

export default function PatientDetail({ patientId, onBack, initialRepId, initialType, initialView, initialParam, context = "patient" }) {
  console.log('[PatientDetail] mounted with initialRepId:', initialRepId, 'initialView:', initialView);
  const { t } = useTheme();
  const { patients, loading, user, editReport, deleteReport, exportToExcel, getPdfUrl, refreshPatient } = useApp();

  // Scoped sessionStorage helpers — prevents bleed between Patient tab and Lab Reports tab
  const pfx = context === "lab" ? "lab_pd_" : "pt_pd_";
  const getStore  = (k)    => sessionStorage.getItem(pfx + k);
  const setStore  = (k, v) => sessionStorage.setItem(pfx + k, v);
  const removeStore = (k)  => sessionStorage.removeItem(pfx + k);

  // Imaging records for this patient
  const [patientImaging, setPatientImaging] = useState([]);
  const [imagingLoading, setImagingLoading] = useState(true);

  // Medications
  const [medications, setMedications]     = useState([]);
  const [medsLoading, setMedsLoading]     = useState(false);
  const [medsError,   setMedsError]       = useState(null);
  const [drugName,    setDrugName]        = useState("");
  const [dose,        setDose]            = useState("");
  const [instructions,setInstructions]    = useState("");
  const [addingMed,   setAddingMed]       = useState(false);
  const [stopModal,   setStopModal]       = useState(null); // { med }
  const [stopReason,  setStopReason]      = useState("");
  const [stoppingMed, setStoppingMed]     = useState(false);
  const [warnModal,   setWarnModal]       = useState(null); // { matchedPast, pendingAdd }
  const [hidePast,    setHidePast]        = useState(false);
  const [drugFieldFocused, setDrugFieldFocused] = useState(false);
  const [showAllergyForm,  setShowAllergyForm]  = useState(false);
  const [activeTab,   setActiveTab]       = useState(() => getStore("tab") || "reports");
  const [route,       setRoute]           = useState("PO (By Mouth)");
  const [frequency,   setFrequency]       = useState("1 + 0 + 1 (Morning & Night)");

  // Allergies
  const [allergies,     setAllergies]     = useState([]);
  const [allergiesLoading, setAllergiesLoading] = useState(false);

  // Vitals
  const [vitals,        setVitals]        = useState([]);
  const [vitalsLoading, setVitalsLoading] = useState(false);
  const [showVitalsModal, setShowVitalsModal] = useState(false);
  const [vitalsForm,    setVitalsForm]    = useState({ date:"", time:"", blood_pressure:"", heart_rate:"", spo2:"", temperature:"", weight:"", heightFt:"", heightIn:"" });
  const [savingVitals,  setSavingVitals]  = useState(false);

  // History
  const [history,         setHistory]         = useState([]);
  const [historyLoading,  setHistoryLoading]  = useState(false);
  const [showHistoryForm, setShowHistoryForm] = useState(null); // "Chronic Condition"|"Surgical"|"Family"|"Social"|null
  const [historyForm,     setHistoryForm]     = useState({ narrative:"", onset_date:"", relation:"Father" });
  const [savingHistory,   setSavingHistory]   = useState(false);
  const [conditionInput,  setConditionInput]  = useState("");
  const [socialEdit,      setSocialEdit]      = useState(false);
  const [socialForm,      setSocialForm]      = useState({ smoking:"Never", smokingDetail:"", betelNut:"No", betelNutDetail:"" });
  const [allergen,      setAllergen]      = useState("");
  const [reaction,      setReaction]      = useState("");
  const [severity,      setSeverity]      = useState("Mild");
  const [addingAllergy, setAddingAllergy] = useState(false);

  const loadVitals = (pid) => {
    setVitalsLoading(true);
    try { const c = sessionStorage.getItem(`emr_cache_vitals_${pid}`); if(c){ setVitals(JSON.parse(c)); setVitalsLoading(false); } else setVitalsLoading(true); } catch(e){}
    vitalsAPI.list(pid)
      .then(res => { const d=res.vitals||[]; setVitals(d); try{sessionStorage.setItem(`emr_cache_vitals_${pid}`,JSON.stringify(d));}catch(e){} })
      .catch(()=>{})
      .finally(()=>setVitalsLoading(false));
  };

  const loadHistory = (pid) => {
    setHistoryLoading(true);
    try { const c = sessionStorage.getItem(`emr_cache_history_${pid}`); if(c){ setHistory(JSON.parse(c)); setHistoryLoading(false); } else setHistoryLoading(true); } catch(e){}
    historyAPI.list(pid)
      .then(res => { const d=res.history||[]; setHistory(d); try{sessionStorage.setItem(`emr_cache_history_${pid}`,JSON.stringify(d));}catch(e){} })
      .catch(()=>{})
      .finally(()=>setHistoryLoading(false));
  };

  const loadAllergies = (pid) => {
    // Show cached instantly, fetch fresh in background
    try {
      const cached = sessionStorage.getItem(`emr_cache_allergies_${pid}`);
      if (cached) { setAllergies(JSON.parse(cached)); setAllergiesLoading(false); }
      else setAllergiesLoading(true);
    } catch(e) { setAllergiesLoading(true); }
    allergyAPI.list(pid)
      .then(res => {
        const data = res.allergies || [];
        setAllergies(data);
        try { sessionStorage.setItem(`emr_cache_allergies_${pid}`, JSON.stringify(data)); } catch(e) {}
      })
      .catch(() => {})
      .finally(() => setAllergiesLoading(false));
  };

  const loadMedications = (pid) => {
    setMedsError(null);
    // Show cached instantly, fetch fresh in background
    try {
      const cached = sessionStorage.getItem(`emr_cache_meds_${pid}`);
      if (cached) { setMedications(JSON.parse(cached)); setMedsLoading(false); }
      else setMedsLoading(true);
    } catch(e) { setMedsLoading(true); }
    medicationAPI.list(pid)
      .then(res => {
        const data = res.medications || [];
        setMedications(data);
        try { sessionStorage.setItem(`emr_cache_meds_${pid}`, JSON.stringify(data)); } catch(e) {}
      })
      .catch(() => setMedsError("Failed to load medications"))
      .finally(() => setMedsLoading(false));
  };
  const [showCategoryDD, setShowCategoryDD] = useState(false);
  const [activeTooltip,  setActiveTooltip]  = useState(null); // "orig-{i}" or "val-{i}"
  const [tooltipPos,     setTooltipPos]     = useState({ x: 0, y: 0 });
  const categoryDDRef = useRef(null);

  // Fetch full patient data from API (paginated list only has summary)
  const [fullPatient, setFullPatient] = useState(null);
  const [reportPage, setReportPage] = useState(1);
  const REPORT_PAGE_SIZE = 20;
  const [loadingReport, setLoadingReport] = useState(false);

  const [fetchingPatient, setFetchingPatient] = useState(true);

  const loadPatientData = (page = 1) => {
    if (page === 1) {
      // Show cached instantly, fetch fresh in background
      // But don't overwrite fullPatient if summary already set it with report_types
      try {
        const cached = sessionStorage.getItem(`emr_cache_patient_${patientId}`);
        if (cached) {
          const parsed = JSON.parse(cached);
          // Only restore if summary hasn't already set fullPatient
          setFullPatient(prev => {
            if (prev && prev.report_types?.length > 0) return prev; // keep summary data
            return parsed;
          });
          setFetchingPatient(false);
        } else { setFetchingPatient(true); }
      } catch(e) { setFetchingPatient(true); }
    }
    patientAPI.get(patientId, page, REPORT_PAGE_SIZE)
      .then(data => {
        const normalized = normalizePatient(data);
        // Preserve report_types from summary if patientAPI didn't return them
        setFullPatient(prev => {
          const summaryReportTypes = prev?.report_types?.length > 0 ? prev.report_types : (normalized.report_types || []);
          return { ...normalized, report_types: summaryReportTypes };
        });
        if (page === 1) {
          try { sessionStorage.setItem(`emr_cache_patient_${patientId}`, JSON.stringify(normalized)); } catch(e) {}
        }
      })
      .catch(() => {})
      .finally(() => setFetchingPatient(false));
  };

  // ── loadSummary: single call replacing 6 separate loads ─────────────────────
  const loadSummary = (pid) => {
    // Step 1: show everything from cache instantly
    try {
      const cached = sessionStorage.getItem(`emr_cache_summary_${pid}`);
      if (cached) {
        const s = JSON.parse(cached);
        if (s.patient) {
          const patientWithReports = { ...s.patient, report_types: s.report_types||[], total_reports: s.total_reports||0, reports: s.patient.reports||[], report_page:1, report_page_size:20, report_total_pages:1 };
          setFullPatient(normalizePatient(patientWithReports)); setFetchingPatient(false);
        }
        if (s.medications) { setMedications(s.medications); setMedsLoading(false); }
        if (s.allergies)   { setAllergies(s.allergies); setAllergiesLoading(false); }
        if (s.vitals)      { setVitals(s.vitals); setVitalsLoading(false); }
        if (s.history)     { setHistory(s.history); setHistoryLoading(false); }
        if (s.imaging)     { setPatientImaging(s.imaging); setImagingLoading(false); }
      }
    } catch(e) {}

    // Step 2: fetch fresh in background, update all at once
    patientSummaryAPI.get(pid)
      .then(s => {
        if (s.patient) {
          // Merge report_types + total_reports into patient so normalizePatient works correctly
          const patientWithReports = {
            ...s.patient,
            report_types:       s.report_types || [],
            total_reports:      s.total_reports || 0,
            reports:            s.patient.reports || [],
            report_page:        1,
            report_page_size:   20,
            report_total_pages: 1,
          };
          setFullPatient(normalizePatient(patientWithReports));
        }
        if (s.medications) { setMedications(s.medications); }
        if (s.allergies)   { setAllergies(s.allergies); }
        if (s.vitals)      { setVitals(s.vitals); }
        if (s.history)     { setHistory(s.history); }
        if (s.imaging)     { setPatientImaging(s.imaging); }
        // Save full summary to cache
        try { sessionStorage.setItem(`emr_cache_summary_${pid}`, JSON.stringify(s)); } catch(e) {}
      })
      .catch(() => {
        // Fallback: if summary fails, load individually
        loadPatientData(1);
        loadMedications(pid);
        loadAllergies(pid);
        loadVitals(pid);
        loadHistory(pid);
      })
      .finally(() => {
        setFetchingPatient(false);
        setMedsLoading(false);
        setAllergiesLoading(false);
        setVitalsLoading(false);
        setHistoryLoading(false);
        setImagingLoading(false);
      });
  };

  const invalidateSummaryCache = (pid) => {
    try { sessionStorage.removeItem(`emr_cache_summary_${pid||patientId}`); } catch(e) {}
  };

  useEffect(() => {
    setReportPage(1);
    loadSummary(patientId);

    // Load full report data if needed for current view
    const savedView = context === "lab" ? (initialView || getStore("view") || "report") : (getStore("view") || "categories");
    if (savedView === "list" || savedView === "report" || savedView === "trend" || savedView === "lab-categories") {
      loadPatientData(1);
    }
  }, [patientId]);

  // Fetch imaging records for this patient
  useEffect(() => {
    setImagingLoading(true);
    imagingAPI.getForPatient(patientId)
      .then(res => {
        const imgs = Array.isArray(res) ? res : (res.imaging || []);
        setPatientImaging(imgs);
      })
      .catch(() => setPatientImaging([]))
      .finally(() => setImagingLoading(false));
  }, [patientId]);

  // Close category dropdown on outside click
  useEffect(() => {
    const handler = (e) => {
      if (categoryDDRef.current && !categoryDDRef.current.contains(e.target)) {
        setShowCategoryDD(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const navigateToImaging = () => {
    sessionStorage.setItem("emr_img_patient", JSON.stringify({ id: patient.id, name: patient.name }));
    window.dispatchEvent(new CustomEvent("emr:navigate", { detail: { page: "imaging" } }));
  };

  const handleReportPageChange = (newPage) => {
    setReportPage(newPage);
    // Clear trendAllReports so typeReports uses the newly loaded page, not all reports
    setTrendAllReports(null);
    setTrendType(null);
    loadPatientData(newPage);
  };

  // Fetch full report with values from API before viewing
  // This fixes the blank white screen when clicking View on a report
  const handleViewReport = (rep) => {
    if (rep.values && rep.values.length > 0) {
      setRep(rep);
      setStore("rep", rep.id);
      setView("report");
      setStore("view", "report");
      setEditMode(false);
      return;
    }
    // Values not loaded — fetch from API
    setLoadingReport(true);
    (()=>{
      reportAPI.get(rep.id)
        .then(data => {
          const fullRep = normalizeReport ? normalizeReport(data) : data;
          setRep(fullRep);
          setStore("rep", fullRep.id);
          setView("report");
          setStore("view", "report");
          setEditMode(false);
        })
        .catch(() => {
          setRep(rep);
          setView("report");
        })
        .finally(() => setLoadingReport(false));
    });
  };

  const patientSummary = useMemo(() => patients.find(p => p.id === patientId), [patients, patientId]);
  const patient = fullPatient || patientSummary;
  const isLoadingFull = !fullPatient && !!patientSummary;

  const [view,      setView]      = useState(() => {
    // Lab Reports context: use initialView if provided, else "report"
    if (context === "lab") return initialView || "report";
    const v = getStore("view") || "categories";
    const hasRep = getStore("rep");
    // Allow trend/report to restore on reload if we have rep context
    if ((v === "trend" || v === "report") && hasRep) return v;
    // Reset report/trend to list if no context
    if (v === "report" || v === "trend") return "list";
    if (v === "medications") return "medications";
    if (v === "allergies")   return "allergies";
    return v;
  });
  const [selType,   setType]      = useState(() => context === "lab" ? (initialType || null) : (getStore("type") || null));
  const [selCategory, setCategory] = useState(() => getStore("category") || null);
  // Reset trend data when report type changes
  const [trendType, setTrendType] = useState(null); // tracks which type trendAllReports belongs to
  const setTypeAndReset = (type) => { setType(type); setTrendAllReports(null); setTrendType(null); isFetchingRef.current = false; ++trendFetchRef.current; };
  const [activeRep, setRep]       = useState(null);
  const [trendAllReports, setTrendAllReports] = useState(null); // all reports of selType, loaded when trend opens
  const restoredRef = useRef(false);

  // Restore active report after patient data loads
  useEffect(() => {
    console.log('[restore] fired — restoredRef:', restoredRef.current, 'reports:', patient?.reports?.length, 'initialRepId:', initialRepId);
    if (restoredRef.current || !patient?.reports?.length) return;
    const hasValues = patient.reports.some(r => r.values && r.values.length > 0);
    console.log('[restore] hasValues:', hasValues, 'initialRepId:', initialRepId);
    if (!hasValues && !initialRepId) return;
    restoredRef.current = true;

    const repId = initialRepId || getStore("rep");
    console.log('[restore] repId:', repId, 'searching in', patient.reports.length, 'reports');
    const found = patient.reports.find(r => r.id === repId);
    console.log('[restore] found:', found?.id);
    const savedView = (initialView && (initialView === "trend" || initialView === "report")) ? initialView : (initialRepId ? "report" : (context === "lab" ? (getStore("view") || "report") : (getStore("view") || "categories")));

    if (repId) {
      const found = patient.reports.find(r => r.id === repId);
      if (found) {
        setRep(found);
        const repType = initialType || found.type;
        if (repType) { setType(repType); setStore("type", repType); }
        if (savedView === "report" || savedView === "trend") {
          setView(savedView);
          setStore("view", savedView);
        } else {
          setView("report");
          setStore("view", "report");
        }
        setStore("rep", repId);
      } else {
        // Report not in current page — fetch it directly by ID
        reportAPI.get(repId)
          .then(data => {
            const fullRep = normalizeReport ? normalizeReport(data) : data;
            setRep(fullRep);
            const repType = initialType || fullRep.type;
            if (repType) { setType(repType); setStore("type", repType); }
            if (savedView === "report" || savedView === "trend") {
              setView(savedView);
              setStore("view", savedView);
            } else {
              setView("report");
              setStore("view", "report");
            }
            setStore("rep", repId);
          })
          .catch(() => {
            // Can't find report — go to categories
            setView("categories");
            removeStore("rep"); removeStore("view"); removeStore("type");
          });
      }
    }
  }, [patient]);

  // Direct navigation when opened from lab ID search
  useEffect(() => {
    if (!initialRepId) return;
    // Fetch the report directly and navigate to it
    reportAPI.get(initialRepId)
      .then(data => {
        const fullRep = normalizeReport ? normalizeReport(data) : data;
        setRep(fullRep);
        const repType = fullRep.type;
        if (repType) { setType(repType); setStore("type", repType); }
        // Respect initialView (e.g. "trend") — don't always force "report"
        const targetView = (initialView === "trend" || initialView === "report") ? initialView : "report";
        setView(targetView);
        setStore("view", targetView);
        setStore("rep", initialRepId);
        restoredRef.current = true;
      })
      .catch(() => setView("categories"));
  }, [initialRepId]);
  const [editMode,      setEditMode]      = useState(false);
  const [editVals,      setEditVals]      = useState([]);
  const [showUpload,    setUpload]        = useState(false);
  const [showDelete,    setDel]           = useState(null);
  const [saving,        setSaving]        = useState(false);
  const [range,         setRange]         = useState("All");
  const [dateFrom,  setFrom]      = useState("");
  const [useYearOnly, setYearOnly] = useState(true);
  const [fromYear,  setFromYear]   = useState("");
  const [toYear,    setToYear]     = useState("");
  const [dateTo,    setTo]        = useState("");
  const [pinned,    setPinned]    = useState([]);
  const [activeTP,  setTP]        = useState(() => initialParam || getStore('param') || null);
  // Persist activeTP to scoped storage whenever it changes
  useEffect(() => {
    if (activeTP) setStore('param', activeTP);
  }, [activeTP]);
  const [chartView, setChartView] = useState("chart");
  const [highlightedParam, setHighlightedParam] = useState(null);
  const rowRefs = useRef({});

  const canUpload = ["admin","doctor","technician"].includes(user?.role);
  const canEdit   = ["admin","doctor","technician"].includes(user?.role);
  const canDelete = ["admin", "technician"].includes(user?.role);

  // All useMemo hooks MUST be before early returns (React rules of hooks)
  // byType: group by specific sub-type (report_type value)
  const byType      = useMemo(() => {
    const bt = {};
    if (fullPatient?.report_types?.length > 0) {
      fullPatient.report_types.forEach(t => {
        bt[t.type] = (fullPatient?.reports || patient?.reports || []).filter(r => r.type === t.type);
        bt[t.type]._count = t.count;
        bt[t.type]._abnormal = t.abnormal_reports;
      });
    } else {
      (patient?.reports || []).forEach(r => { (bt[r.type] = bt[r.type] || []).push(r); });
    }
    return bt;
  }, [patient, fullPatient]);

  // byCategory: group sub-types under their parent category
  // Unrecognized types (not in SUBTYPE_TO_CATEGORY) go under "Other"
  const byCategory = useMemo(() => {
    const bc = {};
    Object.entries(byType).forEach(([type, reps]) => {
      const cat = getCategory(type);
      // If getCategory returned the type itself, it's unrecognized — put under Other
      const resolvedCat = (cat === type && !Object.keys(REPORT_SUBTYPES).includes(type)) ? "Other" : cat;
      if (!bc[resolvedCat]) bc[resolvedCat] = { subtypes: {}, totalCount: 0, lastDate: null };
      bc[resolvedCat].subtypes[type] = reps;
      const count = reps._count || reps.length || 0;
      bc[resolvedCat].totalCount += count;
      // Track most recent date
      const dates = Array.isArray(reps) ? reps.map(r => r.date).filter(Boolean) : [];
      if (dates.length > 0) {
        const maxDate = dates.sort().reverse()[0];
        if (!bc[resolvedCat].lastDate || maxDate > bc[resolvedCat].lastDate) bc[resolvedCat].lastDate = maxDate;
      }
    });
    return bc;
  }, [byType]);
  // For trend view, use trendAllReports (all pages) if available, otherwise fall back to current page
  const typeReports = useMemo(() => {
    // Only use trendAllReports if it belongs to the current selType — prevents stale data from previous type
    const validTrend = trendAllReports !== null && trendType === selType ? trendAllReports : null;
    return validTrend !== null ? validTrend : (selType ? (byType[selType] || []) : []);
  }, [byType, selType, trendAllReports, trendType]);
  const meta        = TYPE_META[selType] || TYPE_META.Other;

  const filtered = useMemo(() => {
    const toMs = s => s ? new Date((s||"").replace(/(\.\d{3})\d+/, '$1')).getTime() : 0;
    return filterRange(typeReports, range, dateFrom, dateTo, fromYear, toYear, useYearOnly)
      .sort((a, b) => {
        const dateDiff = new Date(a.date) - new Date(b.date);
        if (dateDiff !== 0) return dateDiff;
        return toMs(a.createdAt) - toMs(b.createdAt);
      });
  }, [typeReports, range, dateFrom, dateTo, fromYear, toYear, useYearOnly]);

  // TEMP DEBUG — remove after fixing
  if (filtered.length > 0 && filtered[0]?.type?.includes('HBsAg')) {
    console.log('[DEBUG] filtered order:');
    filtered.forEach((r,i) => console.log(i, r.id, r.date, r.createdAt, 'cut:', r.values?.find(v=>v.name==='Cut of Value')?.value));
  }

  const allParams = useMemo(() => {
    // Only collect parameters from the currently selected report type.
    // Without this filter, parameters from Hormone reports bleed into
    // Hematology trend views and vice versa.
    const set = new Set();
    filtered.forEach(r => {
      if (!selType || r.type === selType) {
        r.values.forEach(v => set.add(v.name));
      }
    });
    return [...set];
  }, [filtered, selType]);

  // Auto-select first param when on trend view with no activeTP (e.g. on reload)
  useEffect(() => {
    if (view === "trend" && !activeTP && allParams.length > 0) {
      setTP(allParams[0]);
      setStore("param", allParams[0]);
    }
  }, [view, allParams, activeTP]);

  // Declarative fetch — fires whenever view=trend or view=list and all type data is missing
  const [fetchingTrend, setFetchingTrend] = useState(false);
  const trendFetchRef = useRef(0);
  const isFetchingRef = useRef(false);
  useEffect(() => {
    if ((view === "trend" || view === "list") && selType && patientId && trendType !== selType && !isFetchingRef.current) {
      isFetchingRef.current = true;
      const fetchId = ++trendFetchRef.current;
      setFetchingTrend(true);
      patientAPI.get(patientId, 1, 200)
        .then(data => {
          if (fetchId !== trendFetchRef.current) return;
          const norm = normalizePatient ? normalizePatient(data) : data;
          const allOfType = (norm.reports || []).filter(r => r.type === selType);
          setTrendAllReports(allOfType);
          setTrendType(selType);
        })
        .catch(() => {
          if (fetchId !== trendFetchRef.current) return;
          setTrendAllReports([]);
          setTrendType(selType);
        })
        .finally(() => {
          isFetchingRef.current = false;
          if (fetchId === trendFetchRef.current) setFetchingTrend(false);
        });
    }
  }, [view, selType, patientId, trendType]);

  const trendData = useMemo(() => {
    if (!activeTP) return [];
    return filtered
      .map(r => {
        const v = r.values.find(vv => vv.name === activeTP);
        // Exclude TNP (Test Not Performed) values from trend — they are not measured results
        if (!v || v.status === "TNP" || (v.valueText || "").toLowerCase() === "not needed") return null;
        return {
          date: r.date,
          createdAt: r.createdAt || r.date,
          labId: parseInt(r.labId || r.lab_id || 0),
          value: v.value,
          valueText: v.valueText,
          status: v.status,
          refMin: v.refMin,
          refMax: v.refMax,
        };
      })
      .filter(Boolean);
  }, [filtered, activeTP]);

  const refForParam = useMemo(() => {
    if (!activeTP) return {};
    // Use most recent report's reference range (most clinically relevant)
    // Use labId as tiebreaker when dates are identical (higher labId = later upload)
    const sorted = [...filtered].sort((a, b) => {
      const dateDiff = new Date(b.date) - new Date(a.date);
      if (dateDiff !== 0) return dateDiff;
      return (parseInt(b.labId || b.lab_id || 0)) - (parseInt(a.labId || a.lab_id || 0));
    });
    for (const r of sorted) {
      const v = r.values.find(vv => vv.name === activeTP);
      if (v) return { refMin: v.refMin, refMax: v.refMax, unit: v.unit, refText: v.refText || "" };
    }
    return {};
  }, [filtered, activeTP]);

  // activeReport always uses activeRep directly.
  // Previously used patient.reports.find() which returned stale data after optimistic saves
  // since patient state is never updated after editReport(). Using activeRep directly
  // means setRep() immediately reflects in the UI without a full patient refresh.
  const activeReport = activeRep || null;

  const startEdit = useCallback(() => {
    if (!activeReport) return;
    setEditVals(activeReport.values.map(v => {
      // Recalculate status for titre format values (1:80 etc.) in case DB has stale status
      const vt = v.valueText || "";
      // Handle BT/CT time format — value stored as seconds, recalculate status
      const nameLowerSt = (v.name || "").toLowerCase();
      const isBTCTst = nameLowerSt.includes("bleeding time") || nameLowerSt.includes("clotting time");
      if (isBTCTst && v.value != null) {
        const secs = parseFloat(v.value);
        const rMin = v.refMin ?? 60;
        const rMax = v.refMax ?? 420;
        return { ...v,
          status: secs > rMax ? "High" : secs < rMin ? "Low" : "Normal",
          abnormal: secs > rMax || secs < rMin };
      }
      const titreM = vt.match(/^\s*1\s*[:/]\s*(\d+(?:\.\d+)?)\s*$/);
      if (titreM && v.refMax != null) {
        const titreVal = parseFloat(titreM[1]);
        const isHigh = titreVal > v.refMax;
        // refMin is null for titres — no lower bound
        return { ...v, value: titreVal,
          status: isHigh ? "High" : "Normal",
          abnormal: isHigh };
      }
      // Handle <N prefix (e.g. <5.0 for Beta-hCG) — value=null, status from threshold
      const ltM = vt.match(/^<\s*(\d+(?:\.\d+)?)\s*$/);
      if (ltM && v.refMax != null) {
        const threshold = parseFloat(ltM[1]);
        const isNorm = threshold <= v.refMax;
        return { ...v, value: null,
          status: isNorm ? "Normal" : "High",
          abnormal: !isNorm };
      }
      // Handle microscopy range format "3-5" — use upper bound for status
      const rangeM = vt.match(/^(\d+(?:\.\d+)?)\s*[-–]\s*(\d+(?:\.\d+)?)\s*$/);
      if (rangeM) {
        const lower = parseFloat(rangeM[1]);
        const upper = parseFloat(rangeM[2]);
        const nameLowerSE = (v.name || "").toLowerCase();
        const MICROSCOPY_DEFAULTS = {
          "pus cells": 5, "pus cells (stool)": 4, "pus cells (urine)": 5,
          "rbc (stool)": 2, "red blood cells (stool)": 2, "red blood cells": 2,
          "epithelial cells": 5, "epithelial cells (stool)": 2,
        };
        const rMin = v.refMin ?? 0;
        let rMax = v.refMax;
        if (rMax == null && MICROSCOPY_DEFAULTS[nameLowerSE] !== undefined) {
          rMax = MICROSCOPY_DEFAULTS[nameLowerSE];
        }
        if (rMax != null) {
          const isAbn = upper > rMax || lower < rMin;
          return { ...v, value: upper,
            status: isAbn ? "Abnormal" : "Normal",
            abnormal: isAbn };
        }
      }
      return { ...v };
    }));
    setEditMode(true);
  }, [activeReport]);

  // Early return AFTER all hooks
  if (!patient) {
    if ((fetchingPatient && !fullPatient) || (loading && !fullPatient)) return (
      <div style={{ padding: "24px 0" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
          <div>
            <div style={{ width: 200, height: 22, background: "#E4E8F0", borderRadius: 6, marginBottom: 8, animation: "pulse 1.5s ease-in-out infinite" }} />
            <div style={{ width: 300, height: 14, background: "#F1F5F9", borderRadius: 6, animation: "pulse 1.5s ease-in-out infinite" }} />
          </div>
          <button className="btn btn-secondary" onClick={() => { ['view','type','rep','param'].forEach(k => removeStore(k)); onBack(); }}>← Back</button>
        </div>
        <div style={{ display: "flex", gap: 12, marginBottom: 20 }}>
          {[1,2,3].map(i => <div key={i} style={{ flex: 1, height: 80, background: "#F1F5F9", borderRadius: 10, animation: "pulse 1.5s ease-in-out infinite" }} />)}
        </div>
        <div style={{ height: 200, background: "#F8FAFC", borderRadius: 12, display: "flex", alignItems: "center", justifyContent: "center", color: "#94A3B8", fontSize: 13 }}>
          Loading patient data...
        </div>
        <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }@keyframes pulse { 0%,100%{opacity:1} 50%{opacity:0.5} }`}</style>
      </div>
    );
    return (
      <div className="card">
        <div className="empty-state">
          <h3>Patient not found</h3>
          <p style={{ fontSize: 13, color: "#94A3B8", marginBottom: 12 }}>This patient may have been deleted or the link is invalid.</p>
          <button className="btn btn-ghost" onClick={() => {
            ['view','type','rep','param'].forEach(k => removeStore(k)); sessionStorage.removeItem('emr_selected_patient');
            onBack();
          }}>{t.backToPatients}</button>
        </div>
      </div>
    );
  }

  const updateEdit = (i, field, val) => {
    setEditVals(prev => prev.map((v, idx) => {
      if (idx !== i) return v;
      // Handle titre format (1:80, 1:160) — extract denominator as numeric value
      let processedVal = val;
      let titreDetected = false;
      let lessThanDetected = false;
      if (field === "value" && typeof val === "string") {
        const titreM = val.match(/^\s*1\s*[:/]\s*(\d+(?:\.\d+)?)\s*$/);
        if (titreM) {
          processedVal = parseFloat(titreM[1]);
          titreDetected = true;
        }
        // Handle BT/CT time format "X min Y sec" → total seconds
        const nameLowerEdit = (v.name || "").toLowerCase();
        const isBTCTEdit = nameLowerEdit.includes("bleeding time") || nameLowerEdit.includes("clotting time");
        if (isBTCTEdit && field === "value" && typeof val === "string") {
          const timeM = val.match(/(\d+)\s*min\w*\s*(?:(\d+)\s*sec\w*)?/i);
          if (timeM) {
            const totalSecs = parseInt(timeM[1]) * 60 + parseInt(timeM[2] || 0);
            const rMin = parseFloat(v.refMin ?? 60);
            const rMax = parseFloat(v.refMax ?? 420);
            return { ...v, value: totalSecs, valueText: val.trim(),
              status: totalSecs > rMax ? "High" : totalSecs < rMin ? "Low" : "Normal",
              abnormal: totalSecs > rMax || totalSecs < rMin };
          }
        }
        const ltM = !titreDetected && val.match(/^<\s*(\d+(?:\.\d+)?)\s*$/);
        if (ltM) {
          const threshold = parseFloat(ltM[1]);
          const rMax = parseFloat(v.refMax);
          const updated = { ...v, value: null, valueText: val.trim(),
            status: (!isNaN(rMax) && threshold <= rMax) ? "Normal" : "High",
            abnormal: !(!isNaN(rMax) && threshold <= rMax) };
          return updated;
        }
        // Handle microscopy range format "3-5", "4-6" — use upper bound for status
        const rangeMEdit = !titreDetected && val.match(/^(\d+(?:\.\d+)?)\s*[-–]\s*(\d+(?:\.\d+)?)\s*$/);
        if (rangeMEdit) {
          const lower = parseFloat(rangeMEdit[1]);
          const upper = parseFloat(rangeMEdit[2]);
          // Use stored refMax, or fall back to known microscopy defaults
          const nameLowerEdit = (v.name || "").toLowerCase();
          const MICROSCOPY_DEFAULTS = {
            "pus cells": 5, "pus cells (stool)": 4, "pus cells (urine)": 5,
            "rbc (stool)": 2, "red blood cells (stool)": 2, "red blood cells": 2,
            "epithelial cells": 5, "epithelial cells (stool)": 2,
          };
          const rMinEdit = parseFloat(v.refMin ?? 0);
          let rMaxEdit = parseFloat(v.refMax);
          if (isNaN(rMaxEdit) && MICROSCOPY_DEFAULTS[nameLowerEdit] !== undefined) {
            rMaxEdit = MICROSCOPY_DEFAULTS[nameLowerEdit];
          }
          const isAbn = !isNaN(rMaxEdit) && (upper > rMaxEdit || (!isNaN(rMinEdit) && lower < rMinEdit));
          return { ...v, value: upper, valueText: val.trim(),
            status: isNaN(rMaxEdit) ? "Needs Review" : isAbn ? "Abnormal" : "Normal",
            abnormal: isAbn };
        }
        // Handle intermediate range typing "3-" — keep Needs Review until complete
        if (!titreDetected && val.match(/^\d+[-–]\d*$/)) {
          return { ...v, value: null, valueText: val.trim(),
            status: "Needs Review", abnormal: false };
        }
      }
      const updated = { ...v, [field]: field === "value" ? (val === "" ? null : processedVal) : val };
      if (field === "value" || field === "refMin" || field === "refMax") {
        const num  = parseFloat(field === "value"  ? processedVal : updated.value);
        const rMin = parseFloat(field === "refMin" ? val : updated.refMin);
        const rMax = parseFloat(field === "refMax" ? val : updated.refMax);
        if (!isNaN(num) && !isNaN(rMax)) {
          // For titre params refMin is null — only compare against refMax
          if (isNaN(rMin)) {
            updated.abnormal = num > rMax;
            updated.status   = num > rMax ? "High" : "Normal";
          } else {
            updated.abnormal = num < rMin || num > rMax;
            updated.status   = num > rMax ? "High" : num < rMin ? "Low" : "Normal";
          }
        } else if (field === "value" && val === "") {
          // User cleared the input — reset value and valueText
          updated.value = null;
          updated.valueText = "";
          updated.status = "Needs Review";
          updated.abnormal = false;
        } else if (field === "value" && isNaN(num)) {
          // Text result typed — auto-capitalise known words then auto-set status
          const CAPITALISE_MAP = {
            "positive": "Positive", "negative": "Negative",
            "reactive": "Reactive", "non-reactive": "Non-Reactive", "non reactive": "Non-Reactive",
            "found": "Found", "not found": "Not Found",
            "present": "Present", "absent": "Absent",
            "detected": "Detected", "not detected": "Not Detected",
            "normal": "Normal", "abnormal": "Abnormal",
            "nil": "Nil", "compatible": "Compatible", "incompatible": "Incompatible",
            "no fungi": "No Fungi", "fungi found": "Fungi Found", "fungi present": "Fungi Present",
            "significant": "Significant", "non-significant": "Non-Significant",
          };
          const formatted = CAPITALISE_MAP[val.trim().toLowerCase()] || val;
          const t = formatted.trim().toLowerCase();
          const ABN_WORDS = ["positive","reactive","found","present","detected","incompatible","significant","fungi","hyphae","spores","+","++","+++"];
          const NRM_WORDS = ["negative","non-reactive","non reactive","not found","not seen","absent","normal","compatible","non-significant","not detected","no fungi","no fungal","no organism","no growth","nil"];
          const isAbn = ABN_WORDS.includes(t) || t.startsWith("+") || ABN_WORDS.slice(0,-3).some(w => t.includes(w));
          const isNrm = NRM_WORDS.includes(t) || NRM_WORDS.some(w => t.includes(w));
          updated.value = null;
          updated.valueText = formatted;
          if (isNrm)        { updated.status = "Normal";       updated.abnormal = false; }
          else if (isAbn)   { updated.status = "Abnormal";     updated.abnormal = true;  }
          else              { updated.status = "Needs Review"; updated.abnormal = false; }
        }
      }
      if (field === "status") {
        updated.abnormal = !["Normal", "Needs Review"].includes(val);
      }
      if (field === "value") updated.valueText = titreDetected ? val.trim() : (val || "");
      return updated;
    }));
  };

  const saveEdit = async () => {
    if (!activeReport) return;
    setSaving(true);
    try {
      await editReport(patient.id, activeReport.id, editVals, user.role);
      // Apply edited values to local state immediately — editVals has the correct data
      const updatedRep = { ...activeReport, values: editVals };
      setRep(updatedRep);
      setEditMode(false);
      // Background refresh for trend chart only — don't overwrite current report display
      try { await refreshPatient(patient.id); } catch (_) {}
    } catch (e) {
      alert("Save failed: " + e.message);
    } finally {
      setSaving(false);
    }
  };

  const openPdf = async (id) => {
    const rep = patient?.reports?.find(r => r.id === id) || activeReport;
    if (!rep) return;

    // Real file exists — open via authenticated endpoint using Bearer token
    if (rep.storageUrl || rep.pdfName) {
      const BASE = process.env.REACT_APP_API_URL || (window.location.hostname === "localhost" ? "http://localhost:8000" : "https://nashman-prod-production-c773.up.railway.app");
      const token = localStorage.getItem("emr_token");
      try {
        // Fetch the file as blob using Bearer token — avoids dtok expiry issues
        const res = await fetch(`${BASE}/reports/${rep.id}/pdf`, {
          headers: { "Authorization": "Bearer " + token }
        });
        if (res.ok) {
          const blob = await res.blob();
          const blobUrl = URL.createObjectURL(blob);
          window.open(blobUrl, "_blank");
          setTimeout(() => URL.revokeObjectURL(blobUrl), 60000);
          return;
        }
      } catch (e) {
        console.error("View report failed:", e);
      }
    }

    // No file (Excel import) — generate styled HTML template
    const patient_info = patient;
    // Deduplicate values by name (keep first occurrence)
    const seen = new Set();
    const uniqueVals = rep.values.filter(v => { if (seen.has(v.name)) return false; seen.add(v.name); return true; });
    const rows = uniqueVals.map(v => `
      <tr style="border-bottom:1px solid #eee">
        <td style="padding:8px 12px;font-weight:500">${v.name}</td>
        <td style="padding:8px 12px;text-align:right;font-weight:700;color:${v.abnormal ? "#DC2626" : "#16A34A"}">${v.valueText || v.value}</td>
        <td style="padding:8px 12px;color:#64748B">${v.unit || ""}</td>
        <td style="padding:8px 12px;color:#475569;font-size:12px">${(() => {
          const ref = v.refText || (v.refMin != null ? v.refMin + "–" + v.refMax : "—");
          const safe = ref.replace(/&/g,"&amp;").replace(/</g,"&lt;").replace(/>/g,"&gt;");
          if (ref.length > 25) {
            const short = safe.substring(0, 25) + "…";
            return `<span title="${safe}" style="cursor:help;border-bottom:1px dashed #94A3B8">${short}</span>`;
          }
          return safe;
        })()}</td>
        <td style="padding:8px 12px;font-size:11px;font-weight:600;color:${v.abnormal ? "#DC2626" : "#16A34A"}">${v.status || (v.abnormal ? "Abnormal" : "Normal")}</td>
      </tr>`).join("");
    const html = `<!DOCTYPE html><html><head><meta charset="UTF-8"><title>Lab Report — ${formatDate(rep.date)}</title>
      <meta name="viewport" content="width=device-width, initial-scale=1">
      <style>
        body{font-family:Arial,sans-serif;margin:40px;color:#0F172A;max-width:900px;margin:40px auto}
        .header{text-align:center;border-bottom:2px solid #1a237e;padding-bottom:12px;margin-bottom:16px}
        h1{font-size:20px;font-weight:800;color:#1a237e;margin:0;text-transform:uppercase}
        h2{font-size:12px;color:#475569;margin:4px 0 0}
        h3{font-size:13px;font-weight:700;color:#1a237e;text-transform:uppercase;letter-spacing:1px;margin:8px 0 0}
        .meta{display:grid;grid-template-columns:1fr 1fr;gap:4px 24px;font-size:12px;margin:12px 0;padding:10px 14px;background:#F8FAFC;border-radius:6px;border:1px solid #E2E8F0}
        .meta span b{color:#0F172A}
        table{width:100%;border-collapse:collapse;margin-top:16px}
        thead tr{background:#EEF2FF}
        th{padding:9px 12px;text-align:left;font-size:11px;color:#1a237e;font-weight:700;text-transform:uppercase;letter-spacing:0.5px}
        tbody tr:hover{background:#F8FAFC}
        .footer{margin-top:16px;font-size:10px;color:#94A3B8;border-top:1px solid #E2E8F0;padding-top:8px}
        @media print{body{margin:16px}}
      </style>
      </head><body>
      <div class="header">
        <h1>${rep.lab || "Nashman Institute"}</h1>
        <h2>Nashman Institute — Pathology &amp; Regenerative Medicine</h2>
        <h3>${rep.type} Report</h3>
      </div>
      <div class="meta">
        <span><b>Patient:</b> ${patient_info.name}</span>
        <span><b>Date:</b> ${formatDate(rep.date)}</span>
        <span><b>Age/Sex:</b> ${patient_info.age || "—"}Y / ${patient_info.sex || "—"}</span>
        <span><b>Lab ID:</b> ${rep.labId || "—"}</span>
        <span><b>EMR ID:</b> ${patient_info.id}</span>
        <span><b>Time:</b> ${rep.time || "—"}</span>
        ${patient_info.doctor ? `<span><b>Referred by:</b> ${patient_info.doctor}</span>` : ""}
      </div>
      <table>
        <thead><tr><th>Test</th><th style="text-align:right">Result</th><th>Unit</th><th>Reference Range</th><th>Status</th></tr></thead>
        <tbody>${rows}</tbody>
      </table>
      <div class="footer">
        ${rep.editedBy ? `<p>Edited by ${rep.editedBy} on ${rep.editedAt || "—"}</p>` : ""}
        <p>Generated by Nashman Institute EMR • ${new Date().toLocaleString()}</p>
      </div>
      </body></html>`;
    const blob = new Blob([html], { type: "text/html" });
    const blobUrl = URL.createObjectURL(blob);
    window.open(blobUrl, "_blank");
    setTimeout(() => URL.revokeObjectURL(blobUrl), 30000);
  };

  const handlePrint = () => {
    if (!activeReport) return;
    const patient_info = patient;
    const rep = activeReport;
    const rows = rep.values.map(v => `
      <tr style="border-bottom:1px solid #eee">
        <td style="padding:6px 10px;font-weight:500">${v.name}</td>
        <td style="padding:6px 10px;text-align:right;font-weight:700;color:${v.abnormal?"#DC2626":"#16A34A"}">${v.valueText || v.value}</td>
        <td style="padding:6px 10px;color:#64748B">${v.unit || ""}</td>
        <td style="padding:6px 10px;color:#94A3B8">${v.refMin != null ? v.refMin+"–"+v.refMax : "—"}</td>
        <td style="padding:6px 10px;font-size:11px;font-weight:600;color:${v.abnormal?"#DC2626":"#16A34A"}">${v.status || (v.abnormal?"Abnormal":"Normal")}</td>
      </tr>`).join("");
    const html = `<!DOCTYPE html><html><head><title>Lab Report</title>
      <style>body{font-family:Arial,sans-serif;margin:32px;color:#0F172A}
      h1{font-size:18px;margin:0}h2{font-size:13px;color:#64748B;margin:4px 0 0}
      .meta{display:grid;grid-template-columns:1fr 1fr;gap:4px 24px;font-size:12px;margin:12px 0;padding:10px;background:#F8FAFC;border-radius:6px}
      .meta span b{color:#0F172A}table{width:100%;border-collapse:collapse;margin-top:16px}
      thead tr{background:#F1F5F9}th{padding:8px 10px;text-align:left;font-size:11px;color:#64748B;font-weight:600;text-transform:uppercase}
      @media print{body{margin:16px}}</style>
      </head><body>
      <h1>${rep.lab || "Nashman Institute"}</h1>
      <h2>Nashman Institute — Pathology &amp; Regenerative Medicine</h2>
      <div class="meta">
        <span><b>Patient:</b> ${patient_info.name}</span>
        <span><b>Date:</b> ${formatDate(rep.date)}</span>
        <span><b>Age/Sex:</b> ${patient_info.age || "—"}Y / ${patient_info.sex || "—"}</span>
        <span><b>Lab ID:</b> ${rep.labId || "—"}</span>
        <span><b>EMR ID:</b> ${patient_info.id}</span>
        <span><b>Type:</b> ${rep.type}</span>
      </div>
      <table><thead><tr><th>Test</th><th style="text-align:right">Result</th><th>Unit</th><th>Reference</th><th>Status</th></tr></thead>
      <tbody>${rows}</tbody></table>
      ${rep.editedBy ? `<p style="font-size:10px;color:#94A3B8;margin-top:16px">Edited by ${rep.editedBy} on ${rep.editedAt || "—"}</p>` : ""}
      <p style="font-size:10px;color:#94A3B8">Printed on ${new Date().toLocaleString()}</p>
      </body></html>`;
    const w = window.open("", "_blank");
    w.document.write(html);
    w.document.close();
    w.focus();
    setTimeout(() => { w.print(); }, 300);
  };

  // ── Range bar ──────────────────────────────────────────────────────────────
  const RangeBar = () => (
    <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 14, flexWrap: "wrap" }}>
      {RANGES.map(r => (
        <button key={r} onClick={() => setRange(r)} style={{
          padding: "4px 12px", borderRadius: 99, fontSize: 12, cursor: "pointer", border: "1px solid",
          background: range === r ? meta.color : "#fff",
          borderColor: range === r ? meta.color : "#E4E8F0",
          color: range === r ? "#fff" : "#475569",
          fontWeight: range === r ? 700 : 400,
        }}>{r}</button>
      ))}
      {range === t.custom && (() => {
        const years = [];
        for (let y = 2020; y <= new Date().getFullYear() + 1; y++) years.push(y);
        const selectStyle = { padding: "4px 8px", borderRadius: 6, border: "1px solid #E4E8F0", fontSize: 12, background: "#fff", cursor: "pointer" };
        const inputStyle  = { padding: "4px 8px", borderRadius: 6, border: "1px solid #E4E8F0", fontSize: 12 };
        const isActive = useYearOnly ? (fromYear && toYear) : (dateFrom && dateTo);
        return <>
          {/* Toggle */}
          <div style={{ display:"flex", gap:2, background:"#F1F5F9", borderRadius:6, padding:2 }}>
            <button onClick={() => setYearOnly(true)} style={{ padding:"3px 8px", borderRadius:4, fontSize:11, fontWeight:600, border:"none", cursor:"pointer", background: useYearOnly ? "#fff" : "transparent", color: useYearOnly ? "#0F172A" : "#94A3B8", boxShadow: useYearOnly ? "0 1px 3px rgba(0,0,0,0.1)" : "none" }}>Year</button>
            <button onClick={() => setYearOnly(false)} style={{ padding:"3px 8px", borderRadius:4, fontSize:11, fontWeight:600, border:"none", cursor:"pointer", background: !useYearOnly ? "#fff" : "transparent", color: !useYearOnly ? "#0F172A" : "#94A3B8", boxShadow: !useYearOnly ? "0 1px 3px rgba(0,0,0,0.1)" : "none" }}>Date</button>
          </div>
          {useYearOnly ? <>
            <select value={fromYear} onChange={e => { setFromYear(e.target.value); setRange(t.custom); }} style={selectStyle}>
              <option value="">From year</option>
              {years.map(y => <option key={y} value={y}>{y}</option>)}
            </select>
            <span style={{ fontSize:12, color:"#94A3B8" }}>→</span>
            <select value={toYear} onChange={e => { setToYear(e.target.value); setRange(t.custom); }} style={selectStyle}>
              <option value="">To year</option>
              {years.map(y => <option key={y} value={y}>{y}</option>)}
            </select>
          </> : <>
            <input type="date" value={dateFrom} onChange={e => { setFrom(e.target.value); setRange(t.custom); }} style={inputStyle} />
            <span style={{ fontSize:12, color:"#94A3B8" }}>→</span>
            <input type="date" value={dateTo} onChange={e => { setTo(e.target.value); setRange(t.custom); }} style={inputStyle} />
          </>}
          {isActive
            ? <span style={{ fontSize:11, color:"#16A34A", fontWeight:600 }}>✓ Active</span>
            : <span style={{ fontSize:11, color:"#F59E0B" }}>Select range</span>}
        </>;
      })()}
      <span style={{ marginLeft: "auto", fontSize: 12, color: "#94A3B8" }}>
        {filtered.length} report{filtered.length !== 1 ? "s" : ""}
      </span>
    </div>
  );

  if (isLoadingFull) return (
    <div style={{ padding: "24px 0" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
        <div>
          <div style={{ width: 200, height: 22, background: "#E4E8F0", borderRadius: 6, marginBottom: 8 }} />
          <div style={{ width: 300, height: 14, background: "#F1F5F9", borderRadius: 6 }} />
        </div>
        <button className="btn btn-secondary" onClick={onBack}>← Back</button>
      </div>
      <div style={{ display: "flex", gap: 12, marginBottom: 20 }}>
        {[1,2,3].map(i => <div key={i} style={{ flex: 1, height: 80, background: "#F1F5F9", borderRadius: 10 }} />)}
      </div>
      <div style={{ height: 200, background: "#F8FAFC", borderRadius: 12, display: "flex", alignItems: "center", justifyContent: "center", color: "#94A3B8", fontSize: 13 }}>
        Loading patient data...
      </div>
    </div>
  );

  return (
    <div>
      {/* Patient header */}
      <div style={{ marginBottom: 20 }}>
        <button className="back-btn" onClick={() => {
          ['view','type','rep','param'].forEach(k => removeStore(k));
          sessionStorage.removeItem('emr_selected_patient');
          onBack();
        }}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
            <polyline points="15 18 9 12 15 6"/>
          </svg>
          {context === "lab" ? "← Back to Lab Reports" : "Back to Patients"}
        </button>
      </div>

      {/* Patient info card */}
      <div className="card" style={{ marginBottom: 16 }}>
        <div className="card-header">
          <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
            <div style={{
              width: 48, height: 48, borderRadius: 12,
              background: "#EEF2FF", color: "#4F46E5",
              display: "flex", alignItems: "center", justifyContent: "center",
              fontWeight: 800, fontSize: 18, flexShrink: 0
            }}>{patient.name?.charAt(0)}</div>
            <div>
              <div style={{ fontWeight: 800, fontSize: 17 }}>{patient.name}</div>
              <div style={{ fontSize: 13, color: "#64748B" }}>
                {patient.id} ·&nbsp;
                {patient.age ? `${patient.age}y` : "—"} ·&nbsp;
                {patient.sex || "—"} ·&nbsp;
                Blood: {patient.blood || "—"}
              </div>
              {patient.doctor && (
                <div style={{ fontSize: 12, color: "#94A3B8", marginTop: 2 }}>
                  Physician: {patient.doctor}
                </div>
              )}
            </div>
          </div>
          <div style={{ display: "flex", gap: 7, flexWrap: "wrap" }}>
            {canUpload && (
              <button className="btn btn-primary btn-sm" onClick={() => setUpload(true)}>
                {t.uploadReport}
              </button>
            )}
            <button className="btn btn-secondary btn-sm" onClick={() => exportToExcel(patient)}>
              {t.exportExcel}
            </button>
            {patient.phone && (
              <div style={{ fontSize: 12, color: "#64748B", display: "flex", alignItems: "center" }}>
                {patient.phone}
              </div>
            )}
          </div>
        </div>
      </div>

            {/* ── LAB REPORTS TAB ──────────────────────────────────────────────────── */}
      {(view === "list" || view === "report" || view === "trend") && (
        <div style={{ display:"flex", alignItems:"center", gap:6, marginBottom:16, flexWrap:"wrap" }}>
          <div ref={categoryDDRef} style={{ position:"relative" }}>
            <button className="btn btn-ghost btn-sm" style={{ display:"flex", alignItems:"center", gap:4 }} onClick={() => setShowCategoryDD(v => !v)}>
              {t.allCategories}<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" width="11" height="11"><polyline points="6 9 12 15 18 9"/></svg>
            </button>
            {showCategoryDD && (
              <div style={{ position:"absolute", top:"calc(100% + 4px)", left:0, background:"#fff", border:"1px solid #E2E8F0", borderRadius:8, boxShadow:"0 4px 16px rgba(0,0,0,0.1)", zIndex:100, minWidth:180, overflow:"hidden" }}>
                <button onClick={() => { setShowCategoryDD(false); setView("lab-categories"); setStore("view", "lab-categories"); setType(null); removeStore("type"); setRep(null); removeStore("rep"); restoredRef.current=false; setCategory(null); removeStore("category"); }}
                  style={{ display:"flex", alignItems:"center", gap:8, width:"100%", padding:"9px 14px", background:"none", border:"none", borderBottom:"1px solid #F1F5F9", cursor:"pointer", fontSize:13, color:"#4F46E5", fontWeight:600, textAlign:"left" }}
                  onMouseEnter={e=>e.currentTarget.style.background="#F8FAFC"} onMouseLeave={e=>e.currentTarget.style.background="none"}>All Categories</button>
                {Object.keys(byCategory).map(cat => {
                  const m = TYPE_META[cat]||TYPE_META.Other;
                  const count = Object.keys(byCategory[cat]?.subtypes || {}).length;
                  return (<button key={cat} onClick={() => {
                      setShowCategoryDD(false);
                      setCategory(cat); setStore("category", cat);
                      setView("lab-categories"); setStore("view","lab-categories");
                      setType(null); removeStore("type"); setRep(null); removeStore("rep"); restoredRef.current=false;
                    }}
                    style={{ display:"flex", alignItems:"center", gap:8, width:"100%", padding:"9px 14px", background:"none", border:"none", cursor:"pointer", fontSize:13, color:"#0F172A", textAlign:"left", justifyContent:"space-between" }}
                    onMouseEnter={e=>e.currentTarget.style.background="#F8FAFC"} onMouseLeave={e=>e.currentTarget.style.background="none"}>
                    <div style={{ display:"flex", alignItems:"center", gap:8 }}>
                      <span style={{ width:8, height:8, borderRadius:"50%", background:m.color, flexShrink:0 }}/>{cat}
                    </div>
                    <span style={{ fontSize:11, color:"#94A3B8" }}>{count}</span>
                  </button>);
                })}
              </div>
            )}
          </div>
          {selType && (<><span style={{ color:"#CBD5E1" }}>›</span><button className="btn btn-ghost btn-sm" style={{ color:view==="list"?meta.color:"#64748B", fontWeight:view==="list"?700:400 }} onClick={() => { setView("list"); setStore("view", "list"); if(!initialRepId) setRep(null); removeStore("rep"); restoredRef.current=false; }}>{selType}</button></>)}
          {activeReport && (view==="report"||view==="trend") && (<><span style={{ color:"#CBD5E1" }}>›</span><span style={{ fontSize:13, color:"#64748B" }}>{view==="trend"?t.trendAnalysis:formatDate(activeReport.date)}</span></>)}
          <div style={{ marginLeft:"auto", display:"flex", alignItems:"center", gap:8 }}>
            {allergies.length>0&&(
              <button className="btn btn-ghost btn-sm" style={{ color:"#DC2626", border:"1px solid #FECACA", background:"#FEF2F2", display:"flex", alignItems:"center", gap:5, borderRadius:99 }}
                onClick={() => { setView("medications"); setStore("view", "medications"); }}>
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#DC2626" strokeWidth="2"><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/></svg>
                Allergies ({allergies.length})
              </button>
            )}
            <button className="btn btn-ghost btn-sm" style={{ color:"#059669", border:"1px solid #BBF7D0", background:"#F0FDF4", display:"flex", alignItems:"center", gap:5, borderRadius:99 }}
              onClick={() => { setView("medications"); setStore("view", "medications"); }}>
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#059669" strokeWidth="2"><path d="M9 3H5a2 2 0 0 0-2 2v4m6-6h10a2 2 0 0 1 2 2v4M9 3v18m0 0h10a2 2 0 0 0 2-2v-4M9 21H5a2 2 0 0 1-2-2v-4m0 0h18"/></svg>
              Medications
              {medications.filter(m=>!m.stopped_at).length>0&&<span style={{ background:"#DCFCE7",color:"#166534",fontSize:10,fontWeight:700,padding:"1px 6px",borderRadius:99 }}>{medications.filter(m=>!m.stopped_at).length}</span>}
            </button>
            <button style={{ display:"inline-flex", alignItems:"center", gap:5, borderRadius:99, padding:"5px 13px", fontSize:12, fontWeight:600, cursor:"pointer", border:"1.5px solid #FECACA", background:"#FEF2F2", color:"#DC2626", whiteSpace:"nowrap" }}
              onClick={() => { setView("vitals"); setStore("view", "vitals"); }}>
              <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M22 12h-4l-3 9L9 3l-3 9H2"/></svg>
              Vitals
            </button>
            <button style={{ display:"inline-flex", alignItems:"center", gap:5, borderRadius:99, padding:"5px 13px", fontSize:12, fontWeight:600, cursor:"pointer", border:"1.5px solid #DDD6FE", background:"#F5F3FF", color:"#7C3AED", whiteSpace:"nowrap" }}
              onClick={() => { setView("history"); setStore("view", "history"); }}>
              <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
              Patient History
            </button>
            {!imagingLoading && patientImaging.length > 0 && (
              <button style={{ display:"inline-flex", alignItems:"center", gap:5, borderRadius:99, padding:"5px 13px", fontSize:12, fontWeight:600, cursor:"pointer", border:"1.5px solid #BAE6FD", background:"#F0F9FF", color:"#0284C7", whiteSpace:"nowrap" }}
                onClick={navigateToImaging}>
                <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/></svg>
                Imaging {patientImaging.length > 0 && <span style={{ background:"#E0F2FE", color:"#0369A1", borderRadius:99, padding:"1px 6px", fontSize:10, fontWeight:700 }}>{patientImaging.length}</span>}
              </button>
            )}
            <button className="btn btn-ghost btn-sm" onClick={() => {
              if (view==="trend") { setView("report"); setStore("view", "report"); }
              else if (view==="report") { setView("list"); setStore("view", "list"); if(!initialRepId) setRep(null); removeStore("rep"); restoredRef.current=false; setEditMode(false); }
              else { setView("categories"); setStore("view", "categories"); setType(null); removeStore("type"); }
            }}>← Back</button>
          </div>
        </div>
      )}
      {(view==="medications"||view==="vitals"||view==="history") && (
        <div style={{ display:"flex", gap:0, marginBottom:20, background:"#fff", borderRadius:12, border:"1.5px solid #E2E8F0", overflow:"hidden", width:"fit-content" }}>
          {[
            { key:"medications", label:"Medications & Allergies", color:"#059669", bg:"#F0FDF4", border:"#059669", icon:<svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M9 3H5a2 2 0 0 0-2 2v4m6-6h10a2 2 0 0 1 2 2v4M9 3v18m0 0h10a2 2 0 0 0 2-2v-4M9 21H5a2 2 0 0 1-2-2v-4m0 0h18"/></svg>, badge: medications.filter(m=>!m.stopped_at).length||null, badgeBg:"#DCFCE7", badgeColor:"#166534" },
            { key:"vitals",      label:"Vitals Flowsheet",        color:"#EF4444", bg:"#FEF2F2", border:"#EF4444", icon:<svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M22 12h-4l-3 9L9 3l-3 9H2"/></svg> },
            { key:"history",     label:"Patient History",          color:"#7C3AED", bg:"#F5F3FF", border:"#7C3AED", icon:<svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg> },
            ...(!imagingLoading && patientImaging.length > 0 ? [{ key:"imaging", label:"Imaging", color:"#0EA5E9", bg:"#F0F9FF", border:"#0EA5E9", icon:<svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/></svg>, badge: patientImaging.length, badgeBg:"#E0F2FE", badgeColor:"#0369A1" }] : []),
          ].map((tab,idx,arr) => {
            const isActive = view === tab.key;
            return (
              <button key={tab.key}
                onClick={() => {
                  if (tab.key === "imaging") { navigateToImaging(); return; }
                  setView(tab.key); setStore("view", tab.key);
                }}
                style={{ display:"flex", alignItems:"center", gap:7, padding:"10px 20px", fontSize:13, fontWeight:isActive?700:500, border:"none", borderRight: idx<arr.length-1?"1px solid #E2E8F0":"none", borderBottom: isActive?`2px solid ${tab.border}`:"2px solid transparent", cursor:"pointer", whiteSpace:"nowrap", background: isActive?tab.bg:"#fff", color: isActive?tab.color:"#64748B", transition:"all .15s" }}>
                {tab.icon}
                {tab.label}
                {tab.badge && <span style={{ fontSize:10, fontWeight:700, padding:"1px 6px", borderRadius:99, background: isActive?tab.badgeBg:"#F1F5F9", color: isActive?tab.badgeColor:"#64748B" }}>{tab.badge}</span>}
              </button>
            );
          })}
          <button
            onClick={() => { setView("categories"); setStore("view", "categories"); }}
            style={{ display:"flex", alignItems:"center", padding:"10px 16px", fontSize:13, fontWeight:500, border:"none", borderLeft:"1px solid #E2E8F0", cursor:"pointer", background:"#fff", color:"#64748B", whiteSpace:"nowrap" }}>
            ← Categories
          </button>
        </div>
      )}

            {view === "categories" && (
        <div>
          <div style={{ display:"grid", gridTemplateColumns:"repeat(3,1fr)", gap:14, marginBottom:20 }}>
            <div className="card" style={{ padding:"20px", cursor:"pointer", borderLeft:"3px solid #4F46E5", transition:"box-shadow 0.15s" }}
              onClick={() => { setView("lab-categories"); setStore("view", "lab-categories"); if(!initialRepId) if(!fullPatient?.reports?.length) loadPatientData(1); }}
              onMouseEnter={e=>e.currentTarget.style.boxShadow="0 4px 20px rgba(0,0,0,.08)"} onMouseLeave={e=>e.currentTarget.style.boxShadow=""}>
              <div style={{ display:"flex", alignItems:"flex-start", justifyContent:"space-between", marginBottom:10 }}>
                <div><div style={{ fontWeight:700, fontSize:15 }}>Lab reports</div><div style={{ fontSize:12, color:"#64748B", marginTop:2 }}>{isLoadingFull ? <span style={{ color:"#CBD5E1" }}>Loading…</span> : `${Object.keys(byType).length} categories · ${fullPatient?.total_reports||patient?.reports?.length||0} reports`}</div></div>
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#4F46E5" strokeWidth="1.8"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="12" y1="11" x2="12" y2="17"/><line x1="9" y1="14" x2="15" y2="14"/></svg>
              </div>
              <div style={{ display:"flex", gap:6, flexWrap:"wrap", marginBottom:8 }}>
                {Object.keys(byType).slice(0,3).map(type=>{const m=TYPE_META[type]||TYPE_META.Other;return <span key={type} style={{ fontSize:11,padding:"2px 8px",borderRadius:99,fontWeight:500,background:m.color+"18",color:m.color }}>{type}</span>;})}
                {Object.keys(byType).length>3&&<span style={{ fontSize:11,color:"#94A3B8" }}>+{Object.keys(byType).length-3} more</span>}
              </div>
              <div style={{ fontSize:11, color:"#94A3B8" }}>Click to view all categories</div>
            </div>
            {(()=>{const am=medications.filter(m=>!m.stopped_at);const sa=allergies.filter(a=>a.severity==="Severe");return(
              <div className="card" style={{ padding:"20px",cursor:"pointer",borderLeft:"3px solid #059669",transition:"box-shadow 0.15s" }}
                onClick={()=>{setView("medications");setStore("view", "medications");}}
                onMouseEnter={e=>e.currentTarget.style.boxShadow="0 4px 20px rgba(0,0,0,.08)"} onMouseLeave={e=>e.currentTarget.style.boxShadow=""}>
                <div style={{ display:"flex",alignItems:"flex-start",justifyContent:"space-between",marginBottom:10 }}>
                  <div><div style={{ fontWeight:700,fontSize:15 }}>Medications & allergies</div><div style={{ fontSize:12,color:"#64748B",marginTop:2 }}>{medsLoading||allergiesLoading ? <span style={{ color:"#CBD5E1" }}>Loading…</span> : `${am.length} active · ${allergies.length} allerg${allergies.length===1?"y":"ies"}`}</div></div>
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#059669" strokeWidth="1.8"><path d="M9 3H5a2 2 0 0 0-2 2v4m6-6h10a2 2 0 0 1 2 2v4M9 3v18m0 0h10a2 2 0 0 0 2-2v-4M9 21H5a2 2 0 0 1-2-2v-4m0 0h18"/></svg>
                </div>
                <div style={{ display:"flex",gap:6,flexWrap:"wrap",marginBottom:8 }}>
                  {am.slice(0,2).map(m=><span key={m.id} style={{ fontSize:11,background:"#DCFCE7",color:"#166534",padding:"2px 8px",borderRadius:99,fontWeight:500 }}>{m.drug_name}</span>)}
                  {sa.slice(0,1).map(a=><span key={a.id} style={{ fontSize:11,background:"#FEE2E2",color:"#991B1B",padding:"2px 8px",borderRadius:99,fontWeight:500 }}>⚠ {a.allergen}</span>)}
                  {!medsLoading&&!allergiesLoading&&am.length===0&&allergies.length===0&&<span style={{ fontSize:11,color:"#94A3B8" }}>No records yet</span>}
                </div>
                <div style={{ fontSize:11,color:"#94A3B8" }}>{medsLoading||allergiesLoading?"Loading…":"Click to manage medications"}</div>
              </div>
            );})()}
            {(imagingLoading || patientImaging.length > 0) && (
              <div className="card" style={{ padding:"20px",cursor:"pointer",borderLeft:"3px solid #0EA5E9",transition:"box-shadow 0.15s" }}
                onClick={navigateToImaging}
                onMouseEnter={e=>e.currentTarget.style.boxShadow="0 4px 20px rgba(0,0,0,.08)"} onMouseLeave={e=>e.currentTarget.style.boxShadow=""}>
                <div style={{ display:"flex",alignItems:"flex-start",justifyContent:"space-between",marginBottom:10 }}>
                  <div>
                    <div style={{ fontWeight:700,fontSize:15 }}>Imaging</div>
                    <div style={{ fontSize:12,color:"#64748B",marginTop:2 }}>{imagingLoading ? <span style={{ color:"#CBD5E1" }}>Loading…</span> : `${patientImaging.length} study${patientImaging.length!==1?"s":""}`}</div>
                  </div>
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#0EA5E9" strokeWidth="1.8"><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/></svg>
                </div>
                <div style={{ display:"flex",alignItems:"center",marginBottom:8 }}>
                  {imagingLoading
                    ? <div style={{ height:9, width:60, background:"#F1F5F9", borderRadius:99 }}/>
                    : patientImaging.slice(0,6).map((img,i)=>(<div key={img.id} style={{ display:"flex",alignItems:"center" }}><div style={{ width:9,height:9,borderRadius:"50%",background:"#0EA5E9" }}/>{i<Math.min(patientImaging.length,6)-1&&<div style={{ width:20,height:1,background:"#0EA5E930" }}/>}</div>))}
                </div>
                <div style={{ fontSize:11,color:"#94A3B8" }}>{imagingLoading ? "" : `Last: ${patientImaging[0]?.date||"—"} · DICOM`}</div>
              </div>
            )}

            {/* Vitals card */}
            {(()=>{
              const latestVital = vitals[0]||null;
              return (
                <div className="card" style={{ padding:"20px",cursor:"pointer",borderLeft:"3px solid #EF4444",transition:"box-shadow 0.15s" }}
                  onClick={()=>{setView("vitals");setStore("view", "vitals");}}
                  onMouseEnter={e=>e.currentTarget.style.boxShadow="0 4px 20px rgba(0,0,0,.08)"} onMouseLeave={e=>e.currentTarget.style.boxShadow=""}>
                  <div style={{ display:"flex",alignItems:"flex-start",justifyContent:"space-between",marginBottom:10 }}>
                    <div>
                      <div style={{ fontWeight:700,fontSize:15 }}>Vitals</div>
                      <div style={{ fontSize:12,color:"#64748B",marginTop:2 }}>{vitalsLoading?"Loading…":latestVital?`Last: ${formatDate(latestVital.date)}`:"No records yet"}</div>
                    </div>
                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#EF4444" strokeWidth="1.8"><path d="M22 12h-4l-3 9L9 3l-3 9H2"/></svg>
                  </div>
                  {latestVital && (
                    <div style={{ display:"flex",gap:8,flexWrap:"wrap",marginBottom:8 }}>
                      {latestVital.blood_pressure&&<span style={{ fontSize:11,background:"#FEE2E2",color:"#991B1B",padding:"2px 8px",borderRadius:99,fontWeight:600 }}>BP {latestVital.blood_pressure}</span>}
                      {latestVital.heart_rate&&<span style={{ fontSize:11,background:"#FFF1F2",color:"#BE123C",padding:"2px 8px",borderRadius:99,fontWeight:600 }}>{latestVital.heart_rate} bpm</span>}
                      {latestVital.spo2&&<span style={{ fontSize:11,background:"#F0FDF4",color:"#166534",padding:"2px 8px",borderRadius:99,fontWeight:600 }}>SpO2 {latestVital.spo2}%</span>}
                    </div>
                  )}
                  {!latestVital&&!vitalsLoading&&<span style={{ fontSize:11,color:"#94A3B8" }}>No vitals recorded yet</span>}
                  <div style={{ fontSize:11,color:"#94A3B8" }}>Click to view vitals</div>
                </div>
              );
            })()}

            {/* Clinical History card */}
            {(()=>{
              const chronic = history.filter(h=>h.type==="Chronic Condition");
              const surgical = history.filter(h=>h.type==="Surgical");
              return (
                <div className="card" style={{ padding:"20px",cursor:"pointer",borderLeft:"3px solid #7C3AED",transition:"box-shadow 0.15s" }}
                  onClick={()=>{setView("history");setStore("view", "history");}}
                  onMouseEnter={e=>e.currentTarget.style.boxShadow="0 4px 20px rgba(0,0,0,.08)"} onMouseLeave={e=>e.currentTarget.style.boxShadow=""}>
                  <div style={{ display:"flex",alignItems:"flex-start",justifyContent:"space-between",marginBottom:10 }}>
                    <div>
                      <div style={{ fontWeight:700,fontSize:15 }}>Clinical history</div>
                      <div style={{ fontSize:12,color:"#64748B",marginTop:2 }}>{historyLoading?"Loading…":`${chronic.length} chronic · ${surgical.length} surgical`}</div>
                    </div>
                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#7C3AED" strokeWidth="1.8"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/><polyline points="10 9 9 9 8 9"/></svg>
                  </div>
                  <div style={{ display:"flex",gap:6,flexWrap:"wrap",marginBottom:8 }}>
                    {chronic.slice(0,3).map(h=><span key={h.id} style={{ fontSize:11,background:"#EEF2FF",color:"#4338CA",padding:"2px 8px",borderRadius:99,fontWeight:500 }}>{h.narrative}</span>)}
                    {chronic.length>3&&<span style={{ fontSize:11,color:"#94A3B8" }}>+{chronic.length-3} more</span>}
                    {chronic.length===0&&!historyLoading&&<span style={{ fontSize:11,color:"#94A3B8" }}>No conditions recorded</span>}
                  </div>
                  <div style={{ fontSize:11,color:"#94A3B8" }}>Click to view history</div>
                </div>
              );
            })()}

          </div>

        </div>
      )}

            {/* ── LAB CATEGORIES ───────────────────────────────────────────────── */}
      {view === "lab-categories" && (
        <div>
          <div style={{ marginBottom:16 }}>
            <button className="btn btn-ghost btn-sm" onClick={() => { setView("categories"); setStore("view", "categories"); setCategory(null); removeStore("category"); }}>← Categories</button>
          </div>
          {Object.keys(byCategory).length===0?(
            <div className="card"><div className="empty-state">{!fullPatient?<p style={{ color:"#94A3B8",fontSize:13 }}>Loading...</p>:<><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg><h3>No reports uploaded yet</h3><p>{canUpload?t.uploadToAdd:t.noReports}</p></>}</div></div>
          ) : !selCategory ? (
            /* ── CATEGORY CARDS ── */
            <div style={{ display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(240px,1fr))",gap:14 }}>
              {Object.entries(byCategory).map(([cat, catData]) => {
                const m = TYPE_META[cat] || TYPE_META.Other;
                const subtypeCount = Object.keys(catData.subtypes).length;
                const subtypeNames = Object.keys(catData.subtypes);
                return (
                  <div key={cat} className="card" style={{ padding:"20px",cursor:"pointer",borderLeft:`3px solid ${m.color}`,transition:"box-shadow 0.15s",marginBottom:0 }}
                    onClick={() => {
                      // If only 1 sub-type, go directly to list — no sub-type card needed
                      if (subtypeCount === 1) {
                        const type = subtypeNames[0];
                        setTypeAndReset(type); setStore("type", type);
                        setView("list"); setStore("view", "list");
                        setRange("All"); setPinned([]); setTP(null); removeStore("param");
                      } else {
                        setCategory(cat); setStore("category", cat);
                      }
                    }}
                    onMouseEnter={e=>e.currentTarget.style.boxShadow="0 4px 20px rgba(0,0,0,.1)"}
                    onMouseLeave={e=>e.currentTarget.style.boxShadow=""}>
                    <div style={{ display:"flex",alignItems:"flex-start",justifyContent:"space-between",marginBottom:6 }}>
                      <div style={{ fontWeight:700,fontSize:15 }}>{cat}</div>
                      {subtypeCount > 1 && (
                        <span style={{ fontSize:10,background:m.color+"18",color:m.color,padding:"2px 7px",borderRadius:99,fontWeight:600,whiteSpace:"nowrap" }}>{subtypeCount} types</span>
                      )}
                    </div>
                    <div style={{ fontSize:12,color:"#64748B",marginBottom:8 }}>{catData.totalCount} report{catData.totalCount!==1?"s":""}</div>
                    <div style={{ display:"flex",gap:4,flexWrap:"wrap",marginBottom:8 }}>
                      {subtypeNames.slice(0,3).map(sub => {
                        const shortName = sub.split("/")[0].split("(")[0].trim();
                        return <span key={sub} style={{ fontSize:10,background:m.color+"15",color:m.color,padding:"1px 6px",borderRadius:99,fontWeight:500 }}>{shortName}</span>;
                      })}
                      {subtypeNames.length > 3 && <span style={{ fontSize:10,color:"#94A3B8" }}>+{subtypeNames.length-3}</span>}
                    </div>
                    <div style={{ fontSize:11,color:"#94A3B8" }}>
                      {catData.lastDate ? `Last: ${catData.lastDate}` : ""}
                      {subtypeCount > 1 ? " · Click to see sub-types" : " · Click to view reports"}
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            /* ── SUB-TYPE CARDS (when category is selected) ── */
            <div>
              <div style={{ marginBottom:14,display:"flex",alignItems:"center",gap:10 }}>
                <button className="btn btn-ghost btn-sm" onClick={() => { setCategory(null); removeStore("category"); }}>← {selCategory}</button>
              </div>
              <div style={{ display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(220px,1fr))",gap:12 }}>
                {Object.entries(byCategory[selCategory]?.subtypes || {}).map(([type, reps]) => {
                  const m = TYPE_META[selCategory] || TYPE_META.Other;
                  const count = reps._count || (Array.isArray(reps) ? reps.length : 0);
                  const validReps = Array.isArray(reps) ? reps.filter(r=>r&&r.date) : [];
                  const sorted = [...validReps].sort((a,b)=>new Date(a.date)-new Date(b.date));
                  const lastRep = sorted[sorted.length-1];
                  const abnCount = validReps.reduce((n,r)=>n+(r.values||[]).filter(v=>v.abnormal).length, 0);
                  return (
                    <div key={type} className="card" style={{ padding:"18px",cursor:"pointer",borderLeft:`3px solid ${m.color}`,transition:"box-shadow 0.15s",marginBottom:0 }}
                      onClick={() => {
                        setTypeAndReset(type); setStore("type", type);
                        setView("list"); setStore("view", "list");
                        setRange("All"); setPinned([]); setTP(null); removeStore("param");
                      }}
                      onMouseEnter={e=>e.currentTarget.style.boxShadow="0 4px 20px rgba(0,0,0,.1)"}
                      onMouseLeave={e=>e.currentTarget.style.boxShadow=""}>
                      <div style={{ fontWeight:700,fontSize:14,marginBottom:4,color:"#0F172A" }}>{type}</div>
                      <div style={{ fontSize:12,color:"#64748B",marginBottom:8 }}>
                        {count} report{count!==1?"s":""}
                        {abnCount > 0 && <span style={{ marginLeft:8,fontSize:11,color:"#DC2626",fontWeight:600 }}>· {abnCount} abnormal</span>}
                      </div>
                      {sorted.length > 0 && (
                        <div style={{ display:"flex",alignItems:"center",gap:3,marginBottom:8 }}>
                          {sorted.slice(0,6).map((r,i)=>(
                            <div key={r?.id||i} style={{ display:"flex",alignItems:"center" }}>
                              <div style={{ width:8,height:8,borderRadius:"50%",background:m.color }}/>
                              {i<Math.min(sorted.length,6)-1&&<div style={{ width:16,height:1,background:m.color+"30" }}/>}
                            </div>
                          ))}
                          {sorted.length > 6 && <span style={{ fontSize:10,color:"#94A3B8",marginLeft:4 }}>+{sorted.length-6}</span>}
                        </div>
                      )}
                      <div style={{ fontSize:11,color:"#94A3B8" }}>{lastRep?`Last: ${lastRep.date}`:"No reports"}</div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}

            {/* ── REPORT LIST ─────────────────────────────────────────────────────── */}
      {view === "list" && selType && (
        <div>
          <RangeBar />
          {(fetchingTrend || trendType !== selType) ? (
            <div className="card">
              <div className="empty-state">
                <div style={{ width:32, height:32, borderRadius:"50%", border:"3px solid #E2E8F0", borderTopColor: meta.color || "#4F46E5", animation:"spin 0.8s linear infinite", margin:"0 auto 12px" }} />
                <p style={{ color:"#94A3B8", marginTop:8 }}>Loading reports...</p>
              </div>
            </div>
          ) : filtered.length === 0 ? (
            <div className="card"><div className="empty-state"><p>No reports in this date range.</p></div></div>
          ) : (
            <>
              <div className="card" style={{ marginBottom: 14 }}>
                {([...filtered]
                  .sort((a,b) => new Date(b.createdAt||b.date||0) - new Date(a.createdAt||a.date||0))
                  // Slice for current page when we have all data (trendAllReports set)
                  .slice(
                    trendAllReports ? (reportPage-1)*REPORT_PAGE_SIZE : 0,
                    trendAllReports ? reportPage*REPORT_PAGE_SIZE : undefined
                  )
                ).map((rep, idx) => {
                  const abnCount  = (rep.values || []).filter(v => v.abnormal).length;
                  const critCount = (rep.values || []).filter(v => v.status === "Critical High" || v.status === "Critical Low").length;
                  const isPinned = pinned.includes(rep.id);
                  return (
                    <div key={rep.id} className="patient-row" style={{ padding: "14px 20px", gap: 14 }}>
                      <div style={{
                        width: 32, height: 32, borderRadius: 8,
                        background: meta.color + "15", color: meta.color,
                        display: "flex", alignItems: "center", justifyContent: "center",
                        fontSize: 13, fontWeight: 800, flexShrink: 0
                      }}>
                        {filtered.length - idx}
                      </div>
                      <div style={{ flex: 1, minWidth: 0, cursor: "pointer" }}
                        onClick={() => handleViewReport(rep)}>
                        <div style={{ display: "flex", alignItems: "center", gap: 7, marginBottom: 2, flexWrap: "wrap" }}>
                          <span style={{ fontWeight: 700, fontSize: 14 }}>{formatDate(rep.date)}</span>
                          {rep.time && rep.time !== "10:00 AM" && <span style={{ fontSize: 12, color: "#64748B", background: "#F1F5F9", padding: "1px 6px", borderRadius: 4 }}>{rep.time}</span>}
                          {idx === 0 && (
                            <span className="badge" style={{ background: meta.color + "15", color: meta.color, fontSize: 10 }}>LATEST</span>
                          )}
                          {rep.editedBy && <span className="badge badge-amber" style={{ fontSize: 10 }}>Edited</span>}
                          {rep.uploadedBy && <span style={{ fontSize: 10, color: "#64748B" }}>by {rep.uploadedBy}</span>}

                        </div>
                        <div style={{ fontSize: 12, color: "#64748B" }}>
                          {rep.lab}{rep.labId ? ` · Lab ID: ${rep.labId}` : ""}
                        </div>
                      </div>
                      <div style={{ flexShrink: 0 }}>
                        {critCount > 0
                          ? <span style={{ fontSize: 11, fontWeight: 700, color: "#fff", background: "#DC2626", padding: "1px 7px", borderRadius: 4 }}>⚠ Critical</span>
                          : abnCount > 0
                          ? <span style={{ fontSize: 11, color: "var(--red)", fontWeight: 600 }}>Abnormal</span>
                          : <span style={{ fontSize: 11, color: "var(--green)", fontWeight: 600 }}>Normal</span>
                        }
                      </div>
                      <div style={{ display: "flex", gap: 6 }}>
                        <button onClick={() => setPinned(p =>
                          p.includes(rep.id) ? p.filter(x => x !== rep.id) : [...p, rep.id]
                        )} className="btn btn-ghost btn-sm" style={{ color: isPinned ? meta.color : undefined }}>
                          {isPinned ? "Pinned ●" : t.pinBtn}
                        </button>
                        <button className="btn btn-secondary btn-sm"
                          onClick={() => handleViewReport(rep)}>
                          {loadingReport ? "Loading..." : t.viewBtn}
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Report Pagination — based on filtered (type-specific) count */}
              {(() => {
                // Use trendAllReports length when available (most accurate for this type)
                // Fall back to filtered.length — may undercount if not all pages loaded
                const totalTypeReports = typeReports.length;
                const totalTypePages = Math.ceil(totalTypeReports / REPORT_PAGE_SIZE);
                if (totalTypePages <= 1) return null;
                return (
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "12px 20px", background: "#F8F9FC", borderRadius: "0 0 12px 12px", borderTop: "1px solid #E4E8F0" }}>
                    <div style={{ fontSize: 12, color: "#64748B" }}>
                      Showing {((reportPage-1)*REPORT_PAGE_SIZE)+1}–{Math.min(reportPage*REPORT_PAGE_SIZE, totalTypeReports)} of {totalTypeReports} reports
                    </div>
                    <div style={{ display: "flex", gap: 4 }}>
                      <button className="btn btn-ghost btn-sm" onClick={() => handleReportPageChange(reportPage-1)} disabled={reportPage<=1}>← Prev</button>
                      {Array.from({length: totalTypePages}, (_,i)=>i+1)
                        .filter(p => p===1 || p===totalTypePages || Math.abs(p-reportPage)<=1)
                        .reduce((acc,p,idx,arr) => { if(idx>0 && p-arr[idx-1]>1) acc.push("..."); acc.push(p); return acc; }, [])
                        .map((p,i) => p==="..." ? (
                          <span key={i} style={{padding:"4px 8px",fontSize:13,color:"#94A3B8"}}>...</span>
                        ) : (
                          <button key={i} className={`btn btn-sm ${p===reportPage?"btn-primary":"btn-ghost"}`} onClick={()=>handleReportPageChange(p)}>{p}</button>
                        ))
                      }
                      <button className="btn btn-ghost btn-sm" onClick={() => handleReportPageChange(reportPage+1)} disabled={reportPage>=totalTypePages}>Next →</button>
                    </div>
                  </div>
                );
              })()}

              {pinned.length >= 2 && (
                <div className="card" style={{ borderTop: `2px solid ${meta.color}` }}>
                  <div className="card-header">
                    <div style={{ fontWeight: 700, fontSize: 14 }}>Pinned Comparison ({pinned.length} reports)</div>
                    <button className="btn btn-ghost btn-sm" onClick={() => setPinned([])}>Clear</button>
                  </div>
                  <div className="card-body" style={{ overflowX: "auto" }}>
                    <div style={{ display: "grid", gridTemplateColumns: `repeat(${pinned.length}, 1fr)`, gap: 12, minWidth: 400 }}>
                      {filtered.filter(r => pinned.includes(r.id)).map(rep => (
                        <div key={rep.id} style={{ background: "#F8F9FC", borderRadius: 10, padding: "12px 14px", border: "1px solid #E4E8F0" }}>
                          <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 2 }}>{formatDate(rep.date)}</div>
                          <div style={{ fontSize: 11, color: "#64748B", marginBottom: 10 }}>{rep.lab}</div>
                          {(rep.values || []).map(v => (
                            <div key={v.name} className="value-row" style={{ padding: "4px 0" }}>
                              <span style={{ fontSize: 11, color: "#475569", flex: 1 }}>{v.name}</span>
                              <span style={{ fontSize: 12, fontWeight: 700, color: STATUS_STYLE[v.status]?.color || "#64748B" }}>
                                {v.value != null ? v.value : (v.valueText || "—")} <span style={{ fontWeight: 400, color: "#94A3B8", fontSize: 10 }}>{v.unit}</span>
                              </span>
                            </div>
                          ))}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* ── SINGLE REPORT ─────────────────────────────────────────────────── */}
      {view === "report" && activeReport && (
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 18 }}>
          {/* Left — Original Report reconstruction */}
          <div className="card">
            <div className="card-header">
              <div style={{ fontWeight: 700, fontSize: 14 }}>Original Report</div>
              <div style={{ display: "flex", gap: 6 }}>
                <button className="btn btn-secondary btn-sm" onClick={() => openPdf(activeReport.id)}>
                  {['.jpg','.jpeg','.png','.bmp','.tiff','.webp'].some(ext => activeReport.pdfName?.toLowerCase().endsWith(ext)) ? 'Open Image' : 'View Report'}
                </button>
                <button className="btn btn-ghost btn-sm" onClick={handlePrint} style={{ display:"flex", alignItems:"center", gap:4 }}><svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="6 9 6 2 18 2 18 9"/><path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"/><rect x="6" y="14" width="12" height="8"/></svg>Print</button>
              </div>
            </div>
            <div className="card-body" style={{ maxHeight: 560, overflowY: "auto", padding: "16px 20px" }}>
              {/* Lab header */}
              <div style={{ textAlign: "center", marginBottom: 12, borderBottom: "2px solid #1a237e", paddingBottom: 10 }}>
                <div style={{ fontWeight: 800, fontSize: 15, color: "#1a237e", letterSpacing: 1, textTransform: "uppercase" }}>
                  {activeReport.lab || "Nashman Institute"}
                </div>
                <div style={{ fontSize: 11, color: "#475569", marginTop: 2 }}>Nashman Institute — Pathology & Regenerative Medicine</div>
                <div style={{ fontWeight: 700, fontSize: 12, color: "#1a237e", marginTop: 6, letterSpacing: 1, textTransform: "uppercase" }}>
                  {activeReport.type} Report
                </div>
              </div>
              {/* Patient info grid */}
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "3px 16px", fontSize: 11, marginBottom: 12, padding: "8px 10px", background: "#F8FAFC", borderRadius: 6, border: "1px solid #E2E8F0" }}>
                <span><b>Patient:</b> {patient.name}</span>
                <span><b>Date:</b> {formatDate(activeReport.date)}</span>
                <span><b>Age / Sex:</b> {(() => {
                  const ay = patient.age_years;
                  if (ay != null && ay < 3) {
                    const totalMonths = Math.round(ay * 12);
                    const y = Math.floor(totalMonths / 12);
                    const m = totalMonths % 12;
                    if (y === 0) return `${m}M`;
                    return `${y}Y ${m}M`;
                  }
                  return patient.age ? `${patient.age}Y` : "—";
                })()} / {patient.sex || "—"}</span>
                <span><b>Lab ID:</b> {activeReport.labId || "—"}</span>
                <span><b>Specimen:</b> Blood</span>
                <span><b>Time:</b> {activeReport.time || "—"}</span>
                {patient.doctor && <span style={{ gridColumn: "1/-1" }}><b>Referred by:</b> {patient.doctor}</span>}
              </div>
              {/* Values table */}
              {activeReport.values && activeReport.values.length > 0 ? (() => {
                const _pdfVals = activeReport.values.filter(v => v.name !== "__comment__");
                const _pdfAllQual = _pdfVals.length > 0 && _pdfVals.every(v =>
                  (v.value === null || v.value === undefined || v.value === "") &&
                  !(v.unit || "").trim() && v.refMin == null
                );
                return (
              <div style={{ overflowY: "auto", maxHeight: 480, borderRadius: 8, border: "1px solid #E2E8F0" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
                <thead style={{ position: "sticky", top: 0, zIndex: 2 }}>
                  <tr style={{ background: "#EEF2FF", borderBottom: "2px solid #C7D2FE" }}>
                    <th style={{ padding: "7px 10px", textAlign: "left", fontWeight: 700, color: "#1a237e", width: _pdfAllQual ? "45%" : "35%", position: "sticky", top: 0, background: "#EEF2FF" }}>Parameter</th>
                    <th style={{ padding: "7px 10px", textAlign: _pdfAllQual ? "left" : "center", fontWeight: 700, color: "#1a237e", width: _pdfAllQual ? "42%" : "15%", position: "sticky", top: 0, background: "#EEF2FF" }}>Result</th>
                    {!_pdfAllQual && <th style={{ padding: "7px 10px", textAlign: "center", fontWeight: 700, color: "#1a237e", width: "12%", position: "sticky", top: 0, background: "#EEF2FF" }}>Unit</th>}
                    {!_pdfAllQual && <th style={{ padding: "7px 10px", textAlign: "center", fontWeight: 700, color: "#1a237e", width: "25%", position: "sticky", top: 0, background: "#EEF2FF" }}>Reference Range</th>}
                    <th style={{ padding: "7px 10px", textAlign: "center", fontWeight: 700, color: "#1a237e", width: "13%", position: "sticky", top: 0, background: "#EEF2FF" }}>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {activeReport.values.map((v, i) => {
                    const isHigh = v.status === "High" || v.status === "Critical High";
                    const isLow  = v.status === "Low"  || v.status === "Critical Low";
                    const isCrit = v.status?.includes("Critical");
                    const valColor = isHigh ? "#DC2626" : isLow ? "#2563EB" : "#16A34A";
                    const isAbnormal = v.status === "Abnormal";
                    const isTNP = v.status === "TNP";
                    const badgeBg  = isCrit ? "#FEE2E2" : isHigh ? "#FEF3C7" : isLow ? "#DBEAFE" : isAbnormal ? "#FEF3C7" : isTNP ? "#F1F5F9" : v.status === "Needs Review" ? "#F1F5F9" : "#DCFCE7";
                    const badgeColor = isCrit ? "#991B1B" : isHigh ? "#92400E" : isLow ? "#1D4ED8" : isAbnormal ? "#D97706" : isTNP ? "#94A3B8" : v.status === "Needs Review" ? "#475569" : "#15803D";
                    return (
                      <tr key={v.name + i}
                        style={{ borderBottom: "1px solid #F1F5F9", background: i % 2 === 0 ? "#fff" : "#FAFBFF" }}
                        onMouseEnter={e => e.currentTarget.style.background = "#F0F4FF"}
                        onMouseLeave={e => e.currentTarget.style.background = i % 2 === 0 ? "#fff" : "#FAFBFF"}>
                        <td style={{ padding: "6px 10px", fontWeight: 500, color: "#0F172A" }}>{v.name}</td>
                        <td style={{ padding: "6px 10px", textAlign: _pdfAllQual ? "left" : "center", fontWeight: 700, color: valColor }}>
                          {(() => {
                            const displayVal = v.valueText || v.value;
                            const isLongText = typeof displayVal === 'string' && displayVal.length > 40;
                            if (isLongText) {
                              return (
                                <div className="ref-tip-wrap" style={{ textAlign: "center" }}>
                                  <span style={{ fontSize: 11, cursor: "help", borderBottom: "1px dashed currentColor" }}>
                                    {displayVal.slice(0, 40)}...
                                  </span>
                                  <div className="ref-tip-box" style={{ fontWeight: 400, fontSize: 11, maxWidth: 280, whiteSpace: "normal", textAlign: "left" }}>
                                    {displayVal}
                                  </div>
                                </div>
                              );
                            }
                            return displayVal;
                          })()}
                        </td>
                        {!_pdfAllQual && <td style={{ padding: "6px 10px", textAlign: "center", color: "#64748B" }}>{v.unit}</td>}
                        {!_pdfAllQual && <td style={{ padding: "6px 10px", textAlign: "center", color: "#475569", fontSize: 11 }}>
                          {(() => {
                            const isTitreRef = v.refText && /^\s*1\s*[:/]\s*\d+\s*$/.test(v.refText.trim());
                            if (isTitreRef) {
                              const tipContent = buildRefTooltip(null, v.refMax, v.refText, v.unit);
                              return (
                                <div className="ref-tip-wrap">
                                  <span style={{ fontSize: 11, color: "#4F46E5", borderBottom: "1px dashed #4F46E5", cursor: "help", whiteSpace: "nowrap" }}>
                                    {"< " + v.refText.trim()}
                                  </span>
                                  <div className="ref-tip-box" style={{ maxHeight: "220px", overflowY: "auto" }}>{tipContent}</div>
                                </div>
                              );
                            }
                            const hasMultiple = isMultiRange(v.refText);
                            if (hasMultiple) {
                              const tipContent = buildRefTooltip(v.refMin, v.refMax, v.refText, v.unit);
                              return (
                                <div className="ref-tip-wrap">
                                  <span style={{ fontSize: 11, color: "#4F46E5", borderBottom: "1px dashed #4F46E5", cursor: "help", whiteSpace: "nowrap" }}>
                                    Multiple ranges ↗
                                  </span>
                                  <div className="ref-tip-box" style={{ maxHeight: "220px", overflowY: "auto" }}>{tipContent}</div>
                                </div>
                              );
                            }
                            return v.refText || (v.refMin != null && v.refMax != null ? `${v.refMin} – ${v.refMax}` : "—");
                          })()}
                        </td>}
                        <td style={{ padding: "6px 10px", textAlign: "center" }}>
                          <span style={{ fontSize: 10, fontWeight: 600, padding: "2px 7px", borderRadius: 99, background: badgeBg, color: badgeColor, display: "inline-block" }}>
                            {v.status || (v.abnormal ? "Abnormal" : "Normal")}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              </div>
                );
              })() : null}
              {/* Footer */}
              <div style={{ marginTop: 10, paddingTop: 8, borderTop: "1px solid #E2E8F0", fontSize: 10, color: "#94A3B8" }}>
                {activeReport.uploadedBy && <div>Uploaded by <b>{activeReport.uploadedBy}</b></div>}
                {activeReport.editedBy && <div style={{ marginTop: 2, fontStyle: "italic" }}>Edited by {activeReport.editedBy} on {activeReport.editedAt}</div>}
              </div>
            </div>
          </div>
          {/* Right — Values + edit */}
          <div className="card">
            <div className="card-header">
              <div style={{ fontWeight: 700, fontSize: 14 }}>{t.valueIndicators}</div>
              <div style={{ display: "flex", gap: 6 }}>
                {canEdit && !editMode && (
                  <button className="btn btn-secondary btn-sm" onClick={startEdit}>{t.editValues}</button>
                )}
                {editMode && <>
                  <button className="btn btn-ghost btn-sm" onClick={() => setEditMode(false)}>Cancel</button>
                  <button className="btn btn-primary btn-sm" disabled={saving} onClick={saveEdit}>
                    {saving ? "Saving…" : t.saveBtn}
                  </button>
                </>}
                <button className="btn btn-secondary btn-sm" onClick={() => {
                  setView("trend"); setStore("view", "trend"); if (activeRep) setStore("rep", activeRep.id);
                  if (!activeTP && allParams.length > 0) { setTP(allParams[0]); setStore('param', allParams[0]); } else if(allParams[0]) setStore('param', allParams[0]);
                  // Clear stale data so declarative useEffect re-fetches
                  setTrendAllReports(null); setTrendType(null); isFetchingRef.current = false; ++trendFetchRef.current;
                }}>
                  {t.trend}
                </button>
                {canDelete && (
                  <button className="btn btn-danger btn-sm" onClick={() => setDel(activeReport.id)}>{t.deleteBtn}</button>
                )}
              </div>
            </div>
            <div className="card-body" style={{ maxHeight: 520, overflowY: "auto" }}>
              {activeReport.values.filter(v => v.abnormal && v.status !== "TNP").length > 0 && (
                <div className="alert alert-red" style={{ marginBottom: 12, display: "flex", alignItems: "center", gap: 8 }}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" style={{ flexShrink: 0 }}><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
                  {activeReport.values.filter(v => v.abnormal && v.status !== "TNP").length} value(s) outside normal range
                </div>
              )}
              {activeReport.comment && (
                <div style={{
                  background: "#F0F4FF", border: "1px solid #C7D2FE", borderRadius: 8,
                  padding: "10px 14px", marginBottom: 12, display: "flex", gap: 10, alignItems: "flex-start"
                }}>
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#4338CA" strokeWidth="2" style={{ flexShrink: 0, marginTop: 1 }}><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/><polyline points="10 9 9 9 8 9"/></svg>
                  <div>
                    <div style={{ fontSize: 10, fontWeight: 700, color: "#4338CA", textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 3 }}>Pathologist Comment</div>
                    <div style={{ fontSize: 13, color: "#1E293B", fontStyle: "italic", lineHeight: 1.5 }}>{activeReport.comment}</div>
                  </div>
                </div>
              )}
              {/* ── Format-aware value display ─────────────────────────────── */}
              {!editMode && (() => {
                const fmt = getDisplayFormat(activeReport.type);
                const vals = (activeReport.values || []).filter(v => v.name !== "__comment__");

                // ── NARRATIVE format (PBF, FNAC) ───────────────────────────
                if (fmt === "narrative") {
                  if (vals.length === 0) return <div style={{ fontSize: 13, color: "#94A3B8", padding: "20px 0", textAlign: "center" }}>No parameters extracted. See report PDF.</div>;
                  return (
                    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                      {vals.map((v, i) => {
                        const ss = STATUS_STYLE[v.status] || STATUS_STYLE["Needs Review"];
                        return (
                          <div key={i} style={{ background: v.abnormal ? "#FFF8F0" : "#F8FAFC", border: `1px solid ${v.abnormal ? "#FED7AA" : "#E2E8F0"}`, borderRadius: 8, padding: "10px 14px" }}>
                            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12 }}>
                              <div style={{ flex: 1 }}>
                                <div style={{ fontSize: 11, fontWeight: 700, color: "#64748B", textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 4 }}>{v.name}</div>
                                <div style={{ fontSize: 13, color: "#1E293B", lineHeight: 1.5 }}>{v.valueText || v.value || "—"}</div>
                              </div>
                              <StatusBadge status={v.status} />
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  );
                }

                // ── SECTIONED SEMEN format ─────────────────────────────────
                if (fmt === "sectioned-semen") {
                  const physicalParams = ["method of collection","color","odour","consistency","reaction"];
                  const physical = vals.filter(v => physicalParams.some(p => v.name?.toLowerCase().includes(p)));
                  const microscopic = vals.filter(v => !physicalParams.some(p => v.name?.toLowerCase().includes(p)));
                  const renderRow = (v, i) => {
                    const displayVal = v.value !== null && v.value !== undefined ? v.value : (v.valueText || "—");
                    const isLong = typeof displayVal === "string" && displayVal.length > 25;
                    const ss = STATUS_STYLE[v.status] || STATUS_STYLE["Needs Review"];
                    return (
                      <tr key={i} style={{ borderBottom: "1px solid #F1F5F9" }}
                        onMouseEnter={e => e.currentTarget.style.background = "#EEF2FF"}
                        onMouseLeave={e => e.currentTarget.style.background = ""}>
                        <td style={{ padding: "8px 12px", fontWeight: 500, fontSize: 13, color: "#374151" }}>{v.name}</td>
                        <td style={{ padding: "8px 12px", textAlign: "right", fontWeight: 600, fontSize: 13, color: v.abnormal ? "#DC2626" : "#0F172A" }}>
                          {isLong ? (
                            <div className="ref-tip-wrap">
                              <span style={{ fontSize: 11, cursor: "help", borderBottom: "1px dashed currentColor" }}>{String(displayVal).slice(0,25)}...</span>
                              <div className="ref-tip-box" style={{ fontWeight: 400, fontSize: 11, maxWidth: 240, whiteSpace: "normal", textAlign: "left" }}>{displayVal}</div>
                            </div>
                          ) : displayVal}
                        </td>
                        <td style={{ padding: "8px 12px", textAlign: "center", fontSize: 12, color: "#64748B" }}>{v.unit || ""}</td>
                        <td style={{ padding: "8px 12px", textAlign: "center" }}>
                          {v.refMin != null && v.refMax != null ? <span style={{ fontSize: 11, color: "#94A3B8" }}>{v.refMin} – {v.refMax}</span> : <span style={{ color: "#CBD5E1" }}>—</span>}
                        </td>
                        <td style={{ padding: "8px 12px", textAlign: "center" }}><StatusBadge status={v.status} /></td>
                      </tr>
                    );
                  };
                  const tableHeader = (
                    <tr style={{ background: "#F8FAFC", borderBottom: "1px solid #E4E8F0" }}>
                      <th style={{ padding: "8px 12px", fontWeight: 500, fontSize: 11, color: "#94A3B8", textAlign: "left", width: "35%" }}>PARAMETER</th>
                      <th style={{ padding: "8px 12px", fontWeight: 500, fontSize: 11, color: "#94A3B8", textAlign: "right", width: "18%" }}>RESULT</th>
                      <th style={{ padding: "8px 12px", fontWeight: 500, fontSize: 11, color: "#94A3B8", textAlign: "center", width: "12%" }}>UNIT</th>
                      <th style={{ padding: "8px 12px", fontWeight: 500, fontSize: 11, color: "#94A3B8", textAlign: "center", width: "20%" }}>REFERENCE</th>
                      <th style={{ padding: "8px 12px", fontWeight: 500, fontSize: 11, color: "#94A3B8", textAlign: "center", width: "15%" }}>STATUS</th>
                    </tr>
                  );
                  return (
                    <div>
                      {physical.length > 0 && (
                        <div style={{ marginBottom: 12 }}>
                          <div style={{ fontSize: 10, fontWeight: 700, color: "#64748B", textTransform: "uppercase", letterSpacing: "1px", padding: "6px 12px", background: "#F1F5F9", borderRadius: "6px 6px 0 0", borderBottom: "1px solid #E2E8F0" }}>Physical Parameters</div>
                          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                            <thead>{tableHeader}</thead>
                            <tbody>{physical.map(renderRow)}</tbody>
                          </table>
                        </div>
                      )}
                      {microscopic.length > 0 && (
                        <div>
                          <div style={{ fontSize: 10, fontWeight: 700, color: "#64748B", textTransform: "uppercase", letterSpacing: "1px", padding: "6px 12px", background: "#F1F5F9", borderRadius: "6px 6px 0 0", borderBottom: "1px solid #E2E8F0" }}>Microscopic Parameters</div>
                          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                            <thead>{tableHeader}</thead>
                            <tbody>{microscopic.map(renderRow)}</tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  );
                }

                // ── SEQUENTIAL format (OGTT) ───────────────────────────────
                if (fmt === "sequential") {
                  return (
                    <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                      <thead>
                        <tr style={{ background: "#F8FAFC", borderBottom: "1px solid #E4E8F0" }}>
                          <th style={{ padding: "8px 12px", fontWeight: 500, fontSize: 11, color: "#94A3B8", textAlign: "left" }}>TIMEPOINT</th>
                          <th style={{ padding: "8px 12px", fontWeight: 500, fontSize: 11, color: "#94A3B8", textAlign: "right" }}>RESULT</th>
                          <th style={{ padding: "8px 12px", fontWeight: 500, fontSize: 11, color: "#94A3B8", textAlign: "center" }}>UNIT</th>
                          <th style={{ padding: "8px 12px", fontWeight: 500, fontSize: 11, color: "#94A3B8", textAlign: "center" }}>NORMAL</th>
                          <th style={{ padding: "8px 12px", fontWeight: 500, fontSize: 11, color: "#94A3B8", textAlign: "center" }}>STATUS</th>
                        </tr>
                      </thead>
                      <tbody>
                        {vals.map((v, i) => (
                          <tr key={i} style={{ borderBottom: "1px solid #F1F5F9", background: i % 2 === 0 ? "#fff" : "#FAFAFA" }}
                            onMouseEnter={e => e.currentTarget.style.background = "#EEF2FF"}
                            onMouseLeave={e => e.currentTarget.style.background = i % 2 === 0 ? "#fff" : "#FAFAFA"}>
                            <td style={{ padding: "8px 12px", fontWeight: 500, color: "#374151" }}>{v.name}</td>
                            <td style={{ padding: "8px 12px", textAlign: "right", fontWeight: 700, color: v.abnormal ? "#DC2626" : "#0F172A" }}>{v.valueText || v.value || "—"}</td>
                            <td style={{ padding: "8px 12px", textAlign: "center", fontSize: 12, color: "#64748B" }}>{v.unit || ""}</td>
                            <td style={{ padding: "8px 12px", textAlign: "center", fontSize: 12, color: "#94A3B8" }}>{v.refMax != null ? `Up to ${v.refMax}` : "—"}</td>
                            <td style={{ padding: "8px 12px", textAlign: "center" }}><StatusBadge status={v.status} /></td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  );
                }

                // ── STAGED format (HbA1c, Vitamin D, eGFR, Lipid) ─────────
                if (fmt === "staged") {
                  return (
                    <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                      <thead>
                        <tr style={{ background: "#F8FAFC", borderBottom: "1px solid #E4E8F0" }}>
                          <th style={{ padding: "8px 12px", fontWeight: 500, fontSize: 11, color: "#94A3B8", textAlign: "left", width: "30%" }}>PARAMETER</th>
                          <th style={{ padding: "8px 12px", fontWeight: 500, fontSize: 11, color: "#94A3B8", textAlign: "right", width: "20%" }}>RESULT</th>
                          <th style={{ padding: "8px 12px", fontWeight: 500, fontSize: 11, color: "#94A3B8", textAlign: "center", width: "10%" }}>UNIT</th>
                          <th style={{ padding: "8px 12px", fontWeight: 500, fontSize: 11, color: "#94A3B8", textAlign: "left", width: "25%" }}>CLASSIFICATION</th>
                          <th style={{ padding: "8px 12px", fontWeight: 500, fontSize: 11, color: "#94A3B8", textAlign: "center", width: "15%" }}>STATUS</th>
                        </tr>
                      </thead>
                      <tbody>
                        {vals.map((v, i) => {
                          // Extract tier label from value_text if present (e.g. "7.26% — Fair Control")
                          const tierMatch = (v.valueText || "").match(/—\s*(.+)$/);
                          const tierLabel = tierMatch ? tierMatch[1] : "";
                          const displayVal = v.value !== null && v.value !== undefined ? v.value : "";
                          return (
                            <tr key={i} style={{ borderBottom: "1px solid #F1F5F9" }}
                              onMouseEnter={e => e.currentTarget.style.background = "#EEF2FF"}
                              onMouseLeave={e => e.currentTarget.style.background = ""}>
                              <td style={{ padding: "8px 12px", fontWeight: 500, color: "#374151" }}>{v.name}</td>
                              <td style={{ padding: "8px 12px", textAlign: "right", fontWeight: 700, color: v.abnormal ? "#DC2626" : "#0F172A" }}>{displayVal || "—"}</td>
                              <td style={{ padding: "8px 12px", textAlign: "center", fontSize: 12, color: "#64748B" }}>{v.unit || ""}</td>
                              <td style={{ padding: "8px 12px" }}>
                                {tierLabel ? (
                                  <div className="ref-tip-wrap">
                                    <span style={{ fontSize: 12, fontWeight: 600, color: v.abnormal ? "#DC2626" : v.status === "Low" ? "#2563EB" : v.status === "Needs Review" ? "#D97706" : "#16A34A", cursor: v.refText ? "help" : "default", borderBottom: v.refText ? "1px dashed currentColor" : "none" }}>{tierLabel}</span>
                                    {v.refText && <div className="ref-tip-box" style={{ fontWeight: 400, fontSize: 11, maxWidth: 300, whiteSpace: "normal", textAlign: "left" }}>{formatRefText(v.refText)}</div>}
                                  </div>
                                ) : (
                                  <span style={{ fontSize: 11, color: "#94A3B8" }}>{v.refText ? v.refText.slice(0, 60) : "—"}</span>
                                )}
                              </td>
                              <td style={{ padding: "8px 12px", textAlign: "center" }}><StatusBadge status={v.status} /></td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  );
                }

                // ── HORMONE format — show ref_text as multi-line hover ─────
                if (fmt === "hormone") {
                  return (
                    <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                      <thead>
                        <tr style={{ background: "#F8FAFC", borderBottom: "1px solid #E4E8F0" }}>
                          <th style={{ padding: "8px 12px", fontWeight: 500, fontSize: 11, color: "#94A3B8", textAlign: "left", width: "25%" }}>PARAMETER</th>
                          <th style={{ padding: "8px 12px", fontWeight: 500, fontSize: 11, color: "#94A3B8", textAlign: "right", width: "18%" }}>RESULT</th>
                          <th style={{ padding: "8px 12px", fontWeight: 500, fontSize: 11, color: "#94A3B8", textAlign: "center", width: "12%" }}>UNIT</th>
                          <th style={{ padding: "8px 12px", fontWeight: 500, fontSize: 11, color: "#94A3B8", textAlign: "center", width: "30%" }}>REFERENCE</th>
                          <th style={{ padding: "8px 12px", fontWeight: 500, fontSize: 11, color: "#94A3B8", textAlign: "center", width: "15%" }}>STATUS</th>
                        </tr>
                      </thead>
                      <tbody>
                        {vals.map((v, i) => {
                          const dv = v.valueText && v.valueText !== String(v.value) ? v.valueText : (v.value ?? v.valueText ?? "—");
                          const hasMulti = isMultiRange(v.refText);
                          return (
                          <tr key={i} style={{ borderBottom: "1px solid #F1F5F9" }}
                            onMouseEnter={e => e.currentTarget.style.background = "#EEF2FF"}
                            onMouseLeave={e => e.currentTarget.style.background = ""}>
                            <td style={{ padding: "8px 12px", fontWeight: 500, fontSize: 13, color: "#374151" }}>{v.name}</td>
                            <td style={{ padding: "8px 12px", textAlign: "right", fontWeight: 700, fontSize: 13, color: v.abnormal ? "#DC2626" : "#0F172A" }}>{v.value ?? v.valueText ?? "—"}</td>
                            <td style={{ padding: "8px 12px", textAlign: "center", fontSize: 12, color: "#64748B" }}>{v.unit || ""}</td>
                            <td style={{ padding: "8px 12px", textAlign: "center" }}>
                              {(() => {
                                const tipContent = buildRefTooltip(v.refMin, v.refMax, v.refText, v.unit);
                                const isTitreRef = v.refText && /^\s*1\s*[:/]\s*\d+\s*$/.test(v.refText.trim());
                                const displayLabel = isTitreRef
                                  ? ("< " + v.refText.trim())
                                  : isMultiRange(v.refText)
                                    ? "Multiple ranges ↗"
                                    : v.refMin != null && v.refMax != null
                                      ? `${v.refMin} – ${v.refMax}`
                                      : v.refText || "—";
                                if (tipContent) {
                                  return (
                                    <div className="ref-tip-wrap">
                                      <span style={{ fontSize: 11, color: "#6B7280", cursor: "help", borderBottom: "1px dashed #CBD5E1" }}>
                                        {displayLabel}
                                      </span>
                                      <div className="ref-tip-box" style={{ maxHeight: "220px", overflowY: "auto" }}>{tipContent}</div>
                                    </div>
                                  );
                                }
                                return <span style={{ fontSize: 11, color: "#6B7280" }}>{displayLabel}</span>;
                              })()}
                            </td>
                            <td style={{ padding: "8px 12px", textAlign: "center" }}><StatusBadge status={v.status} /></td>
                          </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  );
                }

                // ── DEFAULT: standard numeric table ────────────────────────
                // ── CONCLUSION format (FNAC / Cytopathology) ─────────────
                if (fmt === "conclusion") {
                  const comment = vals.find(v => v.name === "__comment__") || activeReport.comment;
                  const text = typeof comment === 'string' ? comment : comment?.valueText || "";
                  return (
                    <div style={{ background: "#F8FAFC", border: "1px solid #E2E8F0", borderRadius: 10, padding: "16px 18px" }}>
                      <div style={{ fontSize: 10, fontWeight: 700, color: "#64748B", textTransform: "uppercase", letterSpacing: "1px", marginBottom: 10 }}>Cytopathology Report</div>
                      {text ? (
                        <div style={{ fontSize: 13, color: "#1E293B", lineHeight: 1.8, whiteSpace: "pre-wrap" }}>{text}</div>
                      ) : (
                        <div style={{ fontSize: 13, color: "#94A3B8", fontStyle: "italic" }}>No report text extracted. View the original PDF for the full cytopathology report.</div>
                      )}
                      <div style={{ marginTop: 12, padding: "8px 12px", background: "#FFF8F0", border: "1px solid #FED7AA", borderRadius: 7, fontSize: 12, color: "#92400E" }}>
                        ⚕ Pathologist review required — this is a cytopathology report
                      </div>
                    </div>
                  );
                }

                // ── ELISA format (HBsAg ELISA) ───────────────────────────
                if (fmt === "elisa") {
                  const cutoff = vals.find(v => v.name?.toLowerCase().includes("cut"));
                  const od = vals.find(v => v.name?.toLowerCase().includes("od") || v.name?.toLowerCase().includes("patient od"));
                  const opinion = vals.find(v => v.name?.toLowerCase().includes("opinion") || v.name === "__comment__");
                  // OD/cutoff calculation is the clinical standard (WHO, Mayo, all ELISA kit manufacturers)
                  // Opinion param text takes precedence only when explicitly set
                  const opinionText = opinion?.valueText?.trim().toLowerCase();
                  const hasOpinion = opinionText && opinionText !== "" && opinionText !== "needs review";
                  const isPositive = hasOpinion
                    ? opinionText.includes("positive") || opinionText.includes("reactive")
                    : (od?.value && cutoff?.value && od.value >= cutoff.value);
                  const displayOpinion = hasOpinion ? opinion.valueText : (isPositive ? "Positive" : "Negative");
                  return (
                    <div>
                      <div style={{ background: isPositive ? "#FFF1F2" : "#F0FDF4", border: `1px solid ${isPositive ? "#FECDD3" : "#BBF7D0"}`, borderRadius: 8, padding: "12px 16px", marginBottom: 12, textAlign: "center" }}>
                        <div style={{ fontSize: 11, fontWeight: 700, color: "#64748B", textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 4 }}>Opinion</div>
                        <div style={{ fontSize: 22, fontWeight: 800, color: isPositive ? "#DC2626" : "#16A34A" }}>{displayOpinion}</div>
                      </div>
                      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                        <tbody>
                          {cutoff && <tr style={{ borderBottom: "1px solid #F1F5F9" }}><td style={{ padding: "8px 12px", color: "#64748B" }}>Cut-off Value</td><td style={{ padding: "8px 12px", fontWeight: 600, textAlign: "right" }}>{cutoff.value}</td></tr>}
                          {od && <tr style={{ borderBottom: "1px solid #F1F5F9" }}><td style={{ padding: "8px 12px", color: "#64748B" }}>Patient OD</td><td style={{ padding: "8px 12px", fontWeight: 700, color: isPositive ? "#DC2626" : "#16A34A", textAlign: "right" }}>{od.value}</td></tr>}
                          {cutoff && od && <tr><td style={{ padding: "8px 12px", color: "#64748B" }}>OD / Cut-off Ratio</td><td style={{ padding: "8px 12px", fontWeight: 600, textAlign: "right" }}>{(od.value / cutoff.value).toFixed(2)}×</td></tr>}
                        </tbody>
                      </table>
                    </div>
                  );
                }

                // ── SECTIONED MICRO (Gram Stain + V/S + Urethral) ─────────
                if (fmt === "sectioned-micro") {
                  // Group by section prefix (V/S -, Urethral -, Z-N -, Gram -)
                  const sections = {};
                  vals.forEach(v => {
                    let sec = "Gram Stain";
                    if (v.name?.startsWith("V/S -")) sec = "V/S Study";
                    else if (v.name?.startsWith("Urethral -")) sec = "Urethral Smear Study";
                    else if (v.name?.startsWith("Z-N -")) sec = "Z-N Stain";
                    else if (v.name?.startsWith("Gram -")) sec = "Gram Stain";
                    if (!sections[sec]) sections[sec] = [];
                    sections[sec].push(v);
                  });
                  return (
                    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                      {Object.entries(sections).map(([secName, secVals]) => (
                        <div key={secName}>
                          <div style={{ fontSize: 10, fontWeight: 700, color: "#64748B", textTransform: "uppercase", letterSpacing: "1px", padding: "5px 10px", background: "#F1F5F9", borderRadius: "6px 6px 0 0", borderBottom: "1px solid #E2E8F0" }}>{secName}</div>
                          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                            <tbody>
                              {secVals.map((v, i) => {
                                const displayName = v.name?.replace(/^(V\/S - |Urethral - |Z-N - |Gram - )/, "") || v.name;
                                const displayVal = v.value !== null && v.value !== undefined ? v.value : (v.valueText || "—");
                                return (
                                  <tr key={i} style={{ borderBottom: "1px solid #F1F5F9" }}
                                    onMouseEnter={e => e.currentTarget.style.background = "#EEF2FF"}
                                    onMouseLeave={e => e.currentTarget.style.background = ""}>
                                    <td style={{ padding: "8px 12px", fontWeight: 600, fontSize: 13, color: v.abnormal ? "#DC2626" : "#0F172A" }}>
                                      {typeof displayVal === "string" && displayVal.length > 30 ? (
                                        <div className="ref-tip-wrap">
                                          <span style={{ fontSize: 12, cursor: "help", borderBottom: "1px dashed currentColor" }}>{displayVal.slice(0,30)}...</span>
                                          <div className="ref-tip-box" style={{ fontWeight: 400, fontSize: 11, maxWidth: 260, whiteSpace: "normal" }}>{displayVal}</div>
                                        </div>
                                      ) : displayVal}
                                    </td>
                                    <td style={{ padding: "8px 12px", textAlign: "center", fontSize: 12, color: "#64748B", width: "12%" }}>{v.unit || ""}</td>
                                    <td style={{ padding: "8px 12px", textAlign: "center", width: "18%" }}><StatusBadge status={v.status} /></td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      ))}
                    </div>
                  );
                }

                // ── SECTIONED URINE ───────────────────────────────────────
                if (fmt === "sectioned-urine") {
                  // Section key maps — match by lowercase name inclusion
                  // Order matters: physical → crystals → casts → chemical → microscopic (remainder)
                  const physicalKeys = ["color","appearance","sediment"];
                  const chemicalKeys = ["protein","albumin","glucose","ketone","bilirubin","urobilinogen","nitrite","gravity","reaction","ph","ph)","leucocyte","leukocyte","occult blood"];
                  const castKeys = ["cast","casts/lpf"];
                  const crystalKeys = ["calcium oxalate","uric acid","amorphous","triple phosphate","crystal"];

                  const physical   = vals.filter(v => physicalKeys.some(k => v.name?.toLowerCase().includes(k)));
                  const casts      = vals.filter(v => !physical.includes(v) && castKeys.some(k => v.name?.toLowerCase().includes(k)));
                  const crystals   = vals.filter(v => !physical.includes(v) && !casts.includes(v) && crystalKeys.some(k => v.name?.toLowerCase().includes(k)));
                  const chemical   = vals.filter(v => !physical.includes(v) && !casts.includes(v) && !crystals.includes(v) && chemicalKeys.some(k => v.name?.toLowerCase().includes(k)));
                  const microscopic = vals.filter(v =>
                    !physical.includes(v) && !chemical.includes(v) &&
                    !casts.includes(v) && !crystals.includes(v)
                  );

                  const renderUrineRow = (v, i) => {
                    // Prefer valueText for display (preserves ranges like "3-5"), fall back to numeric value
                    const displayVal = (v.valueText && v.valueText !== "" && v.valueText !== String(v.value))
                      ? v.valueText
                      : (v.value !== null && v.value !== undefined ? v.value : (v.valueText || "—"));
                    const hasRef = v.refMin !== null && v.refMin !== undefined && v.refMax !== null && v.refMax !== undefined;
                    const isNotNeeded = v.status === "TNP" || (v.valueText || "").toLowerCase() === "not needed";
                    return (
                      <tr key={i} style={{ borderBottom: "1px solid #F1F5F9", opacity: isNotNeeded ? 0.55 : 1 }}
                        onMouseEnter={e => { if (!isNotNeeded) e.currentTarget.style.background = "#EEF2FF"; }}
                        onMouseLeave={e => { e.currentTarget.style.background = ""; }}>
                        <td style={{ padding: "7px 12px", fontWeight: 500, fontSize: 13, color: "#374151", width: "38%" }}>{v.name}</td>
                        <td style={{ padding: "7px 12px", fontWeight: isNotNeeded ? 400 : 600, fontSize: 13, color: isNotNeeded ? "#94A3B8" : v.abnormal ? "#DC2626" : "#0F172A", fontStyle: isNotNeeded ? "italic" : "normal" }}>
                          {displayVal}{v.unit ? <span style={{ fontSize: 11, fontWeight: 400, color: "#94A3B8", marginLeft: 3 }}>{v.unit}</span> : null}
                        </td>
                        <td style={{ padding: "7px 12px", textAlign: "center", fontSize: 11, color: "#94A3B8", width: "16%" }}>
                          {hasRef ? (v.refText && /^\s*1\s*[:/]\s*\d+\s*$/.test(v.refText.trim()) ? ('< ' + v.refText.trim()) : `${v.refMin}–${v.refMax}`) : ""}
                        </td>
                        <td style={{ padding: "7px 12px", textAlign: "center", width: "16%" }}>
                          {isNotNeeded ? <span style={{ fontSize: 11, color: "#94A3B8", fontStyle: "italic" }}>—</span> : <StatusBadge status={v.status} />}
                        </td>
                      </tr>
                    );
                  };

                  const sectionHead = (title, color) => (
                    <div style={{ fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.07em",
                      padding: "5px 12px", borderRadius: "6px 6px 0 0", borderBottom: "1px solid",
                      marginTop: 10, color: color.text, background: color.bg, borderColor: color.border }}>
                      {title}
                    </div>
                  );

                  const COLORS = {
                    physical:    { bg: "#EFF6FF", border: "#BFDBFE", text: "#1D4ED8" },
                    chemical:    { bg: "#FFF7ED", border: "#FED7AA", text: "#C2410C" },
                    microscopic: { bg: "#F0FDF4", border: "#BBF7D0", text: "#15803D" },
                    casts:       { bg: "#FAF5FF", border: "#E9D5FF", text: "#7E22CE" },
                    crystals:    { bg: "#FFFBEB", border: "#FDE68A", text: "#B45309" },
                  };

                  if (vals.length === 0) return <div style={{ fontSize: 13, color: "#94A3B8", padding: "20px 0", textAlign: "center" }}>No parameters extracted. See report PDF.</div>;
                  return (
                    <div>
                      {physical.length > 0 && <>{sectionHead("Physical Examination", COLORS.physical)}<table style={{ width: "100%", borderCollapse: "collapse" }}><tbody>{physical.map(renderUrineRow)}</tbody></table></>}
                      {chemical.length > 0 && <>{sectionHead("Chemical Examination", COLORS.chemical)}<table style={{ width: "100%", borderCollapse: "collapse" }}><tbody>{chemical.map(renderUrineRow)}</tbody></table></>}
                      {microscopic.length > 0 && <>{sectionHead("Microscopic Examination", COLORS.microscopic)}<table style={{ width: "100%", borderCollapse: "collapse" }}><tbody>{microscopic.map(renderUrineRow)}</tbody></table></>}
                      {crystals.length > 0 && <>{sectionHead("Crystals", COLORS.crystals)}<table style={{ width: "100%", borderCollapse: "collapse" }}><tbody>{crystals.map(renderUrineRow)}</tbody></table></>}
                      {casts.length > 0 && <>{sectionHead("Casts", COLORS.casts)}<table style={{ width: "100%", borderCollapse: "collapse" }}><tbody>{casts.map(renderUrineRow)}</tbody></table></>}
                    </div>
                  );
                }

                // ── SECTIONED STOOL ───────────────────────────────────────
                if (fmt === "sectioned-stool") {
                  const physicalKeys = ["consistency","color","mucus","blood"];
                  const chemicalKeys = ["reaction","occult","reducing"];
                  const physical = vals.filter(v => physicalKeys.some(k => v.name?.toLowerCase().includes(k)));
                  const chemical = vals.filter(v => chemicalKeys.some(k => v.name?.toLowerCase().includes(k)));
                  const microscopic = vals.filter(v => !physical.includes(v) && !chemical.includes(v));
                  const renderStoolRow = (v, i) => {
                    // Prefer valueText (preserves ranges like "3-5", symbols like "(+)"), fall back to numeric value
                    const displayVal = (v.valueText && v.valueText !== "" && v.valueText !== String(v.value))
                      ? v.valueText
                      : (v.value !== null && v.value !== undefined ? v.value : (v.valueText || "—"));
                    return (
                      <tr key={i} style={{ borderBottom: "1px solid #F1F5F9" }}
                        onMouseEnter={e => e.currentTarget.style.background = "#EEF2FF"}
                        onMouseLeave={e => e.currentTarget.style.background = ""}>
                        <td style={{ padding: "7px 12px", fontWeight: 500, fontSize: 13, color: "#374151", width: "40%" }}>{v.name}</td>
                        <td style={{ padding: "7px 12px", fontWeight: 600, fontSize: 13, color: v.abnormal ? "#DC2626" : "#0F172A" }}>{displayVal}</td>
                        <td style={{ padding: "7px 12px", textAlign: "center", fontSize: 12, color: "#64748B", width: "12%" }}>{v.unit || ""}</td>
                        <td style={{ padding: "7px 12px", textAlign: "center", width: "18%" }}><StatusBadge status={v.status} /></td>
                      </tr>
                    );
                  };
                  const sectionHeader = (title) => (
                    <div style={{ fontSize: 10, fontWeight: 700, color: "#64748B", textTransform: "uppercase", letterSpacing: "1px", padding: "5px 10px", background: "#F1F5F9", borderRadius: "6px 6px 0 0", borderBottom: "1px solid #E2E8F0", marginTop: 10 }}>{title}</div>
                  );
                  return (
                    <div>
                      {physical.length > 0 && <>{sectionHeader("Physical Examination")}<table style={{ width: "100%", borderCollapse: "collapse" }}><tbody>{physical.map(renderStoolRow)}</tbody></table></>}
                      {chemical.length > 0 && <>{sectionHeader("Chemical Examination")}<table style={{ width: "100%", borderCollapse: "collapse" }}><tbody>{chemical.map(renderStoolRow)}</tbody></table></>}
                      {microscopic.length > 0 && <>{sectionHeader("Microscopic Examination")}<table style={{ width: "100%", borderCollapse: "collapse" }}><tbody>{microscopic.map(renderStoolRow)}</tbody></table></>}
                    </div>
                  );
                }
                // ── QUALITATIVE format (Urine PT, Blood Grouping, OBT, Serology rapid tests) ──
                if (fmt === "qualitative") {
                  if (vals.length === 0) return <div style={{ fontSize: 13, color: "#94A3B8", padding: "20px 0", textAlign: "center" }}>No parameters extracted. See report PDF.</div>;
                  return (
                    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                      {vals.map((v, i) => {
                        const displayVal = v.valueText || v.value || "—";
                        const isPositive = v.status === "Abnormal" || v.status === "High";
                        const isNormal = v.status === "Normal";
                        const isNeedsReview = v.status === "Needs Review";
                        const bgColor = isPositive ? "#FFF1F2" : isNormal ? "#F0FDF4" : "#FFFBEB";
                        const borderColor = isPositive ? "#FECDD3" : isNormal ? "#BBF7D0" : "#FDE68A";
                        const valColor = isPositive ? "#DC2626" : isNormal ? "#16A34A" : "#D97706";
                        return (
                          <div key={i} style={{ background: bgColor, border: `1px solid ${borderColor}`, borderRadius: 8, padding: "12px 16px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                            <div style={{ fontSize: 13, fontWeight: 500, color: "#374151" }}>{v.name}</div>
                            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                              <span style={{ fontSize: 15, fontWeight: 700, color: valColor }}>{displayVal}</span>
                              <StatusBadge status={v.status} />
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  );
                }

                return null; // falls through to existing table below
              })()}
              {getDisplayFormat(activeReport.type) === "numeric" || editMode || !["narrative","sectioned-semen","sequential","staged","hormone","qualitative","sectioned-micro","sectioned-stool","sectioned-urine","elisa","conclusion"].includes(getDisplayFormat(activeReport.type)) ? (() => {
                // Detect if all rows are qualitative (no numeric value, no unit, no ref range)
                const allVals = editMode ? editVals : activeReport.values;
                const isAllQual = !editMode && allVals.length > 0 && allVals.every(v =>
                  (v.value === null || v.value === undefined || v.value === "") && !(v.unit || "").trim() && v.refMin == null
                );
                return (
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                  <thead>
                    <tr style={{ background: "#F8FAFC", borderBottom: "1px solid #E4E8F0" }}>
                      <th style={{ padding: "8px 12px", fontWeight: 500, fontSize: 11, color: "#94A3B8", textAlign: "left", width: isAllQual ? "50%" : "38%" }}>{t.parameter.toUpperCase()}</th>
                      <th style={{ padding: "8px 12px", fontWeight: 500, fontSize: 11, color: "#94A3B8", textAlign: "left", width: isAllQual ? "35%" : "14%" }}>{t.result.toUpperCase()}</th>
                      {!isAllQual && <th style={{ padding: "8px 12px", fontWeight: 500, fontSize: 11, color: "#94A3B8", textAlign: "center", width: "13%" }}>{t.unit.toUpperCase()}</th>}
                      {!isAllQual && <th style={{ padding: "8px 12px", fontWeight: 500, fontSize: 11, color: "#94A3B8", textAlign: "center", width: "20%" }}>{t.reference.toUpperCase()}</th>}
                      <th style={{ padding: "8px 12px", fontWeight: 500, fontSize: 11, color: "#94A3B8", textAlign: "center", width: isAllQual ? "15%" : "15%" }}>{t.status2.toUpperCase()}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(editMode ? editVals : activeReport.values).map((v, i) => {
                      const isHigh = v.status === "High" || v.status === "Critical High";
                      const isLow  = v.status === "Low"  || v.status === "Critical Low";
                      const isNeedsReview = v.status === "Needs Review";
                      const isAbnormal = v.status === "Abnormal";
                      const isTNP = v.status === "TNP";
                      const valColor = isTNP ? "#94A3B8" : isHigh ? "#DC2626" : isLow ? "#2563EB" : isNeedsReview ? "#475569" : isAbnormal ? "#92400E" : "#16A34A";
                      const badgeBg  = isTNP ? "#F1F5F9" : isHigh ? "#FEE2E2" : isLow ? "#DBEAFE" : isNeedsReview ? "#F1F5F9" : isAbnormal ? "#FEF3C7" : "#DCFCE7";
                      const badgeColor = isTNP ? "#94A3B8" : isHigh ? "#B91C1C" : isLow ? "#1D4ED8" : isNeedsReview ? "#475569" : isAbnormal ? "#92400E" : "#15803D";
                      const badgeText  = isTNP ? "TNP"
                                       : isHigh ? (v.status === "Critical High" ? "Critical H" : "High")
                                       : isLow  ? (v.status === "Critical Low"  ? "Critical L" : "Low")
                                       : isNeedsReview ? "Needs Review"
                                       : isAbnormal ? "Abnormal"
                                       : "Normal";
                      // Qualitative row: value is text-only (no numeric value, no ref range)
                      // Qualitative row: has text result, no numeric value, no ref range, no unit
                      // Also treat as qualitative if entire report is qualitative (isAllQual)
                      const unitStr = (v.unit || "").trim();
                      const isQualRow = !editMode && (
                        isAllQual ||
                        ((v.value === null || v.value === undefined || v.value === "") && v.valueText && (v.refMin == null) && (v.refMax == null) && !unitStr)
                      );
                      const rowBg = (isHigh || isAbnormal) ? "rgba(220,38,38,0.03)" : isLow ? "rgba(37,99,235,0.03)" : "transparent";
                      return (
                        <tr key={v.name + i} style={{ borderBottom: "1px solid #F1F5F9", cursor: "default", background: rowBg }}
                          onMouseEnter={e => e.currentTarget.style.background = "#EEF2FF"}
                          onMouseLeave={e => e.currentTarget.style.background = rowBg}>
                          <td style={{ padding: "9px 12px", color: "#0F172A", fontWeight: 400, verticalAlign: "middle" }}>{v.name}</td>
                          {isQualRow ? (
                            // Qualitative row: full-width result spanning unit + ref range columns
                            <>
                              <td colSpan={3} style={{ padding: "9px 12px", textAlign: "left", fontWeight: 400, fontSize: 13, color: valColor, lineHeight: 1.4 }}>
                                {isTNP ? <span style={{ fontStyle: "italic", color: "#94A3B8", fontSize: 12 }}>Not needed</span> : (v.valueText || v.value)}
                              </td>
                              <td style={{ padding: "9px 12px", textAlign: "center" }}>
                                <span style={{ fontSize: 11, fontWeight: 500, padding: "2px 8px", borderRadius: 99, display: "inline-block", background: badgeBg, color: badgeColor }}>{badgeText}</span>
                              </td>
                            </>
                          ) : (
                            // Numeric row: show result, unit, ref range separately
                            <>
                              <td style={{ padding: "9px 12px", textAlign: "right", fontWeight: 600, fontSize: 14, color: editMode ? "#0F172A" : valColor, verticalAlign: "middle" }}>
                                {editMode ? (
                                  <input
                                    className="input"
                                    value={v.valueText || (v.value !== null && v.value !== undefined && v.value !== "" ? v.value : "")}
                                    onChange={e => updateEdit(i, "value", e.target.value)}
                                    style={{ width: v.value === null && v.valueText ? 100 : 70, padding: "3px 6px", textAlign: "center", fontSize: 13 }}
                                  />
                                ) : (isTNP ? <span style={{ fontStyle: "italic", color: "#94A3B8", fontSize: 12 }}>—</span> : (v.valueText || v.value))}
                              </td>
                              <td style={{ padding: "9px 12px", textAlign: "center", color: "#64748B", fontSize: 12, verticalAlign: "middle" }}>{v.unit || "—"}</td>
                              <td style={{ padding: "9px 12px", textAlign: "center", color: "#94A3B8", fontSize: 12, verticalAlign: "middle" }}>
                                {editMode ? (() => {
                                  // Titre format — show read-only cutoff, not editable min/max inputs
                                  const isTitreRef = v.refText && /^\s*1\s*[:/]\s*\d+\s*$/.test(v.refText.trim());
                                  if (isTitreRef) {
                                    return (
                                      <div style={{ fontSize: 12, color: "#475569", fontWeight: 500 }}>
                                        {"< " + v.refText.trim()}
                                      </div>
                                    );
                                  }
                                  return (
                                  <div style={{ display: "flex", alignItems: "center", gap: 3, justifyContent: "center" }}>
                                    <input className="input" value={v.refMin ?? ""} onChange={e => updateEdit(i, "refMin", e.target.value)}
                                      style={{ width: 52, padding: "3px 5px", textAlign: "center", fontSize: 12 }} placeholder="min" />
                                    <span style={{ color: "#CBD5E1", fontSize: 11 }}>–</span>
                                    <input className="input" value={v.refMax ?? ""} onChange={e => updateEdit(i, "refMax", e.target.value)}
                                      style={{ width: 52, padding: "3px 5px", textAlign: "center", fontSize: 12 }} placeholder="max" />
                                  </div>
                                  );
                                })() : (() => {
                                  // View mode ref column — keep original trigger logic, improve tooltip content
                                  const isTitreRef = v.refText && /^\s*1\s*[:/]\s*\d+\s*$/.test(v.refText.trim());
                                  if (isTitreRef) {
                                    const tipContent = buildRefTooltip(null, v.refMax, v.refText, v.unit);
                                    return (
                                      <div className="ref-tip-wrap">
                                        <span style={{ fontSize: 12, color: "#475569", fontWeight: 500, cursor: "help", borderBottom: "1px dashed #CBD5E1" }}>{"< " + v.refText.trim()}</span>
                                        <div className="ref-tip-box" style={{ maxHeight: "220px", overflowY: "auto" }}>{tipContent}</div>
                                      </div>
                                    );
                                  }
                                  const hasMultiple = isMultiRange(v.refText);
                                  if (hasMultiple) {
                                    const tipContent = buildRefTooltip(v.refMin, v.refMax, v.refText, v.unit);
                                    return (
                                      <div className="ref-tip-wrap">
                                        <span style={{ fontSize: 11, color: "#4F46E5", borderBottom: "1px dashed #4F46E5", cursor: "help", whiteSpace: "nowrap" }}>
                                          Multiple ranges ↗
                                        </span>
                                        <div className="ref-tip-box" style={{ maxHeight: "220px", overflowY: "auto" }}>{tipContent}</div>
                                      </div>
                                    );
                                  }
                                  // Simple range — plain text, no underline
                                  return <span style={{ fontSize: 11, color: "#94A3B8" }}>
                                    {v.refMin != null && v.refMax != null ? `${v.refMin} – ${v.refMax}` : v.refText || "—"}
                                  </span>;
                                })()}
                              </td>
                              <td style={{ padding: "9px 12px", textAlign: "center", verticalAlign: "middle" }}>
                                {editMode ? (
                                  <select value={v.status || "Needs Review"} onChange={e => updateEdit(i, "status", e.target.value)}
                                    style={{ fontSize: 11, padding: "3px 6px", borderRadius: 5, border: "1px solid #E2E8F0",
                                      background: v.status === "Normal" ? "#DCFCE7" : ["High","Abnormal"].includes(v.status) ? "#FEF3C7" :
                                        v.status === "Low" ? "#DBEAFE" : v.status?.includes("Critical") ? "#FEE2E2" : "#F1F5F9",
                                      color: v.status === "Normal" ? "#166534" : ["High","Abnormal"].includes(v.status) ? "#92400E" :
                                        v.status === "Low" ? "#1E40AF" : v.status?.includes("Critical") ? "#991B1B" : "#475569",
                                      fontWeight: 600, cursor: "pointer", width: "100%" }}>
                                    {["Normal","High","Low","Critical High","Critical Low","Abnormal","Needs Review","TNP"].map(s => (
                                      <option key={s} value={s}>{s}</option>
                                    ))}
                                  </select>
                                ) : (
                                  <span style={{ fontSize: 11, fontWeight: 500, padding: "2px 8px", borderRadius: 99, display: "inline-block", background: badgeBg, color: badgeColor }}>{badgeText}</span>
                                )}
                              </td>
                            </>
                          )}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                );
              })() : null}
            </div>
          </div>
        </div>
      )}

      {/* ── TREND CHART ──────────────────────────────────────────────────────── */}
      {view === "trend" && selType && (
        <div>
          <RangeBar />

          {/* Parameter selector */}
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 14 }}>
            {allParams.map(p => (
              <button key={p} onClick={() => {
                setTP(p); setStore('param', p);
                if (chartView === "table") {
                  setHighlightedParam(p);
                  setTimeout(() => {
                    rowRefs.current[p]?.scrollIntoView({ behavior: "smooth", block: "center" });
                  }, 50);
                  setTimeout(() => setHighlightedParam(null), 2000);
                }
                // In chart view: setTP(p) above is enough, chart re-renders automatically
              }} style={{
                padding: "5px 14px", borderRadius: 99, fontSize: 12, cursor: "pointer", border: "1px solid",
                background: activeTP === p ? meta.color : "#fff",
                borderColor: activeTP === p ? meta.color : "#E4E8F0",
                color: activeTP === p ? "#fff" : "#475569",
                fontWeight: activeTP === p ? 700 : 400,
              }}>{p}</button>
            ))}
          </div>

          {/* View toggle */}
          <div style={{ display: "flex", gap: 6, marginBottom: 16 }}>
            {["chart", "table"].map(v => (
              <button key={v} onClick={() => setChartView(v)} style={{
                padding: "5px 16px", borderRadius: 6, fontSize: 12, cursor: "pointer", border: "1px solid",
                background: chartView === v ? "#1E293B" : "#fff",
                borderColor: chartView === v ? "#1E293B" : "#E4E8F0",
                color: chartView === v ? "#fff" : "#475569",
                fontWeight: 600,
              }}>{v === "chart" ? "Chart View" : "Flowsheet / Table"}</button>
            ))}
          </div>

          {(fetchingTrend || trendType !== selType) ? (
            <div className="card">
              <div className="empty-state">
                <div style={{ width:40, height:40, borderRadius:"50%", border:"3px solid #E2E8F0", borderTopColor:"#4F46E5", animation:"spin 1s linear infinite", margin:"0 auto 16px" }}/>
                <h3>Loading trend data…</h3>
                <p>Fetching all historical reports.</p>
                <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
              </div>
            </div>
          ) : filtered.length === 0 ? (
            <div className="card">
              <div className="empty-state">
                {/* Fix 3: Better empty state — distinguish loading vs truly empty */}
                {fetchingPatient || !fullPatient ? (
                  <div style={{ display:"flex", flexDirection:"column", alignItems:"center", gap:12, padding:"40px 0" }}>
                    <div style={{ width:32, height:32, borderRadius:"50%", border:"3px solid #E2E8F0", borderTopColor:"#4F46E5", animation:"spin 0.8s linear infinite" }}/>
                    <div style={{ fontSize:14, color:"#64748B", fontWeight:500 }}>Loading reports…</div>
                  </div>
                ) : (
                  <>
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M9 19v-6a2 2 0 0 0-2-2H5a2 2 0 0 0-2 2v6a2 2 0 0 0 2 2h2a2 2 0 0 0 2-2zm0 0V9a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v10m-6 0a2 2 0 0 0 2 2h2a2 2 0 0 0 2-2m0 0V5a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v14a2 2 0 0 0-2 2h-2a2 2 0 0 1-2-2z"/></svg>
                    <h3>No data in this date range</h3>
                    <p>Try selecting <strong>All</strong> or a wider range, or upload more reports for this parameter.</p>
                  </>
                )}
              </div>
            </div>
          ) : chartView === "chart" ? (
            <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr", gap: 16 }}>
              {/* Chart card */}
              <div className="card">
                <div className="card-header">
                  <div>
                    <div style={{ fontWeight: 700, fontSize: 15 }}>{activeTP || "Select a parameter"}</div>
                    <div style={{ fontSize: 12, color: "#64748B" }}>
                      Oldest → Newest (left to right) · {filtered.length} data points
                    </div>
                  </div>
                </div>
                <div style={{ padding: "8px 12px 16px" }}>
                  {activeTP ? (
                    <ClinicalTrendChart
                      data={trendData}
                      refMin={refForParam.refMin}
                      refMax={refForParam.refMax}
                      refText={refForParam.refText || ""}
                      unit={refForParam.unit}
                      color={meta.color}
                      paramName={activeTP}
                    />
                  ) : (
                    <div style={{ padding: "32px 0", textAlign: "center", color: "#94A3B8" }}>
                      Select a parameter above to view the trend
                    </div>
                  )}
                </div>
                {/* Upload/Edit audit strip */}
                {activeTP && trendData.length > 0 && (() => {
                  const latestRep = [...filtered].sort((a,b) => {
                    const toMs = s => s ? new Date((s||"").replace(/(\.\d{3})\d+/, '$1')).getTime() : 0;
                    const dateDiff = new Date(b.date)-new Date(a.date);
                    if (dateDiff !== 0) return dateDiff;
                    return toMs(b.createdAt)-toMs(a.createdAt);
                  })[0];
                  if (!latestRep?.uploadedBy && !latestRep?.editedBy) return null;
                  return (
                    <div style={{ display:"flex", gap:16, padding:"7px 14px", background:"#F8FAFC", borderTop:"1px solid #F1F5F9", fontSize:12, flexWrap:"wrap" }}>
                      {latestRep.uploadedBy && (
                        <span style={{ color:"#64748B" }}>
                          <span style={{ display:"inline-block", width:7, height:7, borderRadius:"50%", background:"#16A34A", marginRight:5 }}/>
                          Uploaded by <b style={{ color:"#0F172A" }}>{latestRep.uploadedBy}</b>
                        </span>
                      )}
                      {latestRep.editedBy && (
                        <span style={{ color:"#64748B" }}>
                          <span style={{ display:"inline-block", width:7, height:7, borderRadius:"50%", background:"#185FA5", marginRight:5 }}/>
                          Edited by <b style={{ color:"#0F172A" }}>{latestRep.editedBy}</b>
                          {latestRep.editedAt && <span> · {latestRep.editedAt.substring(0,10)}</span>}
                        </span>
                      )}
                    </div>
                  );
                })()}
              </div>

              {/* Result history sidebar */}
              <div className="card">
                <div className="card-header">
                  <div style={{ fontWeight: 700, fontSize: 14 }}>
                    Result History
                    {activeTP && <span style={{ fontSize: 12, fontWeight: 400, color: "#94A3B8", marginLeft: 6 }}>
                      {activeTP}
                    </span>}
                  </div>
                </div>
                <div style={{ padding: "8px 4px", maxHeight: 440, overflowY: "auto" }}>
                  {activeTP ? (
                    <ResultHistory
                      data={trendData}
                      unit={refForParam.unit}
                      color={meta.color}
                    />
                  ) : (
                    <div style={{ textAlign: "center", color: "#94A3B8", padding: 24, fontSize: 13 }}>
                      Select a parameter
                    </div>
                  )}
                </div>
              </div>
            </div>
          ) : (
            /* Flowsheet / table — dual-axis sticky matrix */
            <div className="card">
              <div className="card-header">
                <div style={{ fontWeight: 700, fontSize: 14 }}>Flowsheet — All parameters</div>
                <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                  <div style={{ fontSize: 12, color: "#94A3B8" }}>{filtered.length} reports · newest → oldest</div>
                  <div style={{ display: "flex", gap: 8, fontSize: 11, color: "#64748B" }}>
                    {[["H","#FEE2E2","#A32D2D"],["L","#DBEAFE","#1D4ED8"],["CH","#A32D2D","#fff"],["CL","#5B21B6","#fff"],["A","#FEF3C7","#854F0B"]].map(([lbl,bg,col])=>(
                      <span key={lbl} style={{ display:"inline-flex",alignItems:"center",gap:3 }}>
                        <span style={{ fontSize:9,fontWeight:600,padding:"0 3px",borderRadius:3,background:bg,color:col }}>{lbl}</span>
                        <span style={{ color:"#94A3B8" }}>{lbl==="H"?"High":lbl==="L"?"Low":lbl==="CH"?"Crit H":lbl==="CL"?"Crit L":"Abnormal"}</span>
                      </span>
                    ))}
                    <span style={{ display:"inline-flex",alignItems:"center",gap:3 }}>
                      <span style={{ fontSize:9,fontStyle:"italic",color:"#94A3B8" }}>—</span>
                      <span style={{ color:"#94A3B8" }}>TNP</span>
                    </span>
                  </div>
                </div>
              </div>
              <div className="flowsheet-outer">
                <table className="flowsheet-table">
                  <thead>
                    <tr>
                      <th className="fs-corner">
                        <div style={{ fontSize: 10, fontWeight: 500, color: "#64748B", textTransform: "uppercase", letterSpacing: "0.05em" }}>Parameter</div>
                        <div style={{ fontSize: 9, color: "#94A3B8", marginTop: 1 }}>Ref range · unit</div>
                      </th>
                      {[...filtered].sort((a, b) => {
                        const toMs = s => s ? new Date((s||"").replace(/(\.\d{3})\d+/, '$1')).getTime() : 0;
                        const dateDiff = new Date(b.date) - new Date(a.date);
                        if (dateDiff !== 0) return dateDiff;
                        return toMs(b.createdAt) - toMs(a.createdAt);
                      }).map((r, ri) => (
                        <th key={r.id} className={ri === 0 ? "fs-date-latest" : "fs-date"}>
                          {ri === 0 && <div style={{ fontSize: 9, color: "#1D4ED8", textTransform: "uppercase", letterSpacing: "0.04em", marginBottom: 2 }}>Latest</div>}
                          <div style={{ fontSize: 11, fontWeight: 600, color: ri === 0 ? "#1D4ED8" : "#334155" }}>
                            {formatDate(r.date)}
                          </div>
                          {r.editedBy && <div style={{ fontSize: 9, color: "#185FA5", background: "#E6F1FB", borderRadius: 3, padding: "0 4px", marginTop: 2, display: "inline-block" }}>edited</div>}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {allParams.map((param) => {
                      const toMs = s => s ? new Date((s||"").replace(/(\.\d{3})\d+/, '$1')).getTime() : 0;
                      const sortedFiltered = [...filtered].sort((a, b) => {
                        const dd = new Date(b.date) - new Date(a.date);
                        if (dd !== 0) return dd;
                        return toMs(b.createdAt) - toMs(a.createdAt);
                      });
                      const refV = sortedFiltered.flatMap(r => r.values).find(v => v.name === param);
                      const isHighlighted = highlightedParam === param;
                      const allVals = sortedFiltered.map(r => r.values.find(vv => vv.name === param)).filter(Boolean);
                      const hasCritical = allVals.some(v => v.status === "Critical High" || v.status === "Critical Low");
                      const hasAbnormal = !hasCritical && allVals.some(v => v.status && v.status !== "Normal" && v.status !== "TNP");
                      const accentBorder = hasCritical ? "3px solid #DC2626" : hasAbnormal ? "3px solid #F59E0B" : "3px solid transparent";
                      const isQual = refV?.value === null || refV?.value === undefined;
                      const hasRefRange = refV?.refMin != null && refV?.refMax != null;
                      return (
                        <tr key={param} ref={el => rowRefs.current[param] = el} className={isHighlighted ? "highlighted" : ""}>
                          <td className="fs-param" style={{ borderLeft: accentBorder }}>
                            <div style={{ fontWeight: 500, fontSize: 12, color: "#0F172A", lineHeight: 1.3, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: 180 }} title={param}>{param}</div>
                            {isQual ? (
                              <div style={{ fontSize: 10, color: "#94A3B8", marginTop: 1 }}>Qualitative</div>
                            ) : (
                              <div style={{ fontSize: 10, color: "#94A3B8", marginTop: 1 }}>
                                {hasRefRange ? (refV.refText && /^\s*1\s*[:/]\s*\d+\s*$/.test(refV.refText.trim()) ? ('< ' + refV.refText.trim()) : `${refV.refMin}–${refV.refMax}`) : ""}
                                {refV?.unit ? ` · ${refV.unit}` : ""}
                              </div>
                            )}
                          </td>
                          {sortedFiltered.map((r, ri) => {
                            const v = r.values.find(vv => vv.name === param);
                            const isCritH = v?.status === "Critical High";
                            const isCritL = v?.status === "Critical Low";
                            const isHigh  = v?.status === "High";
                            const isLow   = v?.status === "Low";
                            const isAbn   = v?.status === "Abnormal";
                            const isNR    = v?.status === "Needs Review";
                            const isTNP   = v?.status === "TNP";
                            const cellCls = ri === 0
                              ? (isCritH ? "fs-val-crith" : isCritL ? "fs-val-critl" : isHigh || isAbn ? "fs-val-abn" : "fs-val-latest")
                              : (isCritH ? "fs-val-crith" : isCritL ? "fs-val-critl" : isHigh || isAbn ? "fs-val-abn" : "fs-val");
                            const valColor = isCritH ? "#A32D2D" : isCritL ? "#5B21B6" : isHigh || isAbn ? "#E24B4A" : isLow ? "#1D4ED8" : isNR ? "#64748B" : "#0F172A";
                            const badge = isCritH ? <span className="fs-badge fs-badge-ch">CH</span>
                                        : isCritL ? <span className="fs-badge fs-badge-cl">CL</span>
                                        : isHigh  ? <span className="fs-badge fs-badge-h">H</span>
                                        : isLow   ? <span className="fs-badge fs-badge-l">L</span>
                                        : isAbn   ? <span className="fs-badge fs-badge-a">A</span>
                                        : isNR    ? <span className="fs-badge fs-badge-nr">?</span>
                                        : null;
                            return (
                              <td key={r.id} className={cellCls}>
                                {v ? (
                                  isTNP ? (
                                    <span style={{ color: "#CBD5E1", fontSize: 11, fontStyle: "italic" }}>—</span>
                                  ) : (() => {
                                    const dv = v.valueText || (v.value !== null && v.value !== undefined ? String(v.value) : null);
                                    const isLongText = typeof dv === "string" && dv.length > 18;
                                    return (
                                      <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 1 }}>
                                        <div style={{ fontWeight: isCritH || isCritL ? 700 : isHigh || isLow || isAbn ? 600 : 500, fontSize: 13, color: valColor, lineHeight: 1.2, maxWidth: 90, width: "100%" }}>
                                          {isLongText ? (
                                            <span className="ref-tip-wrap" style={{ display: "block" }}>
                                              <span className="fs-qual-text" title={dv} style={{ color: valColor }}>{dv}</span>
                                              <div className="ref-tip-box" style={{ fontWeight: 400, fontSize: 11, maxWidth: 240, whiteSpace: "normal", textAlign: "left" }}>{dv}</div>
                                            </span>
                                          ) : (
                                            <span style={{ display: "block", textAlign: "center" }}>{dv ?? "—"}</span>
                                          )}
                                        </div>
                                        {badge}
                                      </div>
                                    );
                                  })()
                                ) : (
                                  <span style={{ color: "#E2E8F0", fontSize: 12 }}>—</span>
                                )}
                              </td>
                            );
                          })}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── MEDICATIONS VIEW */}
      {view === "medications" && (() => {
        const toTitleCase=(str)=>str?str.trim().split(/\s+/).map(w=>w.charAt(0).toUpperCase()+w.slice(1).toLowerCase()).join(" "):str;
        const formatMedDate=(iso)=>{if(!iso)return"—";return new Date(iso).toLocaleDateString("en-GB",{day:"numeric",month:"short",year:"numeric"});};
        const renderInstructions=(txt)=>{
          if(!txt)return <span style={{ color:"#94A3B8" }}>—</span>;
          if(txt.startsWith("Custom: ")){const custom=txt.slice(8);return(<><div style={{ fontWeight:500,color:"#334155",fontSize:13 }}>{custom||"—"}</div><div style={{ fontSize:10,color:"#8B5CF6",marginTop:2,textTransform:"uppercase",letterSpacing:".05em" }}>Custom / PRN</div></>);}
          if(txt.includes(" — ")){const[freq,rt]=txt.split(" — ");return(<><div style={{ fontWeight:500,color:"#334155",fontSize:13 }}>{freq}</div><div style={{ fontSize:11,color:"#94A3B8",marginTop:2 }}>⊙ {rt}</div></>);}
          return(<><div style={{ fontWeight:500,color:"#334155",fontSize:13 }}>{txt}</div><div style={{ fontSize:10,color:"#94A3B8",marginTop:2,textTransform:"uppercase",letterSpacing:".05em" }}>Legacy record</div></>);
        };
        const activeMeds=medications.filter(m=>!m.stopped_at);
        const pastMeds=medications.filter(m=>m.stopped_at);
        const canManage=["admin","doctor","technician"].includes(user?.role);
        const sevBorder=(s)=>s==="Severe"?"#FCA5A5":s==="Moderate"?"#FCD34D":"#CBD5E1";
        const sevReaction=(s)=>s==="Severe"?{background:"#FEE2E2",color:"#991B1B"}:s==="Moderate"?{background:"#FEF3C7",color:"#92400E"}:{background:"#F1F5F9",color:"#475569"};
        const nameBorder=(s)=>s==="Severe"?"#FCA5A5":s==="Moderate"?"#FDE68A":"#E2E8F0";
        const inputSt={padding:"8px 11px",borderRadius:7,border:"1px solid #CBD5E1",fontSize:13,width:"100%",boxSizing:"border-box",outline:"none",fontFamily:"inherit",background:"#fff"};
        const doAdd=(drug,dose_,instr)=>{setAddingMed(true);(()=>{medicationAPI.add(patientId,{drug_name:drug,dose:dose_,instructions:instr}).then(med=>{setMedications(prev=>[med,...prev]);setDrugName("");setDose("");setRoute("PO (By Mouth)");setFrequency("1 + 0 + 1 (Morning & Night)");}).catch(()=>alert("Failed")).finally(()=>setAddingMed(false));})();};
        const handleAddMed=()=>{
          if(!drugName.trim())return;
          const n=toTitleCase(drugName);const ci=`${frequency} — ${route}`;
          const am=allergies.find(a=>a.allergen.toLowerCase().includes(n.toLowerCase())||n.toLowerCase().includes(a.allergen.toLowerCase()));
          if(am){if(am.severity==="Severe"){alert(`SEVERE ALLERGY BLOCK\n\n${patient.name} has a severe allergy to ${am.allergen}.\n\nCannot add.`);return;}if(!window.confirm(`Allergy Warning\n\n${patient.name} has ${am.severity} allergy to ${am.allergen}.\n\nPrescribe ${n} anyway?`))return;}
          const match=pastMeds.find(m=>m.stop_reason&&!m.ack_warning&&(m.drug_name.toLowerCase().includes(n.toLowerCase())||n.toLowerCase().includes(m.drug_name.toLowerCase())));
          if(match){setWarnModal({matchedPast:match,pendingDrug:n,pendingDose:dose,pendingInstructions:ci});return;}
          doAdd(n,dose,ci);
        };
        const handleWarnAddAnyway=()=>{if(!warnModal)return;const{matchedPast,pendingDrug,pendingDose,pendingInstructions}=warnModal;setWarnModal(null);(()=>{medicationAPI.acknowledge(patientId,matchedPast.id).then(()=>setMedications(prev=>prev.map(m=>m.id===matchedPast.id?{...m,ack_warning:1}:m))).catch(()=>{});})();doAdd(pendingDrug,pendingDose,pendingInstructions);};
        const handleStop=()=>{if(!stopModal)return;setStoppingMed(true);(()=>{medicationAPI.stop(patientId,stopModal.med.id,stopReason).then(u=>{setMedications(prev=>prev.map(m=>m.id===u.id?u:m));setStopModal(null);setStopReason("");invalidateSummaryCache();}).catch(()=>alert("Failed")).finally(()=>setStoppingMed(false));})();};
        const handleDelete=(medId)=>{if(!window.confirm("Delete as typo?"))return;(()=>{medicationAPI.delete(patientId,medId).then(()=>setMedications(prev=>prev.filter(m=>m.id!==medId))).catch(()=>alert("Failed"));})();};
        const handleAddAllergy=()=>{if(!allergen.trim())return;setAddingAllergy(true);(()=>{allergyAPI.add(patientId,{allergen:allergen.trim(),reaction,severity}).then(a=>{setAllergies(prev=>[a,...prev]);setAllergen("");setReaction("");setSeverity("Mild");setShowAllergyForm(false);invalidateSummaryCache();}).catch(()=>alert("Failed")).finally(()=>setAddingAllergy(false));})();};
        const handleDeleteAllergy=(id)=>{if(!window.confirm("Remove allergy?"))return;(()=>{allergyAPI.delete(patientId,id).then(()=>setAllergies(prev=>prev.filter(a=>a.id!==id))).catch(()=>alert("Failed"));})();};
        const handlePrintMeds=()=>{const rows=activeMeds.map(m=>`<tr><td style="padding:8px 12px;font-weight:600">${toTitleCase(m.drug_name)}</td><td style="padding:8px 12px">${m.dose||"—"}</td><td style="padding:8px 12px">${m.instructions||"—"}</td><td style="padding:8px 12px">${m.added_by||"—"}</td></tr>`).join("");const html=`<!DOCTYPE html><html><body><h2>Medications — ${patient.name}</h2><table border="1" style="width:100%;border-collapse:collapse">${rows}</table></body></html>`;const w=window.open("","_blank");w.document.write(html);w.document.close();setTimeout(()=>w.print(),300);};
        return (
          <div style={{ display:"flex",flexDirection:"column",gap:16 }}>
            <div style={{ background:"rgba(254,242,242,0.5)",borderRadius:12,border:"2px solid #FECACA",overflow:"hidden" }}>
              <div style={{ padding:"12px 18px",borderBottom:"1px solid #FECACA",display:"flex",alignItems:"center",justifyContent:"space-between",background:"#fff" }}>
                <div style={{ display:"flex",alignItems:"center",gap:10 }}>
                  <div style={{ background:"#FEE2E2",width:32,height:32,borderRadius:6,display:"flex",alignItems:"center",justifyContent:"center" }}><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#DC2626" strokeWidth="2"><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg></div>
                  <span style={{ fontWeight:700,color:"#7F1D1D",fontSize:14 }}>Allergies & adverse reactions</span>
                  {allergies.length===0&&<span style={{ background:"#DCFCE7",color:"#166534",border:"1px solid #BBF7D0",fontSize:11,fontWeight:600,padding:"2px 10px",borderRadius:99 }}>✓ NKDA</span>}
                </div>
                {canManage&&<button onClick={()=>setShowAllergyForm(p=>!p)} style={{ background:"#FEE2E2",color:"#B91C1C",border:"1px solid #FECACA",borderRadius:6,padding:"5px 12px",fontSize:12,fontWeight:600,cursor:"pointer" }}>{showAllergyForm?"× Cancel":"+ Add allergy"}</button>}
              </div>
              <div style={{ padding:"14px 18px",display:"flex",flexWrap:"wrap",gap:10,minHeight:52,background:"rgba(254,242,242,0.15)" }}>
                {allergies.length===0&&!showAllergyForm&&<span style={{ fontSize:13,color:"#94A3B8" }}>No allergies recorded.</span>}
                {allergies.map(a=>(
                  <div key={a.id} style={{ display:"inline-flex",alignItems:"center",background:"#fff",border:`1px solid ${sevBorder(a.severity)}`,borderRadius:6,overflow:"hidden",boxShadow:"0 1px 3px rgba(0,0,0,.06)" }}>
                    <div style={{ padding:"6px 12px",fontWeight:700,color:"#1E293B",fontSize:13,borderRight:`1px solid ${nameBorder(a.severity)}` }}>{toTitleCase(a.allergen)}</div>
                    <div style={{ padding:"6px 10px",fontSize:11,fontWeight:600,display:"flex",alignItems:"center",gap:4,...sevReaction(a.severity) }}>{a.severity==="Severe"&&"⚠ "}{a.reaction||a.severity}</div>
                    {canManage&&<button onClick={()=>handleDeleteAllergy(a.id)} style={{ padding:"6px 8px",background:"#fff",border:"none",cursor:"pointer",color:"#CBD5E1",fontSize:14 }} onMouseEnter={e=>{e.currentTarget.style.color="#DC2626";e.currentTarget.style.background="#FEF2F2";}} onMouseLeave={e=>{e.currentTarget.style.color="#CBD5E1";e.currentTarget.style.background="#fff";}}>×</button>}
                  </div>
                ))}
              </div>
              {showAllergyForm&&canManage&&(
                <div style={{ padding:"12px 18px",borderTop:"1px solid #FECACA",background:"#FFF5F5" }}>
                  <div style={{ display:"grid",gridTemplateColumns:"1fr 1fr 130px 80px",gap:8,alignItems:"end" }}>
                    <div><div style={{ fontSize:10,fontWeight:600,color:"#991B1B",marginBottom:4,textTransform:"uppercase" }}>Allergen *</div><input style={{...inputSt,borderColor:"#FECACA"}} placeholder="e.g. Penicillin" value={allergen} onChange={e=>setAllergen(e.target.value)}/></div>
                    <div><div style={{ fontSize:10,fontWeight:600,color:"#64748B",marginBottom:4,textTransform:"uppercase" }}>Reaction</div><input style={inputSt} placeholder="e.g. Anaphylaxis" value={reaction} onChange={e=>setReaction(e.target.value)}/></div>
                    <div><div style={{ fontSize:10,fontWeight:600,color:"#64748B",marginBottom:4,textTransform:"uppercase" }}>Severity</div><select style={{...inputSt,cursor:"pointer"}} value={severity} onChange={e=>setSeverity(e.target.value)}><option>Mild</option><option>Moderate</option><option>Severe</option></select></div>
                    <button onClick={handleAddAllergy} disabled={addingAllergy||!allergen.trim()} style={{ padding:"8px 0",borderRadius:7,border:"none",background:allergen.trim()?"#DC2626":"#E2E8F0",color:allergen.trim()?"#fff":"#94A3B8",fontWeight:700,fontSize:13,cursor:allergen.trim()?"pointer":"not-allowed",width:"100%" }}>{addingAllergy?"Saving…":"Save"}</button>
                  </div>
                </div>
              )}
            </div>
            {canManage&&(
              <div style={{ background:"#fff",borderRadius:12,border:"1px solid #E2E8F0",overflow:"hidden" }}>
                <div style={{ background:"#F8FAFC",borderBottom:"1px solid #E2E8F0",padding:"10px 18px",display:"flex",alignItems:"center",gap:8 }}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#185FA5" strokeWidth="1.8"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="9" y1="13" x2="15" y2="13"/></svg>
                  <span style={{ fontWeight:600,color:"#1E293B",fontSize:13 }}>Prescribe new medication</span>
                </div>
                <div style={{ padding:"16px 18px" }}>
                  <div style={{ display:"grid",gridTemplateColumns:"3fr 2fr 2fr 3fr auto",gap:10,alignItems:"end" }}>
                    <div><div style={{ fontSize:10,fontWeight:600,color:"#64748B",marginBottom:5,textTransform:"uppercase",letterSpacing:".06em" }}>Drug Name *</div>
                    <input style={{...inputSt,borderColor:drugFieldFocused?"#93C5FD":"#CBD5E1",background:drugFieldFocused?"#EFF6FF":"#fff"}} placeholder="e.g. Napa Extend" value={drugName} onChange={e=>setDrugName(e.target.value)} onFocus={()=>setDrugFieldFocused(true)} onBlur={()=>setDrugFieldFocused(false)} onKeyDown={e=>e.key==="Enter"&&handleAddMed()}/></div>
                    <div><div style={{ fontSize:10,fontWeight:600,color:"#64748B",marginBottom:5,textTransform:"uppercase",letterSpacing:".06em" }}>Dose</div><input style={inputSt} placeholder="e.g. 500mg" value={dose} onChange={e=>setDose(e.target.value)} onKeyDown={e=>e.key==="Enter"&&handleAddMed()}/></div>
                    <div><div style={{ fontSize:10,fontWeight:600,color:"#64748B",marginBottom:5,textTransform:"uppercase",letterSpacing:".06em" }}>Route</div><select style={{...inputSt,cursor:"pointer"}} value={route} onChange={e=>setRoute(e.target.value)}><option>PO (By Mouth)</option><option>IV (Intravenous)</option><option>IM (Intramuscular)</option><option>Topical</option><option>Sublingual</option></select></div>
                    <div><div style={{ fontSize:10,fontWeight:600,color:"#64748B",marginBottom:5,textTransform:"uppercase",letterSpacing:".06em" }}>Frequency (Sig)</div>
                    <select style={{...inputSt,cursor:"pointer"}} value={frequency==="Custom / PRN"||frequency.startsWith("Custom:")?"Custom / PRN":frequency} onChange={e=>{ if(e.target.value==="Custom / PRN"){setFrequency("Custom: ");}else{setFrequency(e.target.value);} }}><option>1 + 0 + 1 (Morning & Night)</option><option>1 + 1 + 1 (Three times daily)</option><option>0 + 0 + 1 (Night only)</option><option>1 + 0 + 0 (Morning only)</option><option>SOS / As needed</option><option>Custom / PRN</option></select>
                    {(frequency==="Custom / PRN"||frequency.startsWith("Custom:"))&&<input style={{...inputSt,marginTop:6,borderColor:"#93C5FD",background:"#EFF6FF"}} placeholder="e.g. Every 8 hours after meals" value={frequency.startsWith("Custom:")?frequency.slice(8):""} onChange={e=>setFrequency("Custom: "+e.target.value)} autoFocus/>}
                    </div>
                    <button onClick={handleAddMed} disabled={addingMed||!drugName.trim()} style={{ padding:"8px 20px",borderRadius:7,border:"none",background:drugName.trim()?"#059669":"#E2E8F0",color:drugName.trim()?"#fff":"#94A3B8",fontWeight:700,fontSize:13,cursor:drugName.trim()?"pointer":"not-allowed",whiteSpace:"nowrap",height:38 }}>{addingMed?"Adding…":"+ Add"}</button>
                  </div>
                </div>
              </div>
            )}
            <div style={{ background:"#fff",borderRadius:12,border:"1px solid #E2E8F0",overflow:"hidden" }}>
              <div style={{ padding:"14px 18px",borderBottom:"1px solid #E2E8F0",display:"flex",alignItems:"center",justifyContent:"space-between" }}>
                <div style={{ display:"flex",alignItems:"center",gap:10 }}><span style={{ fontWeight:700,fontSize:15,color:"#1E293B" }}>Current medications</span><span style={{ background:"#DCFCE7",color:"#166534",fontSize:11,fontWeight:700,padding:"2px 10px",borderRadius:99 }}>{activeMeds.length} Active</span></div>
                {activeMeds.length>0&&<button onClick={handlePrintMeds} style={{ background:"#F8FAFC",border:"1px solid #E2E8F0",borderRadius:6,padding:"5px 12px",fontSize:12,color:"#64748B",cursor:"pointer" }}>🖨 Print Rx</button>}
              </div>
              {medsLoading?<div style={{ padding:24,textAlign:"center",color:"#94A3B8",fontSize:13 }}>Loading...</div>
              :activeMeds.length===0?<div style={{ padding:"24px 18px",textAlign:"center",color:"#94A3B8",fontSize:13 }}>No active medications.{canManage?" Use the form above to prescribe.":""}</div>
              :<div style={{ overflowX:"auto" }}><table style={{ width:"100%",borderCollapse:"collapse" }}>
                <thead><tr style={{ background:"#F8FAFC",borderBottom:"1px solid #E2E8F0" }}>
                  <th style={{ padding:"9px 16px",textAlign:"left",fontSize:11,color:"#64748B",fontWeight:600,textTransform:"uppercase",letterSpacing:".05em" }}>Drug name & dose</th>
                  <th style={{ padding:"9px 16px",textAlign:"left",fontSize:11,color:"#64748B",fontWeight:600,textTransform:"uppercase",letterSpacing:".05em" }}>Instructions (Sig)</th>
                  <th style={{ padding:"9px 16px",textAlign:"left",fontSize:11,color:"#64748B",fontWeight:600,textTransform:"uppercase",letterSpacing:".05em" }}>Prescribed</th>
                  <th style={{ padding:"9px 16px",textAlign:"left",fontSize:11,color:"#64748B",fontWeight:600,textTransform:"uppercase",letterSpacing:".05em" }}>Added by</th>
                  <th style={{ padding:"9px 16px",textAlign:"right",fontSize:11,color:"#64748B",fontWeight:600,textTransform:"uppercase",letterSpacing:".05em" }}>Actions</th>
                </tr></thead>
                <tbody>
                  {activeMeds.map(m=>(<tr key={m.id} style={{ borderLeft:"3px solid #10B981",borderBottom:"1px solid #F1F5F9" }} onMouseEnter={e=>e.currentTarget.style.background="#F8FAFC"} onMouseLeave={e=>e.currentTarget.style.background=""}>
                    <td style={{ padding:"12px 16px" }}><div style={{ fontWeight:700,fontSize:14,color:"#0F172A" }}>{toTitleCase(m.drug_name)}</div><div style={{ fontSize:12,color:"#64748B",marginTop:2 }}>{m.dose||"—"}</div></td>
                    <td style={{ padding:"12px 16px" }}>{renderInstructions(m.instructions)}</td>
                    <td style={{ padding:"12px 16px",color:"#64748B",fontSize:12,whiteSpace:"nowrap" }}>{formatMedDate(m.prescribed_at)}</td>
                    <td style={{ padding:"12px 16px" }}><div style={{ display:"flex",alignItems:"center",gap:7 }}><div style={{ width:26,height:26,borderRadius:"50%",background:"#E2E8F0",display:"flex",alignItems:"center",justifyContent:"center",fontSize:10,fontWeight:700,color:"#475569",flexShrink:0 }}>{(m.added_by||"?").slice(0,2).toUpperCase()}</div><span style={{ fontSize:13,color:"#475569" }}>{m.added_by||"—"}</span></div></td>
                    <td style={{ padding:"12px 16px",textAlign:"right",whiteSpace:"nowrap" }}>
                      <button onClick={()=>{setStopModal({med:m});setStopReason("");}} style={{ background:"#FEF9C3",color:"#854D0E",border:"1px solid #FDE68A",borderRadius:6,padding:"4px 14px",fontSize:12,fontWeight:700,cursor:"pointer",marginRight:6 }}>Stop</button>
                      <button onClick={()=>handleDelete(m.id)} style={{ background:"none",border:"none",color:"#CBD5E1",cursor:"pointer",fontSize:14,padding:"4px 6px" }} onMouseEnter={e=>e.currentTarget.style.color="#DC2626"} onMouseLeave={e=>e.currentTarget.style.color="#CBD5E1"}><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6M14 11v6"/><path d="M9 6V4h6v2"/></svg></button>
                    </td>
                  </tr>))}
                </tbody>
              </table></div>}
            </div>
            {pastMeds.length>0&&(
              <div style={{ background:"#fff",borderRadius:12,border:"1px solid #E2E8F0",overflow:"hidden",opacity:0.92 }}>
                <div style={{ background:"#F8FAFC",borderBottom:"1px solid #E2E8F0",padding:"10px 18px",display:"flex",alignItems:"center",gap:10 }}>
                  <span style={{ fontWeight:600,color:"#64748B",fontSize:13 }}>Past medications</span>
                  <span style={{ background:"#E2E8F0",color:"#475569",fontSize:11,fontWeight:700,padding:"1px 8px",borderRadius:99 }}>{pastMeds.length} Stopped</span>
                  <button onClick={()=>setHidePast(p=>!p)} style={{ marginLeft:"auto",background:"none",border:"none",color:"#94A3B8",fontSize:12,cursor:"pointer" }}>{hidePast?"Show":"Hide"}</button>
                </div>
                {!hidePast&&(<div style={{ overflowX:"auto" }}><table style={{ width:"100%",borderCollapse:"collapse" }}>
                  <thead><tr>
                    <th style={{ padding:"8px 16px",textAlign:"left",fontSize:11,color:"#94A3B8",fontWeight:600,textTransform:"uppercase",letterSpacing:".05em",borderBottom:"1px solid #F1F5F9" }}>Drug name & dose</th>
                    <th style={{ padding:"8px 16px",textAlign:"left",fontSize:11,color:"#94A3B8",fontWeight:600,textTransform:"uppercase",letterSpacing:".05em",borderBottom:"1px solid #F1F5F9" }}>Dates</th>
                    <th style={{ padding:"8px 16px",textAlign:"left",fontSize:11,color:"#94A3B8",fontWeight:600,textTransform:"uppercase",letterSpacing:".05em",borderBottom:"1px solid #F1F5F9" }}>Stopped by</th>
                    <th style={{ padding:"8px 16px",textAlign:"left",fontSize:11,color:"#94A3B8",fontWeight:600,textTransform:"uppercase",letterSpacing:".05em",borderBottom:"1px solid #F1F5F9" }}>Stop reason</th>
                  </tr></thead>
                  <tbody>
                    {pastMeds.map(m=>(<tr key={m.id} style={{ borderLeft:"3px solid #CBD5E1",borderBottom:"1px solid #F8FAFC" }} onMouseEnter={e=>e.currentTarget.style.background="#F8FAFC"} onMouseLeave={e=>e.currentTarget.style.background=""}>
                      <td style={{ padding:"10px 16px" }}><div style={{ fontWeight:500,color:"#64748B",textDecoration:"line-through",fontSize:13 }}>{toTitleCase(m.drug_name)}</div><div style={{ fontSize:11,color:"#94A3B8",marginTop:2 }}>{m.dose||"—"}</div></td>
                      <td style={{ padding:"10px 16px" }}><div style={{ fontSize:12,color:"#64748B" }}>Start: {formatMedDate(m.prescribed_at)}</div><div style={{ fontSize:12,color:"#DC2626",marginTop:2 }}>Stop: {formatMedDate(m.stopped_at)}</div></td>
                      <td style={{ padding:"10px 16px",fontSize:13,color:"#64748B" }}>{m.stopped_by||"—"}</td>
                      <td style={{ padding:"10px 16px" }}>{m.stop_reason?<span style={{ background:"#FEF2F2",color:"#DC2626",border:"1px solid #FECACA",padding:"3px 10px",borderRadius:4,fontSize:12,fontWeight:500,display:"inline-flex",alignItems:"center",gap:5 }}><svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="#DC2626" strokeWidth="2"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>{m.stop_reason}</span>:<span style={{ color:"#CBD5E1",fontSize:12 }}>—</span>}</td>
                    </tr>))}
                  </tbody>
                </table></div>)}
              </div>
            )}
            {stopModal&&(<div className="modal-overlay"><div className="modal" style={{ maxWidth:420 }}>
              <div className="modal-header"><div style={{ fontWeight:700 }}>Stop medication</div><button className="btn btn-ghost btn-sm" onClick={()=>setStopModal(null)}>✕</button></div>
              <div className="modal-body">
                <div style={{ marginBottom:14 }}><span style={{ fontWeight:600 }}>{toTitleCase(stopModal.med.drug_name)}</span>{stopModal.med.dose&&<span style={{ color:"#64748B",marginLeft:6 }}>{stopModal.med.dose}</span>}</div>
                <div style={{ fontSize:11,fontWeight:600,color:"#64748B",marginBottom:6,textTransform:"uppercase" }}>Reason for stopping *</div>
                <input style={{...inputSt,marginBottom:6}} placeholder="e.g. Completed course..." value={stopReason} onChange={e=>setStopReason(e.target.value)} onKeyDown={e=>e.key==="Enter"&&!stoppingMed&&stopReason.trim()&&handleStop()} autoFocus/>
                <div style={{ fontSize:11,color:"#94A3B8" }}>This reason will show if this drug is re-prescribed.</div>
              </div>
              <div className="modal-footer">
                <button className="btn btn-ghost" onClick={()=>setStopModal(null)}>Cancel</button>
                <button className="btn" disabled={stoppingMed||!stopReason.trim()} onClick={handleStop} style={{ background:!stopReason.trim()?"#E2E8F0":"#F59E0B",color:!stopReason.trim()?"#94A3B8":"#fff",border:"none" }}>{stoppingMed?"Stopping…":"Confirm stop"}</button>
              </div>
            </div></div>)}
            {warnModal&&(<div style={{ position:"fixed",inset:0,background:"rgba(15,23,42,0.6)",backdropFilter:"blur(4px)",zIndex:9999,display:"flex",alignItems:"center",justifyContent:"center",padding:16 }}>
              <div style={{ background:"#fff",borderRadius:12,boxShadow:"0 25px 60px rgba(0,0,0,0.3)",maxWidth:520,width:"100%",overflow:"hidden" }}>
                <div style={{ background:"#FEF2F2",borderBottom:"1px solid #FECACA",padding:"18px 20px",display:"flex",alignItems:"flex-start",gap:14 }}>
                  <div style={{ background:"#FEE2E2",width:40,height:40,borderRadius:"50%",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0 }}><svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#DC2626" strokeWidth="2"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg></div>
                  <div><div style={{ fontWeight:700,color:"#7F1D1D",fontSize:15,marginBottom:4 }}>Clinical Safety Warning</div><div style={{ fontSize:13,color:"#DC2626" }}>This medication was previously stopped for this patient.</div></div>
                </div>
                <div style={{ padding:"20px" }}>
                  <div style={{ background:"#F8FAFC",border:"1px solid #E2E8F0",borderRadius:8,padding:"14px 16px",marginBottom:14 }}>
                    <div style={{ display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:8 }}><span style={{ fontWeight:700,color:"#0F172A",fontSize:15 }}>{toTitleCase(warnModal.matchedPast.drug_name)}{warnModal.matchedPast.dose?` (${warnModal.matchedPast.dose})`:""}</span><span style={{ fontSize:11,color:"#64748B" }}>Stopped: {formatMedDate(warnModal.matchedPast.stopped_at)}</span></div>
                    <div style={{ fontSize:13 }}><span style={{ fontWeight:600,color:"#475569" }}>Reason: </span><span style={{ color:"#0F172A" }}>{warnModal.matchedPast.stop_reason}</span></div>
                  </div>
                  <div style={{ fontSize:12,color:"#94A3B8",fontStyle:"italic" }}>If you bypass this warning, it will be logged in the audit trail.</div>
                </div>
                <div style={{ background:"#F8FAFC",borderTop:"1px solid #E2E8F0",padding:"14px 20px",display:"flex",justifyContent:"flex-end",gap:10 }}>
                  <button onClick={()=>{setWarnModal(null);setDrugName("");setDose("");}} style={{ padding:"8px 18px",background:"#fff",border:"1px solid #CBD5E1",borderRadius:8,fontSize:13,fontWeight:500,color:"#475569",cursor:"pointer" }}>Cancel prescription</button>
                  <button onClick={handleWarnAddAnyway} style={{ padding:"8px 18px",background:"#DC2626",border:"none",borderRadius:8,fontSize:13,fontWeight:700,color:"#fff",cursor:"pointer" }}>Acknowledge & Add Anyway</button>
                </div>
              </div>
            </div>)}
          </div>
        );
      })()}

            {/* ── VITALS VIEW ─────────────────────────────────────────────────── */}
      {view === "vitals" && (() => {
        const canRecord = ["admin","doctor","technician","receptionist"].includes(user?.role);
        const latest = vitals[0] || null;
        const prev   = vitals[1] || null;

        const parseBP = (bp) => {
          if (!bp) return null;
          const parts = bp.split("/");
          if (parts.length !== 2) return null;
          return { sys: parseInt(parts[0]), dia: parseInt(parts[1]) };
        };
        const bpStatus = (bp) => {
          const parsed = parseBP(bp);
          if (!parsed) return "normal";
          if (parsed.sys >= 180 || parsed.dia >= 120) return "critical";
          if (parsed.sys >= 140 || parsed.dia >= 90)  return "high";
          return "normal";
        };
        const hrStatus = (hr) => !hr ? "normal" : (hr < 60 || hr > 100) ? "high" : "normal";
        const spo2Status = (s) => !s ? "normal" : s < 90 ? "critical" : s < 95 ? "high" : "normal";
        const tempStatus = (t) => !t ? "normal" : t >= 39 ? "critical" : t >= 37.5 ? "high" : "normal";

        const statusColors = { normal: { bg:"#F0FDF4", border:"#BBF7D0", text:"#166534", bar:"#10B981" }, high: { bg:"#FEF9C3", border:"#FDE68A", text:"#92400E", bar:"#F59E0B" }, critical: { bg:"#FEF2F2", border:"#FECACA", text:"#991B1B", bar:"#EF4444" } };

        const trendArrow = (curr, prev) => {
          if (!curr || !prev) return null;
          const diff = curr - prev;
          if (Math.abs(diff) < 0.5) return { text:"No change", color:"#94A3B8" };
          return diff > 0 ? { text:`↑ ${Math.abs(diff).toFixed(1)}`, color:"#EF4444" } : { text:`↓ ${Math.abs(diff).toFixed(1)}`, color:"#10B981" };
        };

        const bpTrend = () => {
          if (!latest?.blood_pressure || !prev?.blood_pressure) return null;
          const l = parseBP(latest.blood_pressure); const p = parseBP(prev.blood_pressure);
          if (!l || !p) return null;
          const diff = l.sys - p.sys;
          if (Math.abs(diff) < 1) return { text:"No change", color:"#94A3B8" };
          return diff > 0 ? { text:`↑ ${Math.abs(diff)} mmHg`, color:"#EF4444" } : { text:`↓ ${Math.abs(diff)} mmHg`, color:"#10B981" };
        };

        const celsiusToF = (c) => c ? ((c * 9/5) + 32).toFixed(1) : null;

        const patientHeight = fullPatient?.height || patient?.height;

        const handleSaveVitals = () => {
          if (!vitalsForm.blood_pressure && !vitalsForm.heart_rate && !vitalsForm.spo2 && !vitalsForm.temperature && !vitalsForm.weight) {
            alert("Please enter at least one vital sign."); return;
          }
          setSavingVitals(true);
          const payload = {
            date: vitalsForm.date || new Date().toISOString().split("T")[0],
            time: vitalsForm.time || new Date().toLocaleTimeString("en-US",{hour:"2-digit",minute:"2-digit"}),
            blood_pressure: vitalsForm.blood_pressure || null,
            heart_rate: vitalsForm.heart_rate ? parseInt(vitalsForm.heart_rate) : null,
            spo2: vitalsForm.spo2 ? parseInt(vitalsForm.spo2) : null,
            temperature: vitalsForm.temperature ? parseFloat(vitalsForm.temperature) : null,
            weight: vitalsForm.weight ? parseFloat(vitalsForm.weight) : null,
            recorded_by: user?.username,
          };
          vitalsAPI.add(patientId, payload)
            .then(v => {
              const updated = [v, ...vitals];
              setVitals(updated);
              try { sessionStorage.setItem(`emr_cache_vitals_${patientId}`, JSON.stringify(updated)); } catch(e) {} invalidateSummaryCache();
              // Save height to patient profile if entered
              const savedH = ftInToCm(vitalsForm.heightFt, vitalsForm.heightIn);
              if (savedH && savedH > 0) {
                patientAPI.update(patientId, { height: savedH }).catch(()=>{});
              }
              setShowVitalsModal(false);
              setVitalsForm({ date:"", time:"", blood_pressure:"", heart_rate:"", spo2:"", temperature:"", weight:"", heightFt:"", heightIn:"" });
            })
            .catch(() => alert("Failed to save vitals"))
            .finally(() => setSavingVitals(false));
        };

        const ftInToCm = (ft, inch) => {
          const f = parseFloat(ft)||0; const i = parseFloat(inch)||0;
          if (!f && !i) return null;
          return Math.round(((f*12)+i)*2.54*10)/10;
        };
        const enteredHeightCm = ftInToCm(vitalsForm.heightFt, vitalsForm.heightIn);
        const effectiveHeight = enteredHeightCm || patientHeight;
        const bmiCalc = () => {
          if (!vitalsForm.weight || !effectiveHeight) return null;
          const hm = effectiveHeight / 100;
          return (parseFloat(vitalsForm.weight) / (hm*hm)).toFixed(1);
        };

        const bmiLabel = (bmi) => {
          if (!bmi) return "";
          const b = parseFloat(bmi);
          if (b < 18.5) return "Underweight";
          if (b < 25)   return "Normal";
          if (b < 30)   return "Overweight";
          return "Obese";
        };

        const inputSt = { padding:"9px 12px", border:"1.5px solid #CBD5E1", borderRadius:8, fontSize:13, width:"100%", fontFamily:"inherit", outline:"none", background:"#fff", boxSizing:"border-box" };

        const HeroCard = ({ label, value, unit, secondLine, trend, status }) => {
          const sc = statusColors[status] || statusColors.normal;
          return (
            <div style={{ borderRadius:10, padding:"14px 16px", border:`1.5px solid ${sc.border}`, background:sc.bg, position:"relative", overflow:"hidden" }}>
              <div style={{ fontSize:9, fontWeight:700, textTransform:"uppercase", letterSpacing:".08em", color:sc.text, marginBottom:8, display:"flex", alignItems:"center", justifyContent:"space-between" }}>
                {label}
                {status !== "normal" && <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke={sc.text} strokeWidth="2"><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>}
              </div>
              <div style={{ display:"flex", alignItems:"baseline", gap:4, marginBottom:2 }}>
                <span style={{ fontSize:26, fontWeight:800, lineHeight:1, color:status!=="normal"?sc.text:"#0F172A" }}>{value||"—"}</span>
                {unit && <span style={{ fontSize:12, fontWeight:500, color:"#64748B" }}>{unit}</span>}
              </div>
              {secondLine && <div style={{ fontSize:11, color:"#94A3B8", marginBottom:2 }}>{secondLine}</div>}
              {trend && <div style={{ fontSize:10, fontWeight:600, color:trend.color, marginTop:4 }}>{trend.text}</div>}
              <div style={{ position:"absolute", bottom:0, left:0, right:0, height:3, background:sc.bar }}/>
            </div>
          );
        };

        return (
          <div>
            {/* Top bar */}
            <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:20 }}>
              <div>
                <div style={{ fontSize:18, fontWeight:800 }}>Vitals</div>
                {latest && <div style={{ fontSize:12, color:"#64748B", marginTop:2, display:"flex", alignItems:"center", gap:5 }}>
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#94A3B8" strokeWidth="2"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
                  Recorded: {formatDate(latest.date)} {latest.time && `· ${latest.time}`}{latest.recorded_by && ` · By ${latest.recorded_by}`}
                </div>}
              </div>
              {canRecord && (
                <button onClick={() => setShowVitalsModal(true)}
                  style={{ display:"flex", alignItems:"center", gap:7, background:"#4F46E5", color:"#fff", border:"none", borderRadius:9, padding:"9px 18px", fontSize:13, fontWeight:600, cursor:"pointer" }}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.5"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
                  Add Today's Vitals
                </button>
              )}
            </div>

            {vitalsLoading && !latest && <div style={{ textAlign:"center", padding:40, color:"#94A3B8" }}>Loading vitals...</div>}

            {!vitalsLoading && !latest && (
              <div className="card"><div className="empty-state">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M22 12h-4l-3 9L9 3l-3 9H2"/></svg>
                <h3>No vitals recorded yet</h3>
                <p>{canRecord ? "Click '+ Add Today's Vitals' to record the first entry." : "No vitals have been recorded for this patient."}</p>
              </div></div>
            )}

            {latest && (
              <>
                {/* Hero cards */}
                <div style={{ display:"grid", gridTemplateColumns:"repeat(5,1fr)", gap:14, marginBottom:24 }}>
                  <HeroCard label="Blood Pressure" value={latest.blood_pressure} unit="mmHg"
                    trend={bpTrend()} status={bpStatus(latest.blood_pressure)} />
                  <HeroCard label="Heart Rate" value={latest.heart_rate} unit="bpm"
                    trend={trendArrow(latest.heart_rate, prev?.heart_rate)} status={hrStatus(latest.heart_rate)} />
                  <HeroCard label="SpO2" value={latest.spo2} unit="%"
                    trend={trendArrow(latest.spo2, prev?.spo2)} status={spo2Status(latest.spo2)} />
                  <HeroCard label="Temperature" value={latest.temperature ? `${latest.temperature}°C` : null} unit=""
                    secondLine={latest.temperature ? `${celsiusToF(latest.temperature)} °F` : null}
                    trend={trendArrow(latest.temperature, prev?.temperature)} status={tempStatus(latest.temperature)} />
                  <HeroCard label="Weight & BMI" value={latest.weight ? `${latest.weight}` : null} unit="kg"
                    secondLine={latest.bmi ? `BMI: ${latest.bmi} (${bmiLabel(latest.bmi)})` : null}
                    trend={trendArrow(latest.weight, prev?.weight)} status="normal" />
                </div>

                {/* Flowsheet */}
                <div style={{ background:"#fff", borderRadius:14, border:"1.5px solid #E2E8F0", overflow:"hidden", height:"100%", display:"flex", flexDirection:"column" }}>
                  <div style={{ padding:"14px 20px", borderBottom:"1px solid #E2E8F0", display:"flex", alignItems:"center", justifyContent:"space-between" }}>
                    <div style={{ fontWeight:700, fontSize:15 }}>Vitals Flowsheet (History)</div>
                    <span style={{ background:"#F1F5F9", border:"1px solid #E2E8F0", borderRadius:6, fontSize:11, color:"#64748B", padding:"3px 10px" }}>
                      {vitals.length} record{vitals.length!==1?"s":""}
                    </span>
                  </div>
                  <div style={{ overflowX:"auto" }}>
                    <table style={{ width:"100%", borderCollapse:"collapse" }}>
                      <thead>
                        <tr style={{ background:"#F8FAFC", borderBottom:"1px solid #E2E8F0" }}>
                          {["Date & Time","Blood Pressure","Heart Rate","SpO2","Temp","Weight / BMI","Recorded By"].map(h => (
                            <th key={h} style={{ padding:"9px 16px", textAlign:"left", fontSize:10, fontWeight:700, textTransform:"uppercase", letterSpacing:".06em", color:"#94A3B8" }}>{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {vitals.map(v => {
                          const bps = bpStatus(v.blood_pressure);
                          const bpColor = bps==="critical"?"#DC2626":bps==="high"?"#F59E0B":"#10B981";
                          return (
                            <tr key={v.id} style={{ borderBottom:"1px solid #F1F5F9" }}
                              onMouseEnter={e=>e.currentTarget.style.background="#F8FAFC"} onMouseLeave={e=>e.currentTarget.style.background=""}>
                              <td style={{ padding:"12px 16px" }}>
                                <div style={{ fontWeight:700, fontSize:13 }}>{formatDate(v.date)}</div>
                                <div style={{ fontSize:11, color:"#94A3B8", marginTop:2 }}>{v.time||"—"}</div>
                              </td>
                              <td style={{ padding:"12px 16px", fontWeight:700, fontSize:14, color:bpColor }}>{v.blood_pressure||"—"}</td>
                              <td style={{ padding:"12px 16px", fontSize:13 }}>{v.heart_rate ? `${v.heart_rate} bpm` : "—"}</td>
                              <td style={{ padding:"12px 16px" }}>
                                {v.spo2 ? <span style={{ background: v.spo2<95?"#FEE2E2":"#DCFCE7", color: v.spo2<95?"#991B1B":"#166534", borderRadius:99, padding:"2px 8px", fontSize:11, fontWeight:600 }}>{v.spo2}%</span> : "—"}
                              </td>
                              <td style={{ padding:"12px 16px", fontSize:13 }}>
                                {v.temperature ? <>{v.temperature}°C <span style={{ color:"#94A3B8", fontSize:11 }}>/ {celsiusToF(v.temperature)}°F</span></> : "—"}
                              </td>
                              <td style={{ padding:"12px 16px", fontSize:13 }}>
                                {v.weight ? <>{v.weight} kg{v.bmi ? <div style={{ fontSize:11, color:"#94A3B8" }}>BMI: {v.bmi}</div> : null}</> : "—"}
                              </td>
                              <td style={{ padding:"12px 16px" }}>
                                <div style={{ display:"flex", alignItems:"center", gap:6 }}>
                                  <div style={{ width:24, height:24, borderRadius:"50%", background:"#E2E8F0", display:"flex", alignItems:"center", justifyContent:"center", fontSize:9, fontWeight:700, color:"#475569", flexShrink:0 }}>
                                    {(v.recorded_by||"?").slice(0,2).toUpperCase()}
                                  </div>
                                  <span style={{ fontSize:12, color:"#475569" }}>{v.recorded_by||"—"}</span>
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              </>
            )}

            {/* Add Vitals Modal */}
            {showVitalsModal && (
              <div className="modal-overlay">
                <div className="modal" style={{ maxWidth:520 }}>
                  <div className="modal-header">
                    <div style={{ fontWeight:700, fontSize:15 }}>Record Vitals</div>
                    <button className="btn btn-ghost btn-sm" onClick={() => setShowVitalsModal(false)}>✕</button>
                  </div>
                  <div className="modal-body">
                    <div style={{ fontSize:10, fontWeight:700, textTransform:"uppercase", letterSpacing:".07em", color:"#94A3B8", marginBottom:8, paddingBottom:6, borderBottom:"1px solid #F1F5F9" }}>Date & Time</div>
                    <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12, marginBottom:14 }}>
                      <div><div style={{ fontSize:10, fontWeight:600, color:"#64748B", marginBottom:4, textTransform:"uppercase" }}>Date</div><input style={inputSt} type="date" value={vitalsForm.date} onChange={e=>setVitalsForm(f=>({...f,date:e.target.value}))}/></div>
                      <div><div style={{ fontSize:10, fontWeight:600, color:"#64748B", marginBottom:4, textTransform:"uppercase" }}>Time</div><input style={inputSt} type="time" value={vitalsForm.time} onChange={e=>setVitalsForm(f=>({...f,time:e.target.value}))}/></div>
                    </div>
                    <div style={{ fontSize:10, fontWeight:700, textTransform:"uppercase", letterSpacing:".07em", color:"#94A3B8", marginBottom:8, paddingBottom:6, borderBottom:"1px solid #F1F5F9" }}>Cardiovascular</div>
                    <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12, marginBottom:14 }}>
                      <div><div style={{ fontSize:10, fontWeight:600, color:"#64748B", marginBottom:4, textTransform:"uppercase" }}>Blood Pressure (mmHg)</div><input style={inputSt} placeholder="e.g. 120/80" value={vitalsForm.blood_pressure} onChange={e=>setVitalsForm(f=>({...f,blood_pressure:e.target.value}))}/></div>
                      <div><div style={{ fontSize:10, fontWeight:600, color:"#64748B", marginBottom:4, textTransform:"uppercase" }}>Heart Rate (bpm)</div><input style={inputSt} type="number" placeholder="e.g. 82" value={vitalsForm.heart_rate} onChange={e=>setVitalsForm(f=>({...f,heart_rate:e.target.value}))}/></div>
                    </div>
                    <div style={{ fontSize:10, fontWeight:700, textTransform:"uppercase", letterSpacing:".07em", color:"#94A3B8", marginBottom:8, paddingBottom:6, borderBottom:"1px solid #F1F5F9" }}>Respiratory & Temperature</div>
                    <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12, marginBottom:14 }}>
                      <div><div style={{ fontSize:10, fontWeight:600, color:"#64748B", marginBottom:4, textTransform:"uppercase" }}>SpO2 (%)</div><input style={inputSt} type="number" placeholder="e.g. 98" value={vitalsForm.spo2} onChange={e=>setVitalsForm(f=>({...f,spo2:e.target.value}))}/></div>
                      <div><div style={{ fontSize:10, fontWeight:600, color:"#64748B", marginBottom:4, textTransform:"uppercase" }}>Temperature (°C)</div><input style={inputSt} type="number" step="0.1" placeholder="e.g. 37.0" value={vitalsForm.temperature} onChange={e=>setVitalsForm(f=>({...f,temperature:e.target.value}))}/></div>
                    </div>
                    <div style={{ fontSize:10, fontWeight:700, textTransform:"uppercase", letterSpacing:".07em", color:"#94A3B8", marginBottom:8, paddingBottom:6, borderBottom:"1px solid #F1F5F9" }}>Weight & BMI</div>
                    <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12, marginBottom:8 }}>
                      <div><div style={{ fontSize:10, fontWeight:600, color:"#64748B", marginBottom:4, textTransform:"uppercase" }}>Weight (kg)</div><input style={inputSt} type="number" step="0.1" placeholder="e.g. 65" value={vitalsForm.weight} onChange={e=>setVitalsForm(f=>({...f,weight:e.target.value}))}/></div>
                      <div>
                        <div style={{ fontSize:10, fontWeight:600, color:"#64748B", marginBottom:4, textTransform:"uppercase" }}>
                          Height {patientHeight ? <span style={{ color:"#10B981", fontWeight:400 }}>· {patientHeight}cm saved</span> : <span style={{ color:"#94A3B8", fontWeight:400 }}>· optional</span>}
                        </div>
                        <div style={{ display:"flex", alignItems:"center", gap:6 }}>
                          <div style={{ display:"flex", alignItems:"center", gap:4, flex:1 }}>
                            <input style={{...inputSt, textAlign:"center"}} type="number" min="0" max="8" placeholder="5" value={vitalsForm.heightFt} onChange={e=>setVitalsForm(f=>({...f,heightFt:e.target.value}))}/>
                            <span style={{ fontSize:13, color:"#64748B", fontWeight:500, flexShrink:0 }}>ft</span>
                          </div>
                          <div style={{ display:"flex", alignItems:"center", gap:4, flex:1 }}>
                            <input style={{...inputSt, textAlign:"center"}} type="number" min="0" max="11" placeholder="8" value={vitalsForm.heightIn} onChange={e=>setVitalsForm(f=>({...f,heightIn:e.target.value}))}/>
                            <span style={{ fontSize:13, color:"#64748B", fontWeight:500, flexShrink:0 }}>in</span>
                          </div>
                          {enteredHeightCm && <span style={{ fontSize:11, color:"#10B981", fontWeight:600, flexShrink:0 }}>= {enteredHeightCm}cm</span>}
                        </div>
                      </div>
                    </div>
                    {bmiCalc() && (
                      <div style={{ background:"#F0FDF4", border:"1px solid #BBF7D0", borderRadius:8, padding:"10px 14px", fontSize:13, color:"#166534", display:"flex", alignItems:"center", gap:8 }}>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#10B981" strokeWidth="2"><polyline points="20 6 9 17 4 12"/></svg>
                        BMI auto-calculated: <strong>{bmiCalc()}</strong> ({bmiLabel(bmiCalc())})
                      </div>
                    )}
                    {!patientHeight && !enteredHeightCm && vitalsForm.weight && (
                      <div style={{ background:"#FFF7ED", border:"1px solid #FED7AA", borderRadius:8, padding:"8px 12px", fontSize:12, color:"#92400E", marginTop:8 }}>
                        Enter height above to auto-calculate BMI.
                      </div>
                    )}
                  </div>
                  <div className="modal-footer">
                    <button className="btn btn-ghost" onClick={() => setShowVitalsModal(false)}>Cancel</button>
                    <button className="btn btn-primary" disabled={savingVitals} onClick={handleSaveVitals}>{savingVitals ? "Saving…" : "Save Vitals"}</button>
                  </div>
                </div>
              </div>
            )}
          </div>
        );
      })()}

      {/* ── HISTORY VIEW ─────────────────────────────────────────────────── */}
      {view === "history" && (() => {
        const canAddChronic  = ["admin","doctor","technician"].includes(user?.role);
        const canAddSurgical = ["admin","doctor","technician"].includes(user?.role);
        const canAddFamily   = ["admin","doctor","technician","receptionist"].includes(user?.role);
        const canAddSocial   = ["admin","doctor","technician","receptionist"].includes(user?.role);

        const chronic  = history.filter(h => h.type === "Chronic Condition");
        const surgical = history.filter(h => h.type === "Surgical");
        const family   = history.filter(h => h.type === "Family");
        const social   = history.find(h  => h.type === "Social") || null;

        const inputSt = { padding:"8px 11px", border:"1.5px solid #CBD5E1", borderRadius:8, fontSize:13, width:"100%", fontFamily:"inherit", outline:"none", background:"#fff", boxSizing:"border-box" };
        const secSt   = { fontSize:10, fontWeight:700, textTransform:"uppercase", letterSpacing:".07em", color:"#94A3B8", marginBottom:6, paddingBottom:6, borderBottom:"1px solid #F1F5F9" };

        const handleAddHistory = (type, extra={}) => {
          if (!historyForm.narrative.trim() && !extra.narrative) return;
          setSavingHistory(true);
          const payload = { type, narrative: extra.narrative || historyForm.narrative.trim(), onset_date: historyForm.onset_date || null, recorded_by: user?.username };
          historyAPI.add(patientId, payload)
            .then(h => {
              const updated = [...history, h];
              setHistory(updated);
              try { sessionStorage.setItem(`emr_cache_history_${patientId}`, JSON.stringify(updated)); } catch(e) {} invalidateSummaryCache();
              setHistoryForm({ narrative:"", onset_date:"", relation:"Father" });
              setShowHistoryForm(null);
              setConditionInput("");
            })
            .catch(() => alert("Failed to save"))
            .finally(() => setSavingHistory(false));
        };

        const handleDeleteHistory = (id) => {
          if (!window.confirm("Remove this entry?")) return;
          historyAPI.delete(patientId, id)
            .then(() => {
              const updated = history.filter(h => h.id !== id);
              setHistory(updated);
              try { sessionStorage.setItem(`emr_cache_history_${patientId}`, JSON.stringify(updated)); } catch(e) {}
            })
            .catch(() => alert("Failed to delete"));
        };

        // Parse social history from narrative string
        const parseSocial = () => {
          if (!social) return { smoking:"Never", smokingDetail:"", betelNut:"No", betelNutDetail:"" };
          const n = social.narrative;
          const smokingMatch = n.match(/Smoking: (\w+)(?:\s*—\s*(.+?))?(?:\s*\||\s*$)/);
          const betelMatch   = n.match(/Betel Nut\/Jorda: (\w+)(?:\s*—\s*(.+?))?(?:\s*\||\s*$)/);
          return {
            smoking: smokingMatch ? smokingMatch[1] : "Never",
            smokingDetail: smokingMatch ? (smokingMatch[2]||"") : "",
            betelNut: betelMatch ? betelMatch[1] : "No",
            betelNutDetail: betelMatch ? (betelMatch[2]||"") : "",
          };
        };

        const handleSaveSocial = () => {
          const narrative = [
            socialForm.smoking !== "Never" ? `Smoking: ${socialForm.smoking}${socialForm.smokingDetail ? " — " + socialForm.smokingDetail : ""}` : null,
            socialForm.betelNut === "Yes" ? `Betel Nut/Jorda: Yes${socialForm.betelNutDetail ? " — " + socialForm.betelNutDetail : ""}` : "Betel Nut/Jorda: No",
          ].filter(Boolean).join(" | ");

          setSavingHistory(true);
          if (social) {
            historyAPI.update(patientId, social.id, { narrative })
              .then(h => {
                const updated = history.map(x => x.id === h.id ? h : x);
                setHistory(updated);
                try { sessionStorage.setItem(`emr_cache_history_${patientId}`, JSON.stringify(updated)); } catch(e) {}
                setSocialEdit(false);
              })
              .catch(() => alert("Failed to save"))
              .finally(() => setSavingHistory(false));
          } else {
            historyAPI.add(patientId, { type:"Social", narrative, recorded_by: user?.username })
              .then(h => {
                const updated = [...history, h];
                setHistory(updated);
                try { sessionStorage.setItem(`emr_cache_history_${patientId}`, JSON.stringify(updated)); } catch(e) {}
                setSocialEdit(false);
              })
              .catch(() => alert("Failed to save"))
              .finally(() => setSavingHistory(false));
          }
        };

        const toggleSt = (active, variant) => ({
          padding:"7px 18px", fontSize:13, border:"none", borderRight:"1px solid #E2E8F0", cursor:"pointer",
          fontWeight: active ? 700 : 400,
          color: active ? (variant==="danger"?"#DC2626":variant==="warn"?"#92400E":variant==="success"?"#166534":"#475569") : "#64748B",
          background: active ? (variant==="danger"?"#FEE2E2":variant==="warn"?"#FEF3C7":variant==="success"?"#F0FDF4":"#F1F5F9") : "#fff",
        });

        return (
          <div>
            <div style={{ fontSize:18, fontWeight:800, marginBottom:20 }}>Clinical History</div>
            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:16, gridAutoRows:"1fr" }}>

              {/* Past Medical / Chronic */}
              <HistCard title="Past Medical History"
                icon={<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#4F46E5" strokeWidth="2"><path d="M22 12h-4l-3 9L9 3l-3 9H2"/></svg>}
                iconBg="#EEF2FF" iconColor="#4F46E5"
                canAdd={canAddChronic}
                onAdd={() => { setShowHistoryForm("Chronic Condition"); setHistoryForm({narrative:"",onset_date:"",relation:"Father"}); }}>
                <div style={{ display:"flex", flexWrap:"wrap", gap:8, minHeight:40 }}>
                  {chronic.length === 0 && showHistoryForm !== "Chronic Condition" && <span style={{ fontSize:13, color:"#94A3B8" }}>No conditions recorded.</span>}
                  {chronic.map(h => (
                    <span key={h.id} style={{ display:"inline-flex", alignItems:"center", gap:6, background:"#EEF2FF", color:"#4338CA", border:"1px solid #C7D2FE", borderRadius:99, padding:"5px 12px", fontSize:13, fontWeight:600 }}>
                      {h.narrative}
                      {h.onset_date && <span style={{ fontSize:10, color:"#818CF8", fontWeight:400 }}>· {h.onset_date}</span>}
                      <span style={{ cursor:"pointer", opacity:.6, fontSize:14 }} onClick={() => handleDeleteHistory(h.id)}>×</span>
                    </span>
                  ))}
                </div>
                {chronic.length > 0 && <div style={{ fontSize:10, color:"#94A3B8", marginTop:10, display:"flex", alignItems:"center", gap:4 }}>
                  <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="#CBD5E1" strokeWidth="2"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
                  Blue = from registration · Click × to remove
                </div>}
                {showHistoryForm === "Chronic Condition" && (
                  <InlineAddForm onSave={() => handleAddHistory("Chronic Condition")} onCancel={() => setShowHistoryForm(null)} saving={savingHistory}>
                    <div style={{ fontSize:10, fontWeight:700, textTransform:"uppercase", letterSpacing:".06em", color:"#64748B", marginBottom:6 }}>Condition Name *</div>
                    <input style={{...inputSt, marginBottom:8}} placeholder="e.g. COPD, Epilepsy…" value={historyForm.narrative} onChange={e=>setHistoryForm(f=>({...f,narrative:e.target.value}))} autoFocus/>
                    <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:8 }}>
                      <div><div style={{ fontSize:10, fontWeight:600, color:"#94A3B8", marginBottom:4 }}>Onset Year (optional)</div><input style={inputSt} type="number" placeholder="e.g. 2020" value={historyForm.onset_date} onChange={e=>setHistoryForm(f=>({...f,onset_date:e.target.value}))}/></div>
                    </div>
                  </InlineAddForm>
                )}
              </HistCard>

              {/* Social History */}
              <HistCard title="Social History"
                icon={<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#F59E0B" strokeWidth="2"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>}
                iconBg="#FFF7ED" iconColor="#F59E0B"
                canAdd={false}>
                <div style={{ display:"flex", justifyContent:"flex-end", marginBottom:12 }}>
                  {canAddSocial && !socialEdit && <button onClick={() => { setSocialEdit(true); setSocialForm(parseSocial()); }} style={{ display:"flex", alignItems:"center", gap:4, background:"#F8FAFC", color:"#475569", border:"1.5px solid #E2E8F0", borderRadius:7, padding:"5px 12px", fontSize:12, fontWeight:600, cursor:"pointer" }}>
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                    Edit
                  </button>}
                </div>

                {/* Smoking */}
                <div style={{ marginBottom:16 }}>
                  <div style={{ fontSize:10, fontWeight:700, textTransform:"uppercase", letterSpacing:".07em", color:"#94A3B8", marginBottom:8 }}>Smoking Status</div>
                  {socialEdit ? (
                    <>
                      <div style={{ display:"flex", gap:0, borderRadius:8, overflow:"hidden", border:"1.5px solid #E2E8F0", width:"fit-content" }}>
                        {["Never","Former","Current"].map((opt,idx) => (
                          <button key={opt} type="button" onClick={() => setSocialForm(f=>({...f,smoking:opt}))}
                            style={{ ...toggleSt(socialForm.smoking===opt, opt==="Never"?"neutral":opt==="Former"?"warn":"danger"), borderRight: idx<2 ? "1px solid #E2E8F0":"none" }}>{opt}</button>
                        ))}
                      </div>
                      {socialForm.smoking !== "Never" && <input style={{...inputSt, marginTop:8, background:"#FFFBEB", borderColor:"#FDE68A"}} placeholder="Frequency / duration" value={socialForm.smokingDetail} onChange={e=>setSocialForm(f=>({...f,smokingDetail:e.target.value}))}/>}
                    </>
                  ) : (
                    <>
                      <div style={{ display:"flex", gap:0, borderRadius:8, overflow:"hidden", border:"1.5px solid #E2E8F0", width:"fit-content" }}>
                        {["Never","Former","Current"].map((opt,idx) => (
                          <button key={opt} disabled style={{ ...toggleSt(parseSocial().smoking===opt, opt==="Never"?"neutral":opt==="Former"?"warn":"danger"), borderRight: idx<2 ? "1px solid #E2E8F0":"none", cursor:"default" }}>{opt}</button>
                        ))}
                      </div>
                      {parseSocial().smokingDetail && <div style={{ fontSize:12, color:"#F59E0B", fontWeight:600, marginTop:6 }}>↳ {parseSocial().smokingDetail}</div>}
                    </>
                  )}
                </div>

                {/* Betel Nut */}
                <div>
                  <div style={{ fontSize:10, fontWeight:700, textTransform:"uppercase", letterSpacing:".07em", color:"#94A3B8", marginBottom:8 }}>Betel Nut / Jorda</div>
                  {socialEdit ? (
                    <>
                      <div style={{ display:"flex", gap:0, borderRadius:8, overflow:"hidden", border:"1.5px solid #E2E8F0", width:"fit-content" }}>
                        {["No","Yes"].map((opt,idx) => (
                          <button key={opt} type="button" onClick={() => setSocialForm(f=>({...f,betelNut:opt}))}
                            style={{ ...toggleSt(socialForm.betelNut===opt, opt==="No"?"success":"danger"), borderRight: idx<1 ? "1px solid #E2E8F0":"none" }}>{opt}</button>
                        ))}
                      </div>
                      {socialForm.betelNut === "Yes" && <input style={{...inputSt, marginTop:8, background:"#FFFBEB", borderColor:"#FDE68A"}} placeholder="Frequency" value={socialForm.betelNutDetail} onChange={e=>setSocialForm(f=>({...f,betelNutDetail:e.target.value}))}/>}
                      <div style={{ display:"flex", gap:8, marginTop:12 }}>
                        <button onClick={() => setSocialEdit(false)} style={{ padding:"7px 14px", background:"#fff", border:"1.5px solid #E2E8F0", borderRadius:7, fontSize:12, color:"#64748B", cursor:"pointer" }}>Cancel</button>
                        <button onClick={handleSaveSocial} disabled={savingHistory} style={{ padding:"7px 16px", background:"#F59E0B", color:"#fff", border:"none", borderRadius:7, fontSize:12, fontWeight:700, cursor:"pointer" }}>{savingHistory?"Saving…":"Save"}</button>
                      </div>
                    </>
                  ) : (
                    <>
                      <div style={{ display:"flex", gap:0, borderRadius:8, overflow:"hidden", border:"1.5px solid #E2E8F0", width:"fit-content" }}>
                        {["No","Yes"].map((opt,idx) => (
                          <button key={opt} disabled style={{ ...toggleSt(parseSocial().betelNut===opt, opt==="No"?"success":"danger"), borderRight: idx<1 ? "1px solid #E2E8F0":"none", cursor:"default" }}>{opt}</button>
                        ))}
                      </div>
                      {parseSocial().betelNutDetail && <div style={{ fontSize:12, color:"#F59E0B", fontWeight:600, marginTop:6 }}>↳ {parseSocial().betelNutDetail}</div>}
                      {parseSocial().betelNut === "No" && <div style={{ fontSize:12, color:"#10B981", fontWeight:600, marginTop:6 }}>✓ Not a user</div>}
                    </>
                  )}
                </div>
              </HistCard>

              {/* Past Surgeries */}
              <HistCard title="Past Surgeries"
                icon={<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#10B981" strokeWidth="2"><path d="M14.5 10c-.83 0-1.5-.67-1.5-1.5v-5c0-.83.67-1.5 1.5-1.5s1.5.67 1.5 1.5v5c0 .83-.67 1.5-1.5 1.5z"/><path d="M20.5 10H19V8.5c0-.83.67-1.5 1.5-1.5s1.5.67 1.5 1.5-.67 1.5-1.5 1.5z"/><path d="M9.5 14c.83 0 1.5.67 1.5 1.5v5c0 .83-.67 1.5-1.5 1.5S8 21.33 8 20.5v-5c0-.83.67-1.5 1.5-1.5z"/><path d="M3.5 14H5v1.5c0 .83-.67 1.5-1.5 1.5S2 16.33 2 15.5 2.67 14 3.5 14z"/><path d="M14 14.5c0-.83.67-1.5 1.5-1.5h5c.83 0 1.5.67 1.5 1.5s-.67 1.5-1.5 1.5h-5c-.83 0-1.5-.67-1.5-1.5z"/></svg>}
                iconBg="#F0FDF4" iconColor="#10B981"
                canAdd={canAddSurgical}
                onAdd={() => { setShowHistoryForm("Surgical"); setHistoryForm({narrative:"",onset_date:"",relation:"Father"}); }}>
                {surgical.length === 0 && showHistoryForm !== "Surgical" && <div className="empty-state-sm" style={{ textAlign:"center", padding:20, color:"#94A3B8", fontSize:13 }}>No surgical history recorded.</div>}
                <div style={{ display:"flex", flexDirection:"column", gap:0 }}>
                  {surgical.map((h,i) => (
                    <div key={h.id} style={{ display:"flex", gap:14, paddingBottom: i<surgical.length-1?16:0, position:"relative" }}>
                      <div style={{ display:"flex", flexDirection:"column", alignItems:"center", flexShrink:0 }}>
                        <div style={{ width:12, height:12, borderRadius:"50%", background: i===0?"#10B981":"#CBD5E1", marginTop:3 }}/>
                        {i<surgical.length-1 && <div style={{ width:2, background:"#E2E8F0", flex:1, marginTop:4 }}/>}
                      </div>
                      <div style={{ flex:1 }}>
                        <div style={{ fontWeight:700, fontSize:14, color:"#0F172A" }}>{h.narrative}</div>
                        <div style={{ fontSize:11, color:"#94A3B8", marginTop:3, display:"flex", alignItems:"center", gap:6 }}>
                          {h.onset_date && <span>{h.onset_date}</span>}
                        </div>
                      </div>
                      <button onClick={() => handleDeleteHistory(h.id)} style={{ background:"none", border:"none", color:"#CBD5E1", cursor:"pointer", fontSize:14, padding:"0 4px", alignSelf:"flex-start" }}
                        onMouseEnter={e=>e.currentTarget.style.color="#DC2626"} onMouseLeave={e=>e.currentTarget.style.color="#CBD5E1"}>×</button>
                    </div>
                  ))}
                </div>
                {showHistoryForm === "Surgical" && (
                  <InlineAddForm onSave={() => handleAddHistory("Surgical")} onCancel={() => setShowHistoryForm(null)} saving={savingHistory}>
                    <input style={{...inputSt, marginBottom:8}} placeholder="e.g. Appendectomy" value={historyForm.narrative} onChange={e=>setHistoryForm(f=>({...f,narrative:e.target.value}))} autoFocus/>
                    <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:8 }}>
                      <input style={inputSt} placeholder="Year — e.g. 2020" type="number" value={historyForm.onset_date} onChange={e=>setHistoryForm(f=>({...f,onset_date:e.target.value}))}/>
                      <input style={inputSt} placeholder="Hospital (optional)"/>
                    </div>
                  </InlineAddForm>
                )}
              </HistCard>

              {/* Family History */}
              <HistCard title="Family History"
                icon={<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#7C3AED" strokeWidth="2"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>}
                iconBg="#F5F3FF" iconColor="#7C3AED"
                canAdd={canAddFamily}
                onAdd={() => { setShowHistoryForm("Family"); setHistoryForm({narrative:"",onset_date:"",relation:"Father"}); }}>
                {family.length === 0 && showHistoryForm !== "Family" && <div style={{ textAlign:"center", padding:20, color:"#94A3B8", fontSize:13 }}>No family history recorded.</div>}
                <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
                  {family.map(h => (
                    <div key={h.id} style={{ background:"#F8FAFC", borderRadius:8, padding:"10px 14px", display:"flex", alignItems:"center", gap:10 }}>
                      <span style={{ fontSize:12, fontWeight:700, color:"#475569", minWidth:70 }}>{h.narrative.split(":")[0]}</span>
                      <span style={{ fontSize:13, color:"#0F172A", flex:1 }}>{h.narrative.split(":").slice(1).join(":").trim()}</span>
                      {h.onset_date && <span style={{ fontSize:11, color:"#94A3B8" }}>at {h.onset_date}</span>}
                      <button onClick={() => handleDeleteHistory(h.id)} style={{ background:"none", border:"none", color:"#CBD5E1", cursor:"pointer", fontSize:14 }}
                        onMouseEnter={e=>e.currentTarget.style.color="#DC2626"} onMouseLeave={e=>e.currentTarget.style.color="#CBD5E1"}>×</button>
                    </div>
                  ))}
                </div>
                {showHistoryForm === "Family" && (
                  <InlineAddForm onSave={() => handleAddHistory("Family", { narrative: `${historyForm.relation}: ${historyForm.narrative}` })} onCancel={() => setShowHistoryForm(null)} saving={savingHistory}>
                    <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:8, marginBottom:8 }}>
                      <div>
                        <div style={{ fontSize:10, fontWeight:600, color:"#94A3B8", marginBottom:4 }}>Relation</div>
                        <select style={{...inputSt, cursor:"pointer"}} value={historyForm.relation} onChange={e=>setHistoryForm(f=>({...f,relation:e.target.value}))}>
                          {["Father","Mother","Brother","Sister","Grandfather","Grandmother","Other"].map(r => <option key={r}>{r}</option>)}
                        </select>
                      </div>
                      <div>
                        <div style={{ fontSize:10, fontWeight:600, color:"#94A3B8", marginBottom:4 }}>Condition *</div>
                        <input style={inputSt} placeholder="e.g. Stroke" value={historyForm.narrative} onChange={e=>setHistoryForm(f=>({...f,narrative:e.target.value}))} autoFocus/>
                      </div>
                    </div>
                    <input style={inputSt} placeholder="Age at diagnosis (optional)" value={historyForm.onset_date} onChange={e=>setHistoryForm(f=>({...f,onset_date:e.target.value}))}/>
                  </InlineAddForm>
                )}
              </HistCard>

            </div>
          </div>
        );
      })()}

      {/* Upload modal */}
      {showUpload && (
        <UploadReport
          patient={patient}
          onClose={() => setUpload(false)}
          onDone={async (newReport) => {
            setUpload(false);
            // Reload patient data to get fresh report list
            await refreshPatient(patient.id);
            // If we have the new report, navigate directly to it BEFORE loadPatientData
            // so the restore effect doesn't overwrite our navigation
            if (newReport && newReport.id) {
              const repType = newReport.type || selType;
              if (repType) {
                setType(repType);
                setStore("type", repType);
              }
              setRep(newReport);
              setStore("rep", newReport.id);
              setView("report");
              setStore("view", "report");
              setEditMode(false);
              restoredRef.current = true; // prevent restore effect from overwriting
            } else {
              setView("categories");
              restoredRef.current = false;
            }
            loadPatientData(1);
          }}
        />
      )}

      {/* Delete confirm */}
      {showDelete && (
        <div className="modal-overlay">
          <div className="modal" style={{ maxWidth: 400 }}>
            <div className="modal-header">
              <div style={{ fontWeight: 700 }}>{t.deleteBtn} Report</div>
              <button className="btn btn-ghost btn-sm" onClick={() => setDel(null)}>✕</button>
            </div>
            <div className="modal-body">
              <div className="alert alert-red">
                This action cannot be undone. The report and all extracted data will be permanently deleted.
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-ghost" onClick={() => setDel(null)}>{t.cancelBtn}</button>
              <button className="btn btn-danger" onClick={async () => {
                await deleteReport(patient.id, showDelete);
                setDel(null);
                setView("list"); setStore("view", "list"); if(!initialRepId) setRep(null);
              }}>{t.deleteBtn} Report</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );


}